import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Tab,
  Nav,
  Button,
  ListGroup,
  Carousel,
  Accordion,
  Alert,
  Form,
  InputGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";

import NavBar from "./NavBar";
import CoverPage from "./CoverPage";
import ContactPage from "./ContactPage";
import FooterPage from "./FooterPage";
import FloatingContact from "../components/FloatingContact";
import { toggleWishlist, fetchWishlist } from "../services/wishlistService";
import {
  calculateAdminTripQuote,
  calculateTripQuote,
  fetchHotelOptionPrices,
  fetchExtensionRoomAvailability,
  fetchTripRoomAvailability,
  fetchTripRoomSuggestions,
  fetchTripPrices,
  fetchTripQuoteOptions,
} from "../services/priceService";
import HotelOptionSelect from "./HotelOptionSelect";
import CompareStaysModal from "./CompareStaysModal";
import TeamStayMedia from "./TeamStayMedia";
import { listTeamHotelImages } from "../services/leadTrackerApi";
import HotelPriceRangeFilter from "./HotelPriceRangeFilter";
import { vehicleNamesLabel, vehiclesForIds } from "../utils/quoteVehicles";
import { suggestCoupon, applyCoupon } from "../services/couponService";

const TRIP_SECTION_LINKS = [
  { key: "highlights", label: "Highlights" },
  { key: "overview", label: "Overview" },
  { key: "itinerary", label: "Itinerary" },
];
const TRIP_SECTION_KEYS = [
  "highlights",
  "overview",
  "itinerary",
  "video",
  "gallery",
  "terms",
  "faqs",
];

const TRIP_VIDEO_LANGUAGE = "en";
const RESERVATION_PHONE_DISPLAY = "+91 91219 43442";
const RESERVATION_PHONE_DIGITS = "919121943442";

const youtubeVideoId = (url) => {
  const match = String(url || "").match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  return match ? match[1] : "";
};

// Videos are strictly per pickup: no trip-level fallback.
const pickVideoForLanguage = (videos, language = TRIP_VIDEO_LANGUAGE) => {
  const list = Array.isArray(videos) ? videos : [];
  return (
    list.find((video) => (video?.language || TRIP_VIDEO_LANGUAGE) === language) || null
  );
};

const normalizeTripSectionHash = (hash) => {
  const sectionKey = decodeURIComponent(String(hash || ""))
    .replace(/^#/, "")
    .replace(/^trip-section-/, "");
  return TRIP_SECTION_KEYS.includes(sectionKey) ? sectionKey : "";
};

const formatDateParam = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (date) => {
  if (!date) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${date.getFullYear()}`;
};

const addLocalDays = (date, days) => {
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
};

const isChikmagalurJeepAddon = (addon) => {
  const name = String(addon?.name || "").toLowerCase();
  return (name.includes("chikmagalur") || name.includes("chikmangalur")) && name.includes("jeep");
};

const extensionStaysRequestPayload = (extensionStays, forcedScope = null) =>
  (extensionStays || [])
    .filter((stay) => stay.hotelId && stay.roomCategoryId)
    .map(({ selectedFacilityKeys, ...stay }) => ({
      ...stay,
      scope: forcedScope || stay.scope || "BOTH",
      nightIndex: Math.max(1, Number(stay.nightIndex || 1)),
      hotelId: Number(stay.hotelId),
      roomCategorySelections: (stay.roomCategorySelections || [])
        .filter((selection) => selection.roomCategoryId && Number(selection.units || 0) > 0)
        .map((selection) => ({
          roomCategoryId: Number(selection.roomCategoryId),
          units: Math.max(1, Number(selection.units || 1)),
        })),
      roomCategoryId: Number(stay.roomCategoryId),
      units: Math.max(1, Number(stay.units || 1)),
    }));

const DynamicPage = ({ trip, categories }) => {
  const location = useLocation();
  const adminCalculatorMode = new URLSearchParams(location.search).get("adminCalculator") === "1";
  const appliedLeadPrefillRef = React.useRef(null);
  const leadQuotePrefill = React.useMemo(() => {
    if (location.state?.leadQuotePrefill) return location.state.leadQuotePrefill;
    const key = new URLSearchParams(location.search).get("leadQuote");
    if (!key) return null;
    try {
      const stored = localStorage.getItem(key);
      localStorage.removeItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, [location.search, location.state]);
  const [user, setUser] = useState(null);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [numTravellers, setNumTravellers] = useState(0);
  const [suggestedCoupons, setSuggestedCoupons] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [finalPrice, setFinalPrice] = useState(trip.price);
  const [showReservationContact, setShowReservationContact] = useState(false);
  const [priceOptions, setPriceOptions] = useState([]);
  const [selectedPriceListId, setSelectedPriceListId] = useState(null);
  const [selectedPickupOptionId, setSelectedPickupOptionId] = useState("");
  const [quoteOptions, setQuoteOptions] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const [adults, setAdults] = useState("");
  const [kids, setKids] = useState("");
  const [kidAges, setKidAges] = useState([]);
  const [kidVehicleSeatOverrides, setKidVehicleSeatOverrides] = useState({});
  const [dynamicPickupId, setDynamicPickupId] = useState("");
  const [dynamicVehicleId, setDynamicVehicleId] = useState("");
  // Vehicles added on request beyond the first, one id per vehicle (repeats allowed).
  const [additionalVehicleIds, setAdditionalVehicleIds] = useState([]);
  const [dynamicAcType, setDynamicAcType] = useState("AC");
  const [customizationRequired, setCustomizationRequired] = useState(false);
  const [customizationDescription, setCustomizationDescription] = useState("");
  const [customizationPrice, setCustomizationPrice] = useState("");
  const [customizationPriceAdjustment, setCustomizationPriceAdjustment] =
    useState("ADD");
  const [tripExtensionRequired, setTripExtensionRequired] = useState(false);
  // Package nights the customer arranges their own stay for. Empty = every night has a stay.
  const [noStayNights, setNoStayNights] = useState([]);
  // "Show only stays with meals included" - one switch for every night's hotel list.
  const [mealsIncludedOnly, setMealsIncludedOnly] = useState(false);
  // "Same stay all nights" prices, fetched on request.
  const [sameStayState, setSameStayState] = useState({ loading: false, packages: null, note: "" });
  // The night whose stays are open in the side-by-side comparison.
  const [compareNight, setCompareNight] = useState(null);
  // Any change to the quote makes the all-nights prices stale; drop them rather than show old figures.
  useEffect(() => {
    setSameStayState({ loading: false, packages: null, note: "" });
  }, [quote]);

  // A Call / Chat team login saved in this browser (the portal keeps it here). Customers never have
  // one, so the team-only photo tools stay hidden for them; the image endpoints check the login
  // themselves, so forcing the tools to show still gets a customer nothing.
  // Both portals keep their own login, and a browser can hold both - one of them long expired. The
  // portal that opened this quote goes first; if a login is refused the next one is tried, and the
  // page only reports "expired" when every saved login is refused.
  const teamMediaCandidates = React.useMemo(() => {
    const logins = [];
    try {
      const openedFrom = leadQuotePrefill?.team;
      const order = openedFrom === "CHAT" ? ["CHAT", "CALL"] : ["CALL", "CHAT"];
      order.forEach((team) => {
        const token = localStorage.getItem(team === "CALL" ? "callTeamToken" : "chatTeamToken");
        if (token) logins.push({ team, token });
      });
    } catch {
      // Storage blocked - behave as a customer.
    }
    return logins;
  }, [leadQuotePrefill]);
  const teamMediaAccess = teamMediaCandidates.length > 0;
  const [teamHotelMedia, setTeamHotelMedia] = useState({ rows: [], expired: false, access: null });
  useEffect(() => {
    if (!teamMediaCandidates.length) return undefined;
    let cancelled = false;
    (async () => {
      let refused = 0;
      for (const login of teamMediaCandidates) {
        try {
          const rows = await listTeamHotelImages(login.team, login.token);
          if (!cancelled) setTeamHotelMedia({ rows: Array.isArray(rows) ? rows : [], expired: false, access: login });
          return;
        } catch (error) {
          const status = error?.response?.status;
          if (status === 401 || status === 403) {
            refused += 1;
            continue;
          }
          // Not a login problem (network, server) - show no photos, but do not blame the login.
          if (!cancelled) setTeamHotelMedia({ rows: [], expired: false, access: login });
          return;
        }
      }
      if (!cancelled) {
        setTeamHotelMedia({ rows: [], expired: refused === teamMediaCandidates.length, access: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamMediaCandidates]);
  const [extraPreNights, setExtraPreNights] = useState("");
  const [extraPostNights, setExtraPostNights] = useState("");
  const [extensionStays, setExtensionStays] = useState([]);
  const [tripExtensionNote, setTripExtensionNote] = useState("");
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);
  // Per add-on: the option picked and how many take it - { [addonId]: { optionId, people } }.
  const [addonChoices, setAddonChoices] = useState({});
  // Only what was actually chosen is sent: an add-on on its first option for the whole group needs
  // nothing, and reads exactly like a booking made before add-ons had options.
  const selectedAddonOptions = React.useMemo(
    () =>
      selectedAddonIds
        .map((addonId) => ({ addonId, ...(addonChoices[String(addonId)] || {}) }))
        .filter((choice) => choice.optionId || choice.people),
    [selectedAddonIds, addonChoices],
  );
  const [manuallyToggledAddonIds, setManuallyToggledAddonIds] = useState({});
  const [selectedRoomCityByNight, setSelectedRoomCityByNight] = useState({});
  const [selectedRoomRateIdsByNight, setSelectedRoomRateIdsByNight] = useState(
    {},
  );
  const [selectedRoomCategoryUnits, setSelectedRoomCategoryUnits] = useState(
    {},
  );
  const [selectedHotelFacilityKeys, setSelectedHotelFacilityKeys] = useState({});
  const [roomAvailabilityByKey, setRoomAvailabilityByKey] = useState({});
  const [extensionAvailabilityByKey, setExtensionAvailabilityByKey] = useState({});
  const [, setRoomsManuallyAdjusted] = useState(false);
  const [manuallyAdjustedRoomUnitKeys, setManuallyAdjustedRoomUnitKeys] =
    useState({});
  const initialTripSection =
    typeof window === "undefined"
      ? ""
      : normalizeTripSectionHash(window.location.hash);
  const [activeTripTab, setActiveTripTab] = useState(
    initialTripSection || "highlights",
  );
  const pendingTripSectionScrollRef = React.useRef(initialTripSection);
  const tripTabsRef = React.useRef(null);

  const selectedTravellerCount = Number(adults || 0) + Number(kids || 0);
  const kidAgesComplete =
    Number(kids || 0) === 0 ||
    (kidAges.length === Number(kids || 0) &&
      kidAges.every((age) => age !== "" && Number(age) >= 0));
  const extraPreNightCount = Math.max(0, Number(extraPreNights) || 0);
  const extraPostNightCount = Math.max(0, Number(extraPostNights) || 0);

  const pickupOptions = React.useMemo(
    () =>
      (trip.pickupOptions || []).filter((option) => option.isActive !== false),
    [trip.pickupOptions],
  );

  const selectedPickup = React.useMemo(() => {
    if (!pickupOptions.length) return null;
    return (
      pickupOptions.find(
        (option) => String(option.id) === String(selectedPickupOptionId),
      ) ||
      pickupOptions.find((option) => option.isDefault) ||
      pickupOptions[0]
    );
  }, [pickupOptions, selectedPickupOptionId]);

  const displayTrip = React.useMemo(() => {
    if (!selectedPickup) return trip;
    return {
      ...trip,
      overviewText: selectedPickup.overviewText || trip.overviewText,
      departureLocation:
        selectedPickup.departureLocation || trip.departureLocation,
      returnLocation: selectedPickup.returnLocation || trip.returnLocation,
      departureTime: selectedPickup.departureTime || trip.departureTime,
      priceIncludes: selectedPickup.priceIncludes || trip.priceIncludes || [],
      priceExcludes: selectedPickup.priceExcludes || trip.priceExcludes || [],
      itinerary: selectedPickup.itinerary || trip.itinerary || [],
      highlights: trip.highlights || [],
      termsConditions: trip.termsConditions || [],
    };
  }, [selectedPickup, trip]);

  const pickupVideo = React.useMemo(
    () => pickVideoForLanguage(selectedPickup?.videos),
    [selectedPickup]
  );

  const tripSectionUrl = React.useCallback(
    (sectionKey) => {
      if (!trip.path || typeof window === "undefined") return `#${sectionKey}`;
      return `${window.location.origin}/trips/${trip.path}#${sectionKey}`;
    },
    [trip.path],
  );

  const scrollToTripSection = React.useCallback(
    (sectionKey, behavior = "smooth") => {
      const normalizedSectionKey = normalizeTripSectionHash(sectionKey);
      if (!normalizedSectionKey || typeof window === "undefined") return;

      pendingTripSectionScrollRef.current = normalizedSectionKey;
      setActiveTripTab(normalizedSectionKey);

      const scrollWhenReady = (attempt = 0) => {
        window.requestAnimationFrame(() => {
          if (pendingTripSectionScrollRef.current !== normalizedSectionKey) {
            return;
          }
          const section = document.getElementById(
            `trip-section-${normalizedSectionKey}`,
          );
          const target =
            section && section.getClientRects().length
              ? section
              : tripTabsRef.current;

          if (target) {
            const navOffset = 96;
            const top = target.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
              top: Math.max(0, top - navOffset),
              behavior,
            });
          }

          if (attempt < 24) {
            window.setTimeout(() => scrollWhenReady(attempt + 1), 125);
          }
        });
      };

      scrollWhenReady();
    },
    [],
  );

  const handleTripSectionLink = React.useCallback(
    (event, sectionKey) => {
      event.preventDefault();
      if (typeof window !== "undefined") {
        window.history.replaceState(null, "", `#${sectionKey}`);
      }
      scrollToTripSection(sectionKey, "smooth");
    },
    [scrollToTripSection],
  );

  useEffect(() => {
    const applyHash = () => {
      const sectionKey =
        typeof window === "undefined"
          ? ""
          : normalizeTripSectionHash(window.location.hash);
      if (sectionKey) {
        scrollToTripSection(sectionKey, "auto");
      }
    };
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    applyHash();
    window.addEventListener("hashchange", applyHash);
    window.addEventListener("load", applyHash);
    return () => {
      window.removeEventListener("hashchange", applyHash);
      window.removeEventListener("load", applyHash);
    };
  }, [scrollToTripSection]);

  const pickupQuoteVehicles = React.useMemo(() => {
    const vehicles = quoteOptions?.vehicles || [];
    const selectedPickupOption = (quoteOptions?.pickups || []).find(
      (pickup) => String(pickup.id) === String(dynamicPickupId),
    );
    const pickupVehicles = vehicles.filter((vehicle) => {
      if (vehicle.pickupId && selectedPickupOption?.id) {
        return String(vehicle.pickupId) === String(selectedPickupOption.id);
      }
      if (vehicle.pickupValue && selectedPickupOption?.pickupValue) {
        return (
          String(vehicle.pickupValue) ===
          String(selectedPickupOption.pickupValue)
        );
      }
      return false;
    });
    return pickupVehicles.length
      ? pickupVehicles
      : vehicles.filter((vehicle) => !vehicle.pickupId && !vehicle.pickupValue);
  }, [quoteOptions, dynamicPickupId]);
  const selectedQuotePickup = React.useMemo(
    () =>
      (quoteOptions?.pickups || []).find(
        (pickup) => String(pickup.id) === String(dynamicPickupId),
      ) || null,
    [quoteOptions, dynamicPickupId],
  );

  const selectedQuoteVehicle = React.useMemo(() => {
    const vehicles = pickupQuoteVehicles;
    if (dynamicVehicleId) {
      return vehicles.find(
        (vehicle) => String(vehicle.id) === String(dynamicVehicleId),
      );
    }
    if (quote?.selectedVehicle?.id) {
      return quote.selectedVehicle;
    }
    return vehicles[0] || null;
  }, [pickupQuoteVehicles, dynamicVehicleId, quote]);

  const autoSuggestedVehicleLabel = React.useMemo(() => {
    if (dynamicVehicleId) return "";
    const vehicle = quote?.selectedVehicle || quote?.suggestedVehicle;
    if (!vehicle?.vehicleName) return "";
    const capacity = Number(vehicle.capacityMax || 0);
    return capacity > 0
      ? `${vehicle.vehicleName} (max ${capacity})`
      : String(vehicle.vehicleName);
  }, [dynamicVehicleId, quote]);
  // A fixed price pickup bills one flat vehicle amount that does not grow with trip length, so an
  // extension with the vehicle would quote no vehicle cost for the extra days. There is no employee
  // on this page to enter that charge, so extra nights are limited to hotel stay only and extra
  // vehicle days are routed through the team instead.
  const extensionStayOnlyForPickup =
    selectedQuotePickup?.fixedPrice === true ||
    String(selectedQuotePickup?.pricingType || "").toUpperCase() === "FIXED_PRICE";

  const vehicleCountPersonsFor = React.useCallback(
    (vehicle) => {
      const adultCount = Number(adults || 0);
      const ageLimit = Number(vehicle?.childConcessionAgeLimit ?? 0);
      const maxKidsWithoutSeat = Math.max(
        0,
        Number(vehicle?.maxKidsWithoutSeat ?? 0),
      );
      let ignoredKids = 0;
      let countedKids = 0;

      kidAges.slice(0, Number(kids) || 0).forEach((age, index) => {
        if (age === "" || age == null) return;
        const requestedSeat = Object.prototype.hasOwnProperty.call(
          kidVehicleSeatOverrides,
          index,
        )
          ? kidVehicleSeatOverrides[index]
          : Number(age) > ageLimit;

        if (
          !requestedSeat &&
          Number(age) <= ageLimit &&
          ignoredKids < maxKidsWithoutSeat
        ) {
          ignoredKids += 1;
        } else {
          countedKids += 1;
        }
      });

      return adultCount + countedKids;
    },
    [adults, kidAges, kidVehicleSeatOverrides, kids],
  );

  const effectiveKidVehicleSeatNeeded = React.useMemo(() => {
    const ageLimit = Number(selectedQuoteVehicle?.childConcessionAgeLimit ?? 0);
    return Array.from({ length: Number(kids) || 0 }, (_, index) => {
      if (
        Object.prototype.hasOwnProperty.call(kidVehicleSeatOverrides, index)
      ) {
        return kidVehicleSeatOverrides[index];
      }
      const age = kidAges[index];
      if (age === "" || age == null) return true;
      return Number(age) > ageLimit;
    });
  }, [
    kids,
    kidAges,
    kidVehicleSeatOverrides,
    selectedQuoteVehicle?.childConcessionAgeLimit,
  ]);

  const availableQuoteVehicles = React.useMemo(
    () =>
      pickupQuoteVehicles.filter(
        (vehicle) =>
          Number(vehicle.capacityMax || 0) >= vehicleCountPersonsFor(vehicle),
      ),
    [pickupQuoteVehicles, vehicleCountPersonsFor],
  );
  const additionalQuoteVehicles = React.useMemo(
    () => vehiclesForIds(additionalVehicleIds, pickupQuoteVehicles),
    [additionalVehicleIds, pickupQuoteVehicles],
  );
  // Once the group is split across vehicles the first one no longer has to seat everyone; the
  // server checks the seats of all of them together.
  const firstVehicleOptions = additionalVehicleIds.length
    ? pickupQuoteVehicles
    : availableQuoteVehicles;
  const quoteVehicleLabel = vehicleNamesLabel([
    quote?.selectedVehicle,
    ...additionalQuoteVehicles,
  ]);

  const allActiveRoomRates = React.useMemo(
    () =>
      (quoteOptions?.roomRates || [])
        .filter((rate) => rate.active !== false)
        .sort(
          (a, b) =>
            Number(a.displayOrder || 0) - Number(b.displayOrder || 0) ||
            Number(a.nightNumber || 0) - Number(b.nightNumber || 0) ||
            Number(a.id || 0) - Number(b.id || 0),
        ),
    [quoteOptions],
  );

  const roomCityOptions = React.useMemo(
    () =>
      Array.from(
        new Set(
          allActiveRoomRates
            .map((rate) => rate.hotelCity || "Location not set")
            .filter(Boolean),
        ),
      ).sort(),
    [allActiveRoomRates],
  );

  const roomRateOptionsByNight = React.useMemo(() => {
    const groups = new Map();
    allActiveRoomRates.forEach((rate) => {
      const night = Number(rate.nightNumber || 1);
      if (!groups.has(night)) groups.set(night, []);
      groups.get(night).push(rate);
    });
    return Array.from(groups.entries())
      .sort(([a], [b]) => a - b)
      .map(([night, rates]) => {
        const selectedCity =
          selectedRoomCityByNight[String(night)] ||
          rates[0]?.hotelCity ||
          "Location not set";
        const cityRates = allActiveRoomRates.filter(
          (rate) =>
            String(rate.hotelCity || "Location not set") ===
            String(selectedCity),
        );
        return {
          night,
          configuredRates: rates,
          selectedCity,
          rates: cityRates.length ? cityRates : rates,
        };
      });
  }, [allActiveRoomRates, selectedRoomCityByNight]);

  const hotelById = React.useMemo(() => {
    const hotels = new Map();
    (quoteOptions?.availableHotels || []).forEach((hotel) => {
      hotels.set(String(hotel.id), hotel);
    });
    return hotels;
  }, [quoteOptions]);

  const categoryOptionsForRate = React.useCallback(
    (rate) => {
      const hotel = hotelById.get(String(rate?.hotelId));
      return (hotel?.roomCategories || []).filter(
        (category) => category.active !== false,
      );
    },
    [hotelById],
  );

  const hotelForRate = React.useCallback(
    (rate) => hotelById.get(String(rate?.hotelId)) || null,
    [hotelById],
  );

  const roomRateLocationLabel = React.useCallback(
    (rate) => hotelForRate(rate)?.city || rate?.hotelCity || "Location not set",
    [hotelForRate],
  );

  const roomRateHotelCodeLabel = React.useCallback(
    (rate, fallback = "Hotel") =>
      hotelForRate(rate)?.hotelCode || rate?.hotelCode || fallback,
    [hotelForRate],
  );

  const roomRateOptionLabel = React.useCallback(
    (rate, fallback = "Hotel") => {
      const city = roomRateLocationLabel(rate);
      const name = roomRateHotelCodeLabel(rate, fallback);
      // Some hotel codes already carry the city ("Coorg - Residential Hotel"), which read as
      // "Coorg - Coorg - Residential Hotel" once the city was prefixed again.
      const cityAlreadyInName =
        city &&
        name &&
        String(name).toLowerCase().includes(String(city).toLowerCase());
      return [cityAlreadyInName ? "" : city, name].filter(Boolean).join(" - ");
    },
    [roomRateHotelCodeLabel, roomRateLocationLabel],
  );

  const roomRateOptionsForGroup = React.useCallback(
    (group) => {
      const preferredNight = Number(group?.night || 1);
      const optionsByLabel = new Map();
      (group?.rates || []).forEach((rate) => {
        if (!rate?.id) return;
        const label = roomRateOptionLabel(rate);
        const option = {
          value: String(rate.id),
          label,
          nightNumber: Number(rate.nightNumber || 1),
        };
        const existing = optionsByLabel.get(label);
        if (
          !existing ||
          (Number(existing.nightNumber || 1) !== preferredNight &&
            option.nightNumber === preferredNight)
        ) {
          optionsByLabel.set(label, option);
        }
      });
      return Array.from(optionsByLabel.values());
    },
    [roomRateOptionLabel],
  );

  const preferredRoomRateIdForNight = React.useCallback(
    (roomRateId, night) => {
      const rate = allActiveRoomRates.find(
        (item) => String(item.id) === String(roomRateId),
      );
      if (!rate?.id) return String(roomRateId || "");
      const label = roomRateOptionLabel(rate);
      const group = roomRateOptionsByNight.find(
        (item) => Number(item.night) === Number(night),
      );
      const preferred = roomRateOptionsForGroup(group).find(
        (option) => option.label === label,
      );
      return preferred?.value || String(roomRateId);
    },
    [
      allActiveRoomRates,
      roomRateOptionLabel,
      roomRateOptionsByNight,
      roomRateOptionsForGroup,
    ],
  );

  const uniqueRoomRateIdsByLabel = React.useCallback(
    (rateIds) => {
      const seenLabels = new Set();
      return (rateIds || [])
        .filter(Boolean)
        .map((id) => String(id))
        .filter((id) => {
          const rate = allActiveRoomRates.find(
            (item) => String(item.id) === String(id),
          );
          if (!rate?.id) return false;
          const label = roomRateOptionLabel(rate);
          if (seenLabels.has(label)) return false;
          seenLabels.add(label);
          return true;
        });
    },
    [allActiveRoomRates, roomRateOptionLabel],
  );

  const hotelFacilityItems = React.useCallback((hotel, rate = null, stay = null) => {
    const items = [];
    const upsert = (facility) => {
      const name = facility?.name;
      if (!name) return;
      const existing = items.find((item) => item.name === name);
      if (existing) {
        existing.available = existing.available || facility.available === true;
        existing.costBehavior = mergeFacilityCostBehavior(existing, facility);
        existing.optionalWithCost = existing.optionalWithCost || facilityCostBehavior(facility) === "OPTIONAL_PAID";
        existing.extraCost = Number(existing.extraCost || 0) || Number(facility.extraCost || 0);
        existing.pricingType = existing.pricingType || facility.pricingType || "PER_ACTIVITY";
        existing.id = existing.id || facility.id;
        return;
      }
      items.push({
        id: facility.id,
        name,
        available: facility.available === true,
        optionalWithCost: facilityCostBehavior(facility) === "OPTIONAL_PAID",
        costBehavior: facilityCostBehavior(facility),
        pricingType: facility.pricingType || "PER_ACTIVITY",
        extraCost: Number(facility.extraCost || 0),
      });
    };

    [hotel?.facilities, rate?.facilities, stay?.facilities].forEach((facilities) => {
      (facilities || [])
        .filter((facility) => facility?.name && facility?.active !== false)
        .forEach(upsert);
    });

    const addFlag = (label, value) => upsert({ name: label, available: value === true });
    addFlag("Breakfast", stay?.breakfast ?? rate?.breakfast ?? hotel?.breakfast);
    addFlag("Dinner", stay?.dinner ?? rate?.dinner ?? hotel?.dinner);
    addFlag("Campfire", stay?.campfire ?? rate?.campfire ?? hotel?.campfire);
    addFlag("Swimming Pool", stay?.swimmingPool ?? rate?.swimmingPool ?? hotel?.swimmingPool);

    return items;
  }, []);

  const selectedTravelDateValue = React.useMemo(
    () => formatDateParam(selectedDate),
    [selectedDate],
  );

  const maxUnitsForCategory = React.useCallback(
    (category, roomRateId = "") => {
      const configured = Number(category?.maximumUnits || 0);
      const fallback = configured > 0 ? configured : 1;
      if (!roomRateId || !category?.id || !selectedTravelDateValue) {
        return fallback;
      }
      const availability =
        roomAvailabilityByKey[`${roomRateId}:${category.id}`];
      if (!availability) {
        return fallback;
      }
      return Math.max(
        0,
        Math.min(fallback, Number(availability.availableUnits ?? fallback)),
      );
    },
    [roomAvailabilityByKey, selectedTravelDateValue],
  );

  const isCategorySoldOut = React.useCallback(
    (category, roomRateId = "") => {
      if (!roomRateId || !category?.id || !selectedTravelDateValue) return false;
      const availability =
        roomAvailabilityByKey[`${roomRateId}:${category.id}`];
      if (!availability) return false;
      return Number(availability.availableUnits ?? 0) <= 0;
    },
    [roomAvailabilityByKey, selectedTravelDateValue],
  );

  const isRoomRateSoldOut = React.useCallback(
    (rate) => {
      if (!rate?.id) return false;
      const categories = categoryOptionsForRate(rate);
      if (!categories.length) return false;
      return categories.every((category) =>
        isCategorySoldOut(category, rate.id),
      );
    },
    [categoryOptionsForRate, isCategorySoldOut],
  );

  const roomRateById = React.useMemo(() => {
    const rates = new Map();
    allActiveRoomRates.forEach((rate) => {
      if (rate?.id) rates.set(String(rate.id), rate);
    });
    return rates;
  }, [allActiveRoomRates]);

  const maxPeoplePerRoomForCategory = React.useCallback((category) => {
    const configuredMax = Number(category?.maxMemberCount || 0);
    if (configuredMax > 0) return configuredMax;
    const maxMemberCount = (category?.priceOptions || [])
      .filter((option) => Number(option?.memberCount || 0) > 0)
      .reduce(
        (max, option) => Math.max(max, Number(option.memberCount || 0)),
        0,
      );
    return Math.max(1, maxMemberCount);
  }, []);

  const extensionHotelOptions = React.useMemo(
    () =>
      (quoteOptions?.availableHotels || []).filter(
        (hotel) =>
          hotel?.active !== false &&
          (hotel.roomCategories || []).some(
            (category) => category?.active !== false,
          ),
      ),
    [quoteOptions],
  );

  const extensionCityOptions = React.useMemo(
    () =>
      Array.from(
        new Set(
          extensionHotelOptions
            .map((hotel) => hotel.city)
            .filter(Boolean),
        ),
      ).sort(),
    [extensionHotelOptions],
  );

  const extensionHotelsForCity = React.useCallback(
    (city) =>
      extensionHotelOptions.filter(
        (hotel) => !city || String(hotel.city || "") === String(city),
      ),
    [extensionHotelOptions],
  );

  const extensionCategoriesForHotel = React.useCallback(
    (hotelId) =>
      (
        extensionHotelOptions.find(
          (hotel) => String(hotel.id) === String(hotelId),
        )?.roomCategories || []
      ).filter((category) => category?.active !== false),
    [extensionHotelOptions],
  );

  const extensionStayDateValue = React.useCallback(
    (stay) =>
      formatDateParam(
        addLocalDays(
          selectedDate,
          stay?.position === "POST"
            ? Number(quoteOptions?.durationNights || 0) + Number(stay?.nightIndex || 1) - 1
            : -Number(stay?.nightIndex || 1),
        ),
      ),
    [selectedDate, quoteOptions?.durationNights],
  );

  // Extension nights sit outside the trip's own nights, so they need their own lookup, keyed by
  // room category and stay date rather than by room rate.
  useEffect(() => {
    const categoryIds = extensionHotelOptions.flatMap((hotel) =>
      (hotel.roomCategories || [])
        .filter((category) => category?.active !== false)
        .map((category) => category.id),
    );
    if (
      !selectedDate ||
      !categoryIds.length ||
      (extraPreNightCount <= 0 && extraPostNightCount <= 0)
    ) {
      setExtensionAvailabilityByKey({});
      return undefined;
    }
    const startDate = formatDateParam(addLocalDays(selectedDate, -extraPreNightCount));
    const endDate = formatDateParam(
      addLocalDays(
        selectedDate,
        Number(quoteOptions?.durationNights || 0) + extraPostNightCount - 1,
      ),
    );
    let cancelled = false;
    fetchExtensionRoomAvailability(categoryIds, startDate, endDate)
      .then((items) => {
        if (cancelled) return;
        setExtensionAvailabilityByKey(
          Object.fromEntries(
            items.map((item) => [`${item.roomCategoryId}:${item.stayDate}`, item]),
          ),
        );
      })
      .catch(() => {
        if (!cancelled) setExtensionAvailabilityByKey({});
      });
    return () => {
      cancelled = true;
    };
  }, [
    selectedDate,
    extraPreNightCount,
    extraPostNightCount,
    quoteOptions?.durationNights,
    extensionHotelOptions,
  ]);

  const extensionMaxUnits = React.useCallback(
    (category, stay) => {
      const configured = Number(category?.maximumUnits || 0);
      const fallback = configured > 0 ? configured : 1;
      const availability =
        extensionAvailabilityByKey[`${category?.id}:${extensionStayDateValue(stay)}`];
      if (!availability) return fallback;
      return Math.max(
        0,
        Math.min(fallback, Number(availability.availableUnits ?? fallback)),
      );
    },
    [extensionAvailabilityByKey, extensionStayDateValue],
  );

  const suggestedExtensionUnits = React.useCallback(
    (category) => {
      if (!category) return 0;
      const maxPeople = maxPeoplePerRoomForCategory(category);
      const people = Math.max(1, selectedTravellerCount || 1);
      const maxUnits = Math.max(1, Number(category.maximumUnits || 1));
      return Math.min(maxUnits, Math.max(1, Math.ceil(people / maxPeople)));
    },
    [maxPeoplePerRoomForCategory, selectedTravellerCount],
  );

  // The first room this group can fill to the hotel's minimum guests - the first room when the
  // hotel has no minimum, or when none fits and the server has to say why.
  const firstFillableCategory = React.useCallback(
    (categories) =>
      categories.find(
        (category) => Number(category?.minGuests || 0) <= Math.max(1, selectedTravellerCount || 1),
      ) ||
      categories[0] ||
      null,
    [selectedTravellerCount],
  );

  const defaultExtensionStay = React.useCallback(
    (position, previous = {}, nightIndex = 1) => {
      const city =
        previous.city ||
        extensionCityOptions[0] ||
        extensionHotelOptions[0]?.city ||
        "";
      const hotels = extensionHotelsForCity(city);
      const hotel =
        hotels.find((item) => String(item.id) === String(previous.hotelId)) ||
        hotels[0] ||
        extensionHotelOptions[0] ||
        null;
      const categories = extensionCategoriesForHotel(hotel?.id);
      const category =
        categories.find(
          (item) => String(item.id) === String(previous.roomCategoryId),
        ) ||
        firstFillableCategory(categories);
      const previousSelections = Array.isArray(previous.roomCategorySelections)
        ? previous.roomCategorySelections
        : [];
      const roomCategorySelections = previousSelections.length
        ? previousSelections
        : category
          ? [{
              roomCategoryId: category.id,
              units: previous.units || suggestedExtensionUnits(category),
            }]
          : [];
      const primarySelection = roomCategorySelections.find(
        (selection) => Number(selection.units || 0) > 0,
      ) || roomCategorySelections[0] || {};
      return {
        position,
        nightIndex: Number(previous.nightIndex || nightIndex || 1),
        stayKey:
          previous.stayKey ||
          `ext-${position}-${nightIndex}-${Math.random().toString(36).slice(2, 9)}`,
        scope: extensionStayOnlyForPickup ? "STAY" : previous.scope || "BOTH",
        city: hotel?.city || city,
        hotelId: hotel?.id || "",
        roomCategoryId: primarySelection.roomCategoryId || category?.id || "",
        units: primarySelection.units || previous.units || suggestedExtensionUnits(category),
        roomCategorySelections,
        selectedFacilityKeys: previous.selectedFacilityKeys || {},
        note: previous.note || "",
      };
    },
    [
      extensionCategoriesForHotel,
      extensionCityOptions,
      extensionHotelOptions,
      extensionHotelsForCity,
      extensionStayOnlyForPickup,
      firstFillableCategory,
      suggestedExtensionUnits,
    ],
  );

  const categoryCapacityTooltip = React.useCallback(
    (category) => {
      const capacity = maxPeoplePerRoomForCategory(category);
      const kidThreshold = Number(category?.kidRoomCountAgeThreshold ?? 0);
      const kidGracePerRoom = Number(category?.kidGracePerRoom ?? 2);
      const childText =
        kidThreshold > 0
          ? `Kids above ${kidThreshold} years are considered as adults for room occupancy. Up to ${kidGracePerRoom} kid(s) aged ${kidThreshold} years or below are not counted per room.`
          : "All kids are considered as adults for room occupancy.";
      const minGuests = Number(category?.minGuests || 0);
      const minimumText = minGuests > 0 ? ` The hotel gives this room to at least ${minGuests} guests.` : "";
      return `Max adults per room: ${capacity}.${minimumText} ${childText}`;
    },
    [maxPeoplePerRoomForCategory],
  );

  useEffect(() => {
    if (!tripExtensionRequired) {
      setExtensionStays([]);
      setExtraPreNights("");
      setExtraPostNights("");
      setTripExtensionNote("");
      return;
    }
    const desiredPositions = [
      ...Array.from({ length: extraPreNightCount }, (_, index) => ({ position: "PRE", nightIndex: index + 1 })),
      ...Array.from({ length: extraPostNightCount }, (_, index) => ({ position: "POST", nightIndex: index + 1 })),
    ];
    setExtensionStays((previous) => {
      const grouped = previous.reduce((acc, stay) => {
        const key = `${stay.position || "PRE"}:${Number(stay.nightIndex || 1)}`;
        acc[key] = [...(acc[key] || []), stay];
        return acc;
      }, {});
      return desiredPositions.flatMap(({ position, nightIndex }) => {
        const key = `${position}:${nightIndex}`;
        const existing = grouped[key] || [];
        return existing.length
          ? existing.map((stay) => defaultExtensionStay(position, stay, nightIndex))
          : [defaultExtensionStay(position, {}, nightIndex)];
      });
    });
  }, [
    tripExtensionRequired,
    extraPreNightCount,
    extraPostNightCount,
    defaultExtensionStay,
  ]);

  const clampRoomUnits = React.useCallback(
    (value, category, roomRateId = "") => {
      const maxUnits = maxUnitsForCategory(category, roomRateId);
      return Math.min(maxUnits, Math.max(0, Number(value || 0)));
    },
    [maxUnitsForCategory],
  );

  const categoryUnitKey = React.useCallback(
    (roomRateId, categoryId) => `${roomRateId}:${categoryId}`,
    [],
  );

  const resetRoomManualAdjustments = React.useCallback(() => {
    setRoomsManuallyAdjusted(false);
    setManuallyAdjustedRoomUnitKeys({});
  }, []);

  const clearManualRoomUnitKeysForRate = React.useCallback((roomRateId) => {
    setManuallyAdjustedRoomUnitKeys((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${roomRateId}:`)) delete next[key];
      });
      return next;
    });
  }, []);

  const updateRoomCityForNight = React.useCallback((night, city) => {
    const nightKey = String(night);
    const selectedRateIds = selectedRoomRateIdsByNight[nightKey] || [];
    setSelectedRoomCityByNight((prev) => ({ ...prev, [nightKey]: city }));
    setSelectedRoomRateIdsByNight((prev) => ({ ...prev, [nightKey]: [] }));
    setSelectedRoomCategoryUnits((prev) => {
      const next = { ...prev };
      selectedRateIds.forEach((rateId) => {
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${rateId}:`)) delete next[key];
        });
      });
      return next;
    });
    setSelectedHotelFacilityKeys((prev) => {
      const next = { ...prev };
      selectedRateIds.forEach((rateId) => {
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${rateId}:`)) delete next[key];
        });
      });
      return next;
    });
    resetRoomManualAdjustments();
  }, [resetRoomManualAdjustments, selectedRoomRateIdsByNight]);

  const selectedRoomRateIdsForNight = React.useCallback(
    (night) =>
      uniqueRoomRateIdsByLabel(
        (selectedRoomRateIdsByNight[String(night)] || []).map((id) =>
          preferredRoomRateIdForNight(id, night),
        ),
      ),
    [
      preferredRoomRateIdForNight,
      selectedRoomRateIdsByNight,
      uniqueRoomRateIdsByLabel,
    ],
  );

  const selectedRoomRateIds = React.useMemo(
    () =>
      roomRateOptionsByNight
        .flatMap((group) => selectedRoomRateIdsForNight(group.night))
        .filter(Boolean),
    [roomRateOptionsByNight, selectedRoomRateIdsForNight],
  );

  const selectedRoomRateNightSelections = React.useMemo(
    () =>
      roomRateOptionsByNight.flatMap((group) =>
        selectedRoomRateIdsForNight(group.night).map((roomRateId) => ({
          roomRateId: Number(roomRateId),
          nightNumber: Number(group.night),
        })),
      ),
    [roomRateOptionsByNight, selectedRoomRateIdsForNight],
  );

  const roomCategorySelections = React.useMemo(() => {
    const selectionsByKey = new Map();
    roomRateOptionsByNight.forEach((group) => {
      const selectedRateIds = selectedRoomRateIdsForNight(group.night);
      const selectedRates = selectedRateIds
        .map((rateId) =>
          group.rates.find((rate) => String(rate.id) === String(rateId)),
        )
        .filter(Boolean);
      selectedRates.forEach((selectedRate) => {
      categoryOptionsForRate(selectedRate).forEach((category) => {
        const units = Number(
          selectedRoomCategoryUnits[categoryUnitKey(selectedRate.id, category.id)] || 0,
        );
        if (units > 0) {
          const selection = {
            roomRateId: selectedRate?.id ? Number(selectedRate.id) : null,
            roomCategoryId: Number(category.id),
            units: clampRoomUnits(units, category, selectedRate?.id),
          };
          selectionsByKey.set(`${selection.roomRateId}:${selection.roomCategoryId}`, selection);
        }
      });
      });
    });
    return Array.from(selectionsByKey.values());
  }, [
    roomRateOptionsByNight,
    selectedRoomRateIdsForNight,
    selectedRoomCategoryUnits,
    categoryOptionsForRate,
    categoryUnitKey,
    clampRoomUnits,
  ]);

  const selectedHotelFacilities = React.useMemo(() => {
    const selections = [];
    roomRateOptionsByNight.forEach((group) => {
      selectedRoomRateIdsForNight(group.night)
        .map((rateId) => group.rates.find((rate) => String(rate.id) === String(rateId)))
        .filter(Boolean)
        .forEach((rate) => {
          hotelFacilityItems(hotelForRate(rate), rate)
            .filter((facility) => facility.id != null && facility.available && facilityCostBehavior(facility) === "OPTIONAL_PAID")
            .forEach((facility) => {
              const key = `${rate.id}:${facility.id}`;
              if (selectedHotelFacilityKeys[key]) {
                selections.push({ roomRateId: Number(rate.id), facilityId: Number(facility.id), selected: true });
              }
            });
        });
    });
    extensionStays
      .filter((stay) => (stay.scope || "BOTH") !== "VEHICLE" && stay.hotelId)
      .forEach((stay) => {
        const hotel = extensionHotelOptions.find((item) => String(item.id) === String(stay.hotelId));
        if (!hotel) return;
        hotelFacilityItems(hotel)
          .filter((facility) => facility.id != null && facility.available && facilityCostBehavior(facility) === "OPTIONAL_PAID")
          .forEach((facility) => {
            if (stay.selectedFacilityKeys?.[facility.id]) {
              selections.push({
                extensionStayKey: stay.stayKey,
                facilityId: Number(facility.id),
                selected: true,
              });
            }
          });
      });
    return selections;
  }, [
    roomRateOptionsByNight,
    selectedRoomRateIdsForNight,
    extensionStays,
    extensionHotelOptions,
    hotelFacilityItems,
    hotelForRate,
    selectedHotelFacilityKeys,
  ]);
  const highlights = Array.isArray(displayTrip.highlights)
    ? displayTrip.highlights
    : [];
  const termsConditions = Array.isArray(displayTrip.termsConditions)
    ? displayTrip.termsConditions
    : [];

  const getPeopleCount = (vehicleType) => {
    if (!vehicleType) return null;
    const match = vehicleType.match(/(\d+)\s*People/i);
    return match ? Number(match[1]) : null;
  };

  useEffect(() => {
    if (!quoteOptions || !selectedTravelDateValue) {
      setRoomAvailabilityByKey({});
      return undefined;
    }
    let cancelled = false;
    fetchTripRoomAvailability(trip.id, selectedTravelDateValue)
      .then((items) => {
        if (cancelled) return;
        setRoomAvailabilityByKey(
          Object.fromEntries(
            items.map((item) => [
              `${item.roomRateId}:${item.roomCategoryId}`,
              item,
            ]),
          ),
        );
        resetRoomManualAdjustments();
      })
      .catch((error) => {
        console.error("Failed to load room availability", error);
        if (!cancelled) setRoomAvailabilityByKey({});
      });
    return () => {
      cancelled = true;
    };
  }, [quoteOptions, resetRoomManualAdjustments, selectedTravelDateValue, trip.id]);

  useEffect(() => {
    if (!normalizeTripSectionHash(window.location.hash)) {
      window.scrollTo(0, 0);
    }
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      fetchWishlist(parsed.id).then((data) => {
        setWishlist(data);
        setIsWishlisted(data.some((t) => t.path === trip.path));
      });
    }
    handleTravellerCountChange(0);
    if (pickupOptions.length) {
      const defaultPickup =
        pickupOptions.find((option) => option.isDefault) || pickupOptions[0];
      setSelectedPickupOptionId(defaultPickup?.id || "");
    } else {
      setSelectedPickupOptionId("");
    }
    // Load active prices for this trip
    (async () => {
      try {
        const prices = await fetchTripPrices(trip.id, true);
        setPriceOptions(prices);
        // If there is exactly one active price, preselect it
        if (prices && prices.length === 1) {
          setSelectedPriceListId(prices[0].id);
          const people = getPeopleCount(prices[0].vehicleType);
          if (people) handleTravellerCountChange(people);
        }
      } catch (e) {
        // Surface a gentle message; UI still works without prices
        console.error("Failed to load prices", e);
      }
    })();
    (async () => {
      try {
        const options = await fetchTripQuoteOptions(trip.id);
        setQuoteOptions(options);
        setSelectedRoomCityByNight({});
        setNoStayNights([]);
        const firstPickup = (options.pickups || [])[0];
        setDynamicPickupId(firstPickup?.id || "");
        setDynamicVehicleId("");
        setAdditionalVehicleIds([]);
        setSelectedAddonIds(
          (options.addons || [])
            .filter((addon) => addon.includedDefault)
            .map((addon) => addon.id),
        );
        setManuallyToggledAddonIds({});
        handleTravellerCountChange(0);
      } catch (e) {
        setQuoteOptions(null);
        setQuote(null);
      }
    })();
  }, [trip.path, pickupOptions]);

  useEffect(() => {
    const selected = priceOptions.find((p) => p.id === selectedPriceListId);
    const unitPrice = selected?.price ?? trip.price;
    const total = unitPrice * numTravellers;
    setFinalPrice(total - discountAmount);
  }, [numTravellers, discountAmount, selectedPriceListId, priceOptions]);

  useEffect(() => {
    if (
      !quoteOptions ||
      selectedTravellerCount < 1 ||
      !selectedTravelDateValue
    ) {
      setQuote(null);
      setQuoteError("");
      setQuoteLoading(false);
      return;
    }
    if (!kidAgesComplete) {
      setQuote(null);
      return;
    }
    const timer = setTimeout(async () => {
      setQuoteLoading(true);
      setQuoteError("");
      try {
        const calculateQuote = adminCalculatorMode
          ? calculateAdminTripQuote
          : calculateTripQuote;
        const result = await calculateQuote(trip.id, {
          adults: Number(adults || 0),
          kids: Number(kids || 0),
          kidAges: kidAges.map((age) => Number(age)),
          kidVehicleSeatNeeded: effectiveKidVehicleSeatNeeded,
          pickupId: dynamicPickupId ? Number(dynamicPickupId) : null,
          vehicleId: dynamicVehicleId ? Number(dynamicVehicleId) : null,
          additionalVehicles: additionalQuoteVehicles.map((vehicle) => ({
            vehicleId: Number(vehicle.id),
          })),
          acType: dynamicAcType,
          selectedAddonIds,
          selectedAddonOptions,
          selectedRoomRateIds: selectedRoomRateIds
            .filter(Boolean)
            .map((id) => Number(id)),
          selectedRoomRateNightSelections,
          selectedRoomCategoryIds: roomCategorySelections.map(
            (selection) => selection.roomCategoryId,
          ),
          selectedRoomUnits: roomCategorySelections.map(
            (selection) => selection.units,
          ),
          selectedRoomCategorySelections: roomCategorySelections,
          selectedHotelFacilities,
          noStayNights,
          travelDate: selectedTravelDateValue || null,
          tripExtensionRequired,
          extensionScope: extensionStayOnlyForPickup ? "STAY" : "BOTH",
          extraPreNights: extraPreNightCount,
          extraPostNights: extraPostNightCount,
          extensionStays: tripExtensionRequired
            ? extensionStaysRequestPayload(
                extensionStays,
                extensionStayOnlyForPickup ? "STAY" : null,
              )
            : [],
          tripExtensionNote: tripExtensionNote.trim() || null,
        });
        setQuote(result);
        setFinalPrice(
          Number(result.roundedTotalPrice || result.finalTripPrice || 0) -
            discountAmount,
        );
      } catch (e) {
        console.error("Failed to calculate quote", e);
        setQuote(null);
        setQuoteError(quoteErrorMessage(e));
      } finally {
        setQuoteLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [
    quoteOptions,
    adults,
    kids,
    selectedTravellerCount,
    kidAges,
    effectiveKidVehicleSeatNeeded,
    kidAgesComplete,
    dynamicPickupId,
    dynamicVehicleId,
    additionalQuoteVehicles,
    dynamicAcType,
    selectedAddonIds,
    selectedAddonOptions,
    selectedRoomRateIds,
    selectedRoomRateNightSelections,
    roomCategorySelections,
    selectedHotelFacilities,
    noStayNights,
    selectedTravelDateValue,
    adminCalculatorMode,
    tripExtensionRequired,
    extensionStayOnlyForPickup,
    extraPreNightCount,
    extraPostNightCount,
    extensionStays,
    tripExtensionNote,
    discountAmount,
    trip.id,
  ]);

  // Chikmagalur (Bababudangiri / Mullayanagiri) route restricts vehicles above
  // 13 seats, so a trip's "Chikmagalur/Chikmangalur Jeep" add-on must be kept in
  // sync with whichever vehicle the quote actually resolves to (explicit pick or
  // auto-suggest), not just the raw dropdown selection.
  useEffect(() => {
    const jeepAddon = (quoteOptions?.addons || []).find(isChikmagalurJeepAddon);
    if (!jeepAddon) return;
    if (manuallyToggledAddonIds[String(jeepAddon.id)]) return;
    const shouldBeSelected = [quote?.selectedVehicle, ...additionalQuoteVehicles]
      .some((vehicle) => Number(vehicle?.capacityMax || 0) > 13);
    const isSelected = selectedAddonIds.includes(jeepAddon.id);
    if (shouldBeSelected === isSelected) return;
    setSelectedAddonIds((prev) =>
      shouldBeSelected
        ? Array.from(new Set([...prev, jeepAddon.id]))
        : prev.filter((id) => id !== jeepAddon.id)
    );
  }, [quote?.selectedVehicle, additionalQuoteVehicles, quoteOptions, selectedAddonIds, manuallyToggledAddonIds]);

  const handleTravellerCountChange = (value) => {
    const count = Number(value);
    setNumTravellers(count);
  };

  const handleAdultKidChange = (nextAdults, nextKids) => {
    const safeAdults =
      nextAdults === "" ? "" : Math.max(0, Number(nextAdults) || 0);
    const safeKids = nextKids === "" ? "" : Math.max(0, Number(nextKids) || 0);
    const safeKidsCount = Number(safeKids || 0);
    resetRoomManualAdjustments();
    setAdults(safeAdults);
    setKids(safeKids);
    setKidAges((prev) =>
      Array.from({ length: safeKidsCount }, (_, index) => prev[index] ?? ""),
    );
    setKidVehicleSeatOverrides((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([index]) => Number(index) < safeKidsCount),
      ),
    );
    handleTravellerCountChange(Number(safeAdults || 0) + safeKidsCount);
  };

  // The prefill is intentionally applied once per lead/trip after quote options load.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const prefill = leadQuotePrefill;
    if (!quoteOptions || !prefill || prefill.destinationName !== trip.title) return;
    const prefillKey = `${prefill.leadId || ""}-${trip.id}`;
    if (appliedLeadPrefillRef.current === prefillKey) return;
    appliedLeadPrefillRef.current = prefillKey;

    handleAdultKidChange(prefill.adults || 0, prefill.kids || 0);
    setKidAges(Array.from({ length: Number(prefill.kids || 0) }, (_, index) => prefill.kidAges?.[index] ?? ""));
    if (prefill.travelDate && /^\d{4}-\d{2}-\d{2}/.test(prefill.travelDate)) {
      const [year, month, day] = prefill.travelDate.slice(0, 10).split("-").map(Number);
      setSelectedDate(new Date(year, month - 1, day));
    }
    const normalizePickup = (value) => String(value || "").trim().toLowerCase();
    const pickup = (quoteOptions.pickups || []).find((item) =>
      [item.pickupName, item.pickupValue].some((value) =>
        normalizePickup(value) === normalizePickup(prefill.pickupType)));
    setDynamicPickupId(pickup?.id || (quoteOptions.pickups || [])[0]?.id || "");
    const pickupLocation = pickupOptions.find((option) =>
      [option.pickupLabel, option.pickupValue].some((optionValue) =>
        [prefill.pickupType, pickup?.pickupName, pickup?.pickupValue].some((sourceValue) =>
          normalizePickup(optionValue) === normalizePickup(sourceValue))));
    if (pickupLocation) setSelectedPickupOptionId(pickupLocation.id);
    const addonNames = new Set(prefill.addons || []);
    setSelectedAddonIds((quoteOptions.addons || [])
      .filter((addon) => addonNames.has(addon.name) || (addon.includedDefault && addonNames.size === 0))
      .map((addon) => addon.id));
    setTimeout(() => document.getElementById("book-now")?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  }, [leadQuotePrefill, quoteOptions, trip.id, trip.title]);

  const updateKidAge = (index, value) => {
    resetRoomManualAdjustments();
    setKidAges((prev) =>
      Array.from({ length: Number(kids) || 0 }, (_, idx) =>
        idx === index ? value : (prev[idx] ?? ""),
      ),
    );
  };

  const updateKidVehicleSeatNeeded = (index, checked) => {
    setKidVehicleSeatOverrides((prev) => ({ ...prev, [index]: checked }));
  };

  useEffect(() => {
    if (!dynamicVehicleId) return;
    const stillAvailable = firstVehicleOptions.some(
      (vehicle) => String(vehicle.id) === String(dynamicVehicleId),
    );
    if (!stillAvailable) {
      setDynamicVehicleId("");
    }
  }, [firstVehicleOptions, dynamicVehicleId]);

  // Vehicles belong to a pickup, so an added vehicle the new pickup does not offer goes.
  useEffect(() => {
    if (additionalQuoteVehicles.length !== additionalVehicleIds.length) {
      setAdditionalVehicleIds(additionalQuoteVehicles.map((vehicle) => String(vehicle.id)));
    }
  }, [additionalQuoteVehicles, additionalVehicleIds.length]);

  useEffect(() => {
    setSelectedRoomRateIdsByNight((prev) => {
      const next = {};
      roomRateOptionsByNight.forEach((group) => {
        const key = String(group.night);
        const options = roomRateOptionsForGroup(group);
        const optionIds = new Set(options.map((option) => String(option.value)));
        const existing = (prev[key] || [])
          .map((id) => preferredRoomRateIdForNight(id, group.night))
          .filter((id) => optionIds.has(String(id)));
        next[key] = existing.length
          ? existing.map(String)
          : options[0]?.value
            ? [String(options[0].value)]
            : [];
      });
      return next;
    });
  }, [preferredRoomRateIdForNight, roomRateOptionsByNight, roomRateOptionsForGroup]);

  useEffect(() => {
    setSelectedRoomCategoryUnits((prev) => {
      const next = {};
      roomRateOptionsByNight.forEach((group) => {
        selectedRoomRateIdsForNight(group.night).forEach((rateId) => {
          const rate = group.rates.find((item) => String(item.id) === String(rateId));
          const categories = categoryOptionsForRate(rate);
          categories.forEach((category) => {
            const key = categoryUnitKey(rateId, category.id);
            const units = clampRoomUnits(prev[key] || 0, category, rate?.id);
            if (units > 0) {
              next[key] = units;
            }
          });
        });
      });
      return next;
    });
  }, [
    roomRateOptionsByNight,
    selectedRoomRateIdsForNight,
    categoryOptionsForRate,
    clampRoomUnits,
    categoryUnitKey,
  ]);

  useEffect(() => {
    if (
      !quoteOptions ||
      selectedTravellerCount < 1 ||
      !kidAgesComplete ||
      !selectedTravelDateValue ||
      roomRateOptionsByNight.length === 0
    ) {
      return undefined;
    }
    const selectedRateIds = selectedRoomRateIds
      .filter(Boolean)
      .map((id) => Number(id));
    if (selectedRateIds.length === 0) {
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await fetchTripRoomSuggestions(trip.id, {
          adults: Number(adults || 0),
          kids: Number(kids || 0),
          kidAges: kidAges.map((age) => Number(age)),
          selectedRoomRateIds: selectedRateIds,
          travelDate: selectedTravelDateValue || null,
        });
        if (cancelled) return;
        const suggestions = Array.isArray(
          result?.selectedRoomCategorySelections,
        )
          ? result.selectedRoomCategorySelections
          : [];
        setSelectedRoomCategoryUnits((prev) => {
          const next = {};
          roomRateOptionsByNight.forEach((group) => {
            selectedRoomRateIdsForNight(group.night).forEach((selectedRateId) => {
            const rate = group.rates.find(
              (item) => String(item.id) === String(selectedRateId),
            );
            categoryOptionsForRate(rate).forEach((category) => {
              const key = categoryUnitKey(selectedRateId, category.id);
              if (manuallyAdjustedRoomUnitKeys[key]) {
                next[key] = clampRoomUnits(
                  prev[key] || 0,
                  category,
                  selectedRateId,
                );
                return;
              }
              const suggestion = suggestions.find(
                (item) =>
                  String(item.roomRateId) === String(selectedRateId) &&
                  String(item.roomCategoryId) === String(category.id),
              );
              const units = clampRoomUnits(
                suggestion?.units || 0,
                category,
                selectedRateId,
              );
              if (units > 0) {
                next[key] = units;
              }
            });
            });
          });
          return next;
        });
      } catch (error) {
        console.error("Failed to load room suggestions", error);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    quoteOptions,
    selectedTravellerCount,
    kidAgesComplete,
    roomRateOptionsByNight,
    selectedRoomRateIds,
    selectedRoomRateIdsForNight,
    adults,
    kids,
    kidAges,
    selectedTravelDateValue,
    trip.id,
    categoryOptionsForRate,
    clampRoomUnits,
    categoryUnitKey,
    manuallyAdjustedRoomUnitKeys,
  ]);

  const updateSelectedRoomRate = (night, previousRateId, value) => {
    clearManualRoomUnitKeysForRate(previousRateId);
    setSelectedRoomRateIdsByNight((prev) => {
      const key = String(night);
      const current = (prev[key] || []).map(String);
      return {
        ...prev,
        [key]: current.map((id) =>
          String(id) === String(previousRateId) ? String(value) : id,
        ),
      };
    });
    setSelectedRoomCategoryUnits((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${previousRateId}:`)) delete next[key];
      });
      return next;
    });
  };

  const addSelectedRoomRate = (night) => {
    const group = roomRateOptionsByNight.find(
      (item) => Number(item.night) === Number(night),
    );
    if (!group) return;
    const selectedIds = selectedRoomRateIdsForNight(night);
    const nextOption = roomRateOptionsForGroup(group).find(
      (option) => !selectedIds.includes(String(option.value)),
    );
    if (!nextOption?.value) return;
    setSelectedRoomRateIdsByNight((prev) => ({
      ...prev,
      [String(night)]: [...selectedIds, String(nextOption.value)],
    }));
  };

  const removeSelectedRoomRate = (night, rateId) => {
    clearManualRoomUnitKeysForRate(rateId);
    setSelectedRoomRateIdsByNight((prev) => ({
      ...prev,
      [String(night)]: selectedRoomRateIdsForNight(night).filter(
        (id) => String(id) !== String(rateId),
      ),
    }));
    setSelectedRoomCategoryUnits((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${rateId}:`)) delete next[key];
      });
      return next;
    });
  };

  const updateSelectedRoomCategoryUnits = (roomRateId, category, delta) => {
    setRoomsManuallyAdjusted(true);
    setManuallyAdjustedRoomUnitKeys((prev) => ({
      ...prev,
      [categoryUnitKey(roomRateId, category.id)]: true,
    }));
    setSelectedRoomCategoryUnits((prev) => {
      const key = categoryUnitKey(roomRateId, category.id);
      const nextUnits = clampRoomUnits(
        Number(prev[key] || 0) + delta,
        category,
        roomRateId,
      );
      return {
        ...prev,
        [key]: nextUnits,
      };
    });
  };

  const formatAmount = (value) =>
    Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

  const pluralize = (count, singular, plural = `${singular}s`) =>
    `${count} ${Number(count) === 1 ? singular : plural}`;

  const NO_STAY_TEXT = "No stay (own arrangement)";

  // Package stays in night order with a placeholder for each no-stay night, so quotations say
  // "No stay" for that night instead of silently skipping it. Extension stays follow as before.
  const quoteStaysInNightOrder = (quoteValue) => [
    ...[
      ...(quoteValue?.roomStayCosts || []),
      ...(quoteValue?.noStayNights || []).map((night) => ({
        noStay: true,
        nightNumber: Number(night),
      })),
    ].sort((a, b) => Number(a.nightNumber || 0) - Number(b.nightNumber || 0)),
    ...(quoteValue?.extensionStayCosts || []),
  ];

  // Extra nights the quote adds before and after the package; none when there is no extension.
  const quoteExtensionNights = (quoteValue) => {
    if (!tripExtensionRequired && !quoteValue?.tripExtensionRequired) {
      return { pre: 0, post: 0 };
    }
    return {
      pre: Math.max(0, Number(quoteValue?.extraPreNights || extraPreNightCount) || 0),
      post: Math.max(0, Number(quoteValue?.extraPostNights || extraPostNightCount) || 0),
    };
  };

  // Every night the customer is away, in date order: extra pre-nights (saved as -1, -2 ...) come
  // before the package, extra post-nights after it. An extra night with no hotel on the quote is
  // vehicle only, so it reads "No stay" like a package no-stay night.
  const quoteStaysByDay = (quoteValue) => {
    const stays = [
      ...(quoteValue?.roomStayCosts || []),
      ...(quoteValue?.extensionStayCosts || []),
    ];
    const { pre, post } = quoteExtensionNights(quoteValue);
    const baseNights = Number(quoteOptions?.durationNights || 0);
    const extraNights = [
      ...Array.from({ length: pre }, (_, index) => -(index + 1)),
      ...Array.from({ length: post }, (_, index) => baseNights + index + 1),
    ];
    const noStayNights = [
      ...(quoteValue?.noStayNights || []).map(Number),
      ...extraNights.filter(
        (night) => !stays.some((stay) => Number(stay.nightNumber) === night),
      ),
    ];
    return [
      ...stays,
      ...noStayNights.map((night) => ({ noStay: true, nightNumber: night })),
    ].sort((a, b) => Number(a.nightNumber || 0) - Number(b.nightNumber || 0));
  };

  // Customers count days from the first night they stay, so an extra pre-night is Day 1 and the
  // package's own Day 1 moves down. Each day carries its check-in date rather than internal
  // wording like "Extra pre-night 1".
  const quoteStayDayLabel = (quoteValue, stay) => {
    const night = Number(stay?.nightNumber || 0);
    if (!night) return "";
    const offset = night < 0 ? night : night - 1;
    const date = formatDisplayDate(addLocalDays(selectedDate, offset));
    const day = `Day ${offset + quoteExtensionNights(quoteValue).pre + 1}`;
    if (!date) return day;
    return `${day} (${stay.noStay ? "" : "Check-in "}${date})`;
  };
  const withDayLabel = (day, text) => (day ? `${day} : ${text}` : text);

  const quoteHotelRoomDetailLines = (quoteValue, plainMarks = false) => {
    const lines = quoteStaysByDay(quoteValue)
      .map((stay) => {
        if (stay.noStay) return withDayLabel(quoteStayDayLabel(quoteValue, stay), NO_STAY_TEXT);
        const allocation = stay.roomAllocation;
        if (!allocation) return "";
        const optionRooms = Array.isArray(allocation.roomOptions)
          ? allocation.roomOptions.reduce(
              (total, option) => total + Number(option.rooms || 0),
              0,
            )
          : 0;
        const rooms =
          optionRooms ||
          Number(allocation.rooms2 || 0) +
            Number(allocation.rooms3 || 0) +
            Number(allocation.rooms4 || 0);
        if (rooms <= 0) return "";
        const day = quoteStayDayLabel(quoteValue, stay);
        const details = [
          stay.hotelCity,
          stay.hotelCode,
          stay.roomCategoryName,
          `Total Units Provided: ${rooms}`,
          stayMealText(stay),
        ]
          .filter(Boolean)
          .join(" - ");
        const facilities = plainMarks ? hotelFacilityPdfLabels(stay) : hotelFacilityLabels(stay);
        const facilityText = facilities.length ? `\nFacilities:\n${facilities.join("\n")}` : "";
        const optionalFacilities = optionalHotelFacilityLabels(stay);
        const optionalFacilityText = optionalFacilities.length
          ? `\nOptional Facilities:\n${optionalFacilities.join("\n")}`
          : "";
        return details ? `${withDayLabel(day, details)}${facilityText}${optionalFacilityText}` : "";
      })
      .filter(Boolean);
    return Array.from(new Set(lines));
  };

  // "Meals: Breakfast + Dinner included" on each night of the quotation - what customers keep and
  // compare with other agents, so it says meals as plainly as the page does. A paid meal the
  // customer ticked is in the price, so it counts as included here.
  const stayMealText = (stay) => {
    const available = (stay?.facilities || []).filter((facility) => facility?.name && facility.available === true);
    const selectedIds = new Set((stay?.selectedHotelFacilities || []).map((facility) => String(facility?.id || "")));
    const selectedNames = new Set((stay?.selectedHotelFacilities || []).map((facility) => String(facility?.name || "")));
    const chosen = (facility) =>
      selectedIds.has(String(facility.id || "")) || selectedNames.has(String(facility.name || ""));
    const included = available.filter((facility) => facilityCostBehavior(facility) !== "OPTIONAL_PAID" || chosen(facility));
    const extras = available.filter((facility) => facilityCostBehavior(facility) === "OPTIONAL_PAID" && !chosen(facility));
    const legacyMeals = [stay?.breakfast ? "Breakfast" : "", stay?.dinner ? "Dinner" : ""].filter(Boolean);
    return `Meals: ${mealPlanFromFacilities(
      [...included.map((facility) => facility.name), ...legacyMeals],
      extras.map((facility) => facility.name),
    ).label}`;
  };

  const hotelFacilityLabels = (stay) =>
    (stay?.facilities || [])
      .filter((facility) => facility?.name && facility.available === true && facilityCostBehavior(facility) !== "OPTIONAL_PAID")
      .map((facility) => hotelFacilityDisplayLabel(facility, stay?.nightNumber));

  const hotelFacilityPdfLabels = (stay) => hotelFacilityLabels(stay);

  const optionalHotelFacilityLabels = (stay) => {
    const selectedIds = new Set((stay?.selectedHotelFacilities || []).map((facility) => String(facility?.id || "")));
    const selectedNames = new Set((stay?.selectedHotelFacilities || []).map((facility) => String(facility?.name || "")));
    return (stay?.facilities || [])
      .filter((facility) => facility?.name && facility.available === true && facilityCostBehavior(facility) === "OPTIONAL_PAID")
      .map((facility) => {
        const selected = selectedIds.has(String(facility.id || "")) || selectedNames.has(String(facility.name || ""));
        return `${hotelFacilityPriceLabel(facility)}: ${selected ? "Yes" : "No"}`;
      });
  };

  const hotelFacilityPriceLabel = (facility) =>
    `${facility.name} (+Rs. ${formatAmount(facility.extraCost || 0)}${facility.pricingType === "PER_PERSON" ? " / person / night" : " / night"})`;

  // One night of the customer quotation, split so each part reads on its own: where they stay and
  // how many rooms, the meals, what the price includes, and what they can add at extra cost. A paid
  // extra is "included" only when the customer ticked it; otherwise it is offered with its price,
  // never marked as missing - a cross next to Breakfast read as "this hotel has no breakfast".
  const quoteStayCustomerDetails = (quoteValue, stay) => {
    const day = quoteStayDayLabel(quoteValue, stay);
    if (stay.noStay) {
      return { day, place: NO_STAY_TEXT, meals: "", included: [], canAdd: [] };
    }
    if (!stay.roomAllocation) return null;
    const rooms = quoteStayRoomUnits(stay);
    if (rooms <= 0) return null;
    const selectedIds = new Set((stay.selectedHotelFacilities || []).map((facility) => String(facility?.id || "")));
    const selectedNames = new Set((stay.selectedHotelFacilities || []).map((facility) => String(facility?.name || "")));
    const chosen = (facility) =>
      selectedIds.has(String(facility.id || "")) || selectedNames.has(String(facility.name || ""));
    const available = (stay.facilities || []).filter((facility) => facility?.name && facility.available === true);
    const optional = (facility) => facilityCostBehavior(facility) === "OPTIONAL_PAID";
    const night = Number(stay.nightNumber || 0);
    const checkoutDate = night
      ? formatDisplayDate(addLocalDays(selectedDate, night < 0 ? night + 1 : night))
      : "";
    const includedName = (facility) =>
      String(facility.name).trim().toLowerCase() === "breakfast"
        ? `Breakfast on checkout morning${checkoutDate ? ` (${checkoutDate})` : ""}`
        : facility.name;
    return {
      day,
      place: [stay.hotelCity, stay.hotelCode, stay.roomCategoryName, pluralize(rooms, "Room")]
        .filter(Boolean)
        .join(" - "),
      meals: stayMealText(stay),
      included: available.filter((facility) => !optional(facility) || chosen(facility)).map(includedName),
      canAdd: available
        .filter((facility) => optional(facility) && !chosen(facility))
        .map((facility) =>
          `${facility.name} Rs. ${formatAmount(facility.extraCost || 0)}${facility.pricingType === "PER_PERSON" ? "/person/night" : "/night"}`,
        ),
    };
  };

  const quoteHotelRoomQuotationLines = (quoteValue) =>
    Array.from(
      new Set(
        quoteStaysByDay(quoteValue)
          .map((stay) => quoteStayCustomerDetails(quoteValue, stay))
          .filter(Boolean)
          .map(({ day, place, meals, included, canAdd }) =>
            [
              // Bold day label so each day stands out in WhatsApp.
              withDayLabel(day ? `*${day}*` : "", place),
              meals ? `   ${meals}` : "",
              included.length ? `   Included: ${included.map((name) => `${name} \u2705`).join(", ")}` : "",
              canAdd.length ? `   Can add: ${canAdd.join(", ")}` : "",
            ]
              .filter(Boolean)
              .join("\n"),
          ),
      ),
    );

  const quoteHotelRoomPdfDetails = (quoteValue) =>
    Array.from(
      new Map(
        quoteStaysByDay(quoteValue)
          .map((stay) => quoteStayCustomerDetails(quoteValue, stay))
          .filter(Boolean)
          .map(({ day, place, meals, included, canAdd }) => {
            const item = {
              text: withDayLabel(day, place),
              meals,
              facilities: included.map((name) => ({ name, label: name, available: true })),
              optionalFacilities: canAdd.map((label) => ({ name: label, label, neutral: true })),
            };
            return [JSON.stringify(item), item];
          }),
      ).values(),
    );

  const chargeablePersonText = (quoteValue) => {
    const count = Number(
      quoteValue?.chargeablePersons || quoteValue?.totalTravellers || 0,
    );
    return count > 0 ? pluralize(count, "person") : "selected persons";
  };

  // What a customer quotation says about the extension: just that there is one. The final length
  // is in the trip title and the nights show under Hotel/Room Details with their dates.
  const customerTripExtensionText = (quoteValue) =>
    tripExtensionSummary(quoteValue) ? "Yes" : "";

  // The trip's length as the customer will travel it: the package's own duration, or the extended
  // length with how many extra nights it includes, e.g. "2 Nights / 3 Days (includes 1 extra night)".
  const quotationDurationText = (quoteValue) => {
    const { pre, post } = quoteExtensionNights(quoteValue);
    const finalNights = Number(quoteValue?.finalDurationNights || 0);
    const finalDays = Number(quoteValue?.finalDurationDays || 0);
    if (!(pre + post) || finalNights <= 0 || finalDays <= 0) {
      return trip.duration || "Duration not specified";
    }
    return `${pluralize(finalNights, "Night")} / ${pluralize(finalDays, "Day")} (includes ${pluralize(pre + post, "extra night")})`;
  };

  // The advance a customer pays to confirm, per chargeable person.
  const QUOTATION_ADVANCE_PER_PERSON = 1000;
  const quotationAdvanceText = (quoteValue) => {
    const persons = Number(quoteValue?.chargeablePersons || quoteValue?.totalTravellers || 0);
    const perPerson = `Rs. ${formatAmount(QUOTATION_ADVANCE_PER_PERSON)} per person`;
    return persons > 0
      ? `${perPerson} (Rs. ${formatAmount(QUOTATION_ADVANCE_PER_PERSON * persons)} for ${pluralize(persons, "person")})`
      : perPerson;
  };

  // The quotation's Date: the package date, or the whole span from the first extra pre-night to
  // the checkout day when the trip is extended.
  const quotationDateText = (quoteValue) => {
    if (!selectedDate) return "Not selected";
    const { pre, post } = quoteExtensionNights(quoteValue);
    if (!pre && !post) return formatDisplayDate(selectedDate);
    const totalNights =
      Number(quoteValue?.finalDurationNights || 0) ||
      Number(quoteOptions?.durationNights || 0) + pre + post;
    const start = addLocalDays(selectedDate, -pre);
    return `${formatDisplayDate(start)} to ${formatDisplayDate(addLocalDays(start, totalNights))}`;
  };

  const tripExtensionSummary = (quoteValue) => {
    if (!tripExtensionRequired && !quoteValue?.tripExtensionRequired) {
      return "";
    }
    const items = [];
    const preNights = Number(quoteValue?.extraPreNights || extraPreNightCount);
    const postNights = Number(
      quoteValue?.extraPostNights || extraPostNightCount,
    );
    const transportDays = Number(quoteValue?.extraTransportDays || 0);
    const finalNights = Number(quoteValue?.finalDurationNights || 0);
    const finalDays = Number(quoteValue?.finalDurationDays || 0);
    const extensionCost = Number(quoteValue?.tripExtensionCost || 0);
    const note = tripExtensionNote.trim();
    const extensionStays = quoteValue?.extensionStayCosts || [];
    if (
      preNights <= 0 &&
      postNights <= 0 &&
      transportDays <= 0 &&
      extensionCost <= 0 &&
      !extensionStays.length &&
      !note
    ) {
      return "";
    }
    const totalNights = preNights + postNights;
    const scopeLabel =
      transportDays <= 0 && extensionStays.length > 0
        ? "stay only"
        : transportDays > 0 && extensionStays.length <= 0
          ? "vehicle only"
          : transportDays > 0 && transportDays !== totalNights
            ? "mixed stay/vehicle by night"
            : "stay and vehicle";
    items.push(`applies to ${scopeLabel}`);
    if (preNights > 0) items.push(`${pluralize(preNights, "pre-night")}`);
    if (postNights > 0) items.push(`${pluralize(postNights, "post-night")}`);
    if (transportDays > 0) {
      items.push(`${pluralize(transportDays, "extra transport day")}`);
    }
    if (finalNights > 0 && finalDays > 0) {
      items.push(`final duration ${finalNights}N/${finalDays}D`);
    }
    if (extensionCost > 0) {
      items.push(`extension cost Rs. ${formatAmount(extensionCost)}`);
    }
    extensionStays.forEach((stay) => {
      const label = [
        stay.roomLabel,
        stay.hotelCity,
        stay.hotelCode,
        stay.roomCategoryName,
      ]
        .filter(Boolean)
        .join(" - ");
      if (label) items.push(label);
    });
    if (note) items.push(note);
    return items.join(" | ");
  };

  const customizationPriceAmount = customizationRequired
    ? Math.max(0, Number(customizationPrice) || 0)
    : 0;
  const signedCustomizationPriceAmount =
    customizationPriceAdjustment === "REDUCE"
      ? -customizationPriceAmount
      : customizationPriceAmount;
  const quotedTotalPrice = (quoteValue) =>
    Math.max(
      0,
      Number(quoteValue?.roundedTotalPrice || quoteValue?.finalTripPrice || 0) +
        signedCustomizationPriceAmount,
    );
  const quotedPerPersonPrice = (quoteValue) => {
    const count = Number(
      quoteValue?.chargeablePersons || quoteValue?.totalTravellers || 0,
    );
    return count > 0
      ? Math.round(quotedTotalPrice(quoteValue) / count)
      : Number(quoteValue?.roundedPerPersonPrice || 0);
  };
  const quoteAddonTotal = (quoteValue) => {
    const explicit =
      quoteValue?.addonsCost ??
      quoteValue?.addonCost ??
      quoteValue?.mandalpattiJeepCost;
    if (explicit != null) return Number(explicit || 0);
    return (quoteValue?.selectedAddons || []).reduce(
      (total, addon) =>
        total + Number(addon?.selectedCost ?? addon?.cost ?? 0),
      0,
    );
  };
  const quoteAddonNames = (quoteValue) =>
    (quoteValue?.selectedAddons || [])
      .map((addon) => addon?.displayName || addon?.name || addon?.addonName)
      .filter(Boolean)
      .join(", ");
  const quoteStayRoomUnits = (stay) => {
    const allocation = stay?.roomAllocation;
    if (!allocation) {
      return Number(stay?.units || stay?.roomUnits || stay?.totalUnits || 0);
    }
    const optionRooms = Array.isArray(allocation.roomOptions)
      ? allocation.roomOptions.reduce(
          (total, option) => total + Number(option.rooms || 0),
          0,
        )
      : 0;
    return (
      optionRooms ||
      Number(allocation.rooms2 || 0) +
        Number(allocation.rooms3 || 0) +
        Number(allocation.rooms4 || 0)
    );
  };
  const selectedHotelFacilityLabelsForStay = (stay) =>
    (stay?.selectedHotelFacilities || [])
      .map((facility) => facility?.label || facility?.name)
      .filter(Boolean);
  const externalVendorFacilityLabelsForStay = (stay) =>
    (stay?.externalVendorFacilities || [])
      .map((facility) => {
        const name = facility?.name || facility?.facilityName;
        const cost = Number(facility?.cost || facility?.amount || 0);
        return name
          ? `${name}${cost > 0 ? ` Rs. ${formatAmount(cost)}` : ""}`
          : "";
      })
      .filter(Boolean);
  const adminQuoteBreakupRows = (quoteValue) => {
    const stays = quoteStaysInNightOrder(quoteValue);
    const rows = [
      { label: "Travellers", value: quoteValue?.totalTravellers || 0, plain: true },
      { label: "Base Trip Cost", value: quoteValue?.baseTripCost || 0 },
      {
        label: "Hotel / Room Cost",
        value: quoteValue?.totalRoomCostAcrossAllNights || 0,
      },
      ...stays.map((stay) => stay.noStay ? ({
        label: `Night ${stay.nightNumber} - ${NO_STAY_TEXT}`,
        note: "",
        value: 0,
        subrow: true,
      }) : ({
        label: [
          stay.nightNumber ? `Night ${stay.nightNumber}` : stay.roomLabel,
          stay.hotelCity,
          stay.hotelName,
          stay.hotelCode,
          stay.roomCategoryName,
        ]
          .filter(Boolean)
          .join(" - "),
        note: [
          quoteStayRoomUnits(stay) > 0
            ? `Total Units Provided: ${quoteStayRoomUnits(stay)}`
            : "",
          selectedHotelFacilityLabelsForStay(stay).length
            ? `Selected facilities: ${selectedHotelFacilityLabelsForStay(stay).join(", ")}`
            : "",
          externalVendorFacilityLabelsForStay(stay).length
            ? `External vendor: ${externalVendorFacilityLabelsForStay(stay).join(", ")}`
            : "",
        ]
          .filter(Boolean)
          .join(" | "),
        value: stay.roomPrice || 0,
        subrow: true,
      })),
    ];
    if (Number(quoteValue?.externalVendorFacilityCost || 0) > 0) {
      rows.push({
        label: "External Vendor Facilities",
        value: quoteValue.externalVendorFacilityCost,
      });
    }
    rows.push(
      { label: "Vehicle KM Cost", value: quoteValue?.vehicleKmCost || 0 },
      { label: "Driver Beta", value: quoteValue?.driverBetaCost || 0 },
      { label: "Tolls", value: quoteValue?.tolls || 0 },
      { label: "Parking", value: quoteValue?.parking || 0 },
      {
        label: "Total Vehicle Cost",
        value: quoteValue?.vehicleCost || 0,
        strong: true,
      },
      {
        label: `Add-ons${quoteAddonNames(quoteValue) ? `: ${quoteAddonNames(quoteValue)}` : ""}`,
        value: quoteAddonTotal(quoteValue),
      },
      { label: "Profit", value: quoteValue?.profitAmount || 0 },
    );
    if (signedCustomizationPriceAmount !== 0) {
      rows.push({
        label: "Customization",
        value: Math.abs(signedCustomizationPriceAmount),
        prefix: signedCustomizationPriceAmount < 0 ? "- " : "+ ",
      });
    }
    rows.push(
      {
        label: "Final Total",
        value: quotedTotalPrice(quoteValue),
        strong: true,
        total: true,
      },
      {
        label: `Per Person (${chargeablePersonText(quoteValue)})`,
        value: quotedPerPersonPrice(quoteValue),
        muted: true,
      },
    );
    return rows;
  };
  const renderAdminQuoteBreakup = (quoteValue) => (
    <div className="mt-3 pt-3 border-top small trip-admin-breakup">
      <div className="fw-semibold mb-2">Calculation Details</div>
      <div className="d-flex flex-column">
        {adminQuoteBreakupRows(quoteValue).map((row, index) => (
          <div
            key={`${row.label}-${index}`}
            className={[
              "d-flex justify-content-between border-bottom py-1 gap-3",
              "trip-admin-breakup-row",
              row.subrow ? "ps-3 trip-admin-breakup-subrow" : "",
              row.strong ? "fw-semibold" : "",
              row.total ? "trip-admin-breakup-total mt-1" : "",
              row.muted ? "text-muted" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <span>
              {row.label}
              {row.note && <span className="d-block trip-admin-breakup-note">{row.note}</span>}
            </span>
            <span className="text-nowrap">
              {row.plain
                ? row.value
                : `${row.prefix || ""}Rs. ${formatAmount(row.value)}`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const quoteTravellerSummary = (quoteValue) =>
    [
      `${quoteValue?.adultsCount ?? adults} adult${Number(quoteValue?.adultsCount ?? adults) === 1 ? "" : "s"}`,
      Number(quoteValue?.kidsCount ?? kids ?? 0) > 0
        ? `${quoteValue?.kidsCount ?? kids ?? 0} kid${Number(quoteValue?.kidsCount ?? kids ?? 0) === 1 ? "" : "s"}`
        : "",
    ]
      .filter(Boolean)
      .join(", ");

  const quoteCapacityFallbackMessage = () => {
    const personCount = Math.max(1, Number(adults || 0) + Number(kids || 0));
    const selectedGroup =
      roomRateOptionsByNight.find((group) => {
        const selectedRates = selectedRoomRateIdsForNight(group.night)
          .map((rateId) =>
            group.rates.find((rate) => String(rate.id) === String(rateId)),
          )
          .filter(Boolean);
        const selectedCapacity = selectedRates.reduce((rateTotal, selectedRate) =>
          rateTotal + categoryOptionsForRate(selectedRate).reduce(
            (total, category) => {
              const units = Number(
                selectedRoomCategoryUnits[categoryUnitKey(selectedRate.id, category.id)] ||
                  0,
              );
              return total + units * maxPeoplePerRoomForCategory(category);
            },
            0,
          ), 0);
        return selectedCapacity > 0 && selectedCapacity < personCount;
      }) ||
      roomRateOptionsByNight.find((group) => {
        const selectedRates = selectedRoomRateIdsForNight(group.night)
          .map((rateId) =>
            group.rates.find((rate) => String(rate.id) === String(rateId)),
          )
          .filter(Boolean);
        return selectedRates.some((selectedRate) =>
          categoryOptionsForRate(selectedRate).some(
            (category) =>
              Number(
                selectedRoomCategoryUnits[categoryUnitKey(selectedRate.id, category.id)] ||
                  0,
              ) > 0,
          ),
        );
      }) ||
      roomRateOptionsByNight[0];
    const selectedRate = selectedGroup
      ? selectedGroup.rates.find((rate) =>
          selectedRoomRateIdsForNight(selectedGroup.night).includes(String(rate.id)),
        ) || selectedGroup.rates[0]
      : null;
    const location = selectedRate
      ? roomRateLocationLabel(selectedRate)
      : "the selected location";
    return `Please add more rooms or choose a larger room in ${location} for ${pluralize(personCount, "person")}.`;
  };

  const quoteErrorMessage = (error) => {
    const data = error?.response?.data || {};
    const message = data.message || data.detail || data.reason;
    if (message && !["Bad Request", "400 BAD_REQUEST"].includes(message)) {
      return message;
    }
    return quoteCapacityFallbackMessage();
  };

  // True while the per-night hotel prices are being refreshed, so the picker can say so rather
  // than showing stale differences as if they were current.
  const [hotelPricesLoading, setHotelPricesLoading] = React.useState(false);

  // Customer-set price window per night. Cleared whenever the prices behind it move, so a stale
  // window can never silently hide every hotel.
  const [hotelPriceRanges, setHotelPriceRanges] = React.useState({});

  // Per-night hotel prices for the dropdown, keyed night -> roomRateId -> priced option.
  const [hotelOptionPrices, setHotelOptionPrices] = React.useState({});

  const priceCardRef = React.useRef(null);
  const [priceCardVisible, setPriceCardVisible] = React.useState(true);

  // The sticky bar is a stand-in for the price card, so it should only appear once the real one is
  // off screen - two copies of the same total on screen at once is noise, not help.
  React.useEffect(() => {
    const node = priceCardRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setPriceCardVisible(entry.isIntersecting),
      { rootMargin: "-80px 0px 0px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [quote]);

  // Per-extension-night hotel prices, keyed stayKey -> hotelId -> priced option.
  const [extensionOptionPrices, setExtensionOptionPrices] = React.useState({});

  // Prices the hotels offered for each extra pre/post night. Built the same way as the main
  // nights, except an extension is chosen by hotel, so we hand the server the exact stay the page
  // would build for each one - first category, suggested units - and it prices that.
  React.useEffect(() => {
    if (!quoteOptions || !quote || !trip?.id || !tripExtensionRequired) {
      setExtensionOptionPrices({});
      return undefined;
    }
    const stays = (extensionStays || []).filter((stay) => stay.stayKey && stay.city);
    if (!stays.length) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const payload = quoteRequestPayload();
        const results = await Promise.all(
          stays.map(async (stay) => {
            const candidateHotels = extensionHotelOptions.filter(
              (hotel) => String(hotel.city || "") === String(stay.city || ""),
            );
            const candidateExtensionStays = candidateHotels
              .map((hotel) => {
                const category = firstFillableCategory(extensionCategoriesForHotel(hotel.id));
                if (!category) return null;
                const units = suggestedExtensionUnits(category);
                return {
                  stayKey: stay.stayKey,
                  position: stay.position,
                  nightIndex: Math.max(1, Number(stay.nightIndex || 1)),
                  scope: stay.scope || "BOTH",
                  city: stay.city,
                  hotelId: Number(hotel.id),
                  roomCategoryId: Number(category.id),
                  units,
                  roomCategorySelections: [
                    { roomCategoryId: Number(category.id), units: Math.max(1, units) },
                  ],
                };
              })
              .filter(Boolean);
            if (!candidateExtensionStays.length) return [stay.stayKey, {}];
            const rows = await fetchHotelOptionPrices(trip.id, {
              quote: payload,
              extensionStayKey: stay.stayKey,
              candidateExtensionStays,
            });
            const byHotel = {};
            rows.forEach((row) => {
              if (row?.hotelId != null) byHotel[String(row.hotelId)] = row;
            });
            return [stay.stayKey, byHotel];
          }),
        );
        if (cancelled) return;
        setExtensionOptionPrices(Object.fromEntries(results));
      } catch (error) {
        if (!cancelled) setExtensionOptionPrices({});
        console.error("Failed to price extension hotel options", error);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quote,
    quoteOptions,
    trip?.id,
    tripExtensionRequired,
    extensionStays,
    extensionHotelOptions,
    extensionCategoriesForHotel,
    firstFillableCategory,
    suggestedExtensionUnits,
  ]);

  // Prices every hotel on each night's dropdown, one request per night, after the main quote has
  // settled. Refires whenever the quote changes because the figures shown are differences against
  // whatever is currently selected.
  React.useEffect(() => {
    if (!quoteOptions || !quote || !trip?.id) return undefined;
    const groups = roomRateOptionsByNight;
    if (!groups.length) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setHotelPricesLoading(true);
      try {
        const payload = quoteRequestPayload();
        const results = await Promise.all(
          groups.map(async (group) => {
            // No hotel is picked for a no-stay night, so there is nothing to compare.
            if (noStayNights.includes(Number(group.night))) return [group.night, {}];
            const candidateRoomRateIds = roomRateOptionsForGroup(group)
              .map((option) => Number(option.value))
              .filter((id) => Number.isFinite(id));
            if (!candidateRoomRateIds.length) return [group.night, {}];
            const rows = await fetchHotelOptionPrices(trip.id, {
              quote: payload,
              nightNumber: Number(group.night),
              candidateRoomRateIds,
            });
            const byRate = {};
            rows.forEach((row) => {
              if (row?.roomRateId != null) byRate[String(row.roomRateId)] = row;
            });
            return [group.night, byRate];
          }),
        );
        if (cancelled) return;
        setHotelOptionPrices(
          Object.fromEntries(results.map(([night, byRate]) => [String(night), byRate])),
        );
        setHotelPriceRanges({});
      } catch (error) {
        // A pricing failure must never break hotel selection - the dropdown falls back to plain
        // names in its configured order.
        if (!cancelled) setHotelOptionPrices({});
        console.error("Failed to price hotel options", error);
      } finally {
        if (!cancelled) setHotelPricesLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, quoteOptions, trip?.id, roomRateOptionsByNight, roomRateOptionsForGroup]);

  const quoteRequestPayload = (overrides = {}) => ({
    adults: Number(adults || 0),
    kids: Number(kids || 0),
    kidAges: kidAges.map((age) => Number(age)),
    kidVehicleSeatNeeded: effectiveKidVehicleSeatNeeded,
    pickupId: dynamicPickupId ? Number(dynamicPickupId) : null,
    vehicleId: dynamicVehicleId ? Number(dynamicVehicleId) : null,
    additionalVehicles: additionalQuoteVehicles.map((vehicle) => ({
      vehicleId: Number(vehicle.id),
    })),
    acType: dynamicAcType,
    selectedAddonIds,
    selectedAddonOptions,
    selectedRoomRateIds: selectedRoomRateIds
      .filter(Boolean)
      .map((id) => Number(id)),
    selectedRoomRateNightSelections,
    selectedRoomCategoryIds: roomCategorySelections.map(
      (selection) => selection.roomCategoryId,
    ),
    selectedRoomUnits: roomCategorySelections.map((selection) => selection.units),
    selectedRoomCategorySelections: roomCategorySelections,
    selectedHotelFacilities,
    noStayNights,
    travelDate: selectedTravelDateValue || null,
    tripExtensionRequired,
    extensionScope: extensionStayOnlyForPickup ? "STAY" : "BOTH",
    extraPreNights: extraPreNightCount,
    extraPostNights: extraPostNightCount,
    extensionStays: tripExtensionRequired
      ? extensionStaysRequestPayload(
          extensionStays,
          extensionStayOnlyForPickup ? "STAY" : null,
        )
      : [],
    tripExtensionNote: tripExtensionNote.trim() || null,
    ...overrides,
  });

  const isNoStayNight = (night) => noStayNights.includes(Number(night));

  // Package nights that carry a stay - "same stay all nights" only means something with two or more.
  const stayNightGroups = roomRateOptionsByNight.filter((group) => !isNoStayNight(group.night));

  // Prices one stay kept for every night, for each stay offered on all of them. Fetched on request
  // (one quote per stay), and dropped as soon as the quote changes so a stale price never shows.
  const loadSameStayPackages = async () => {
    const rows = customerHotelOptionRows();
    const nights = Array.from(new Set(rows.map((row) => Number(row.night)))).sort((a, b) => a - b);
    const cities = new Set(rows.map((row) => String(row.destination || "").trim().toLowerCase()).filter(Boolean));
    if (nights.length < 2 || cities.size > 1) {
      setSameStayState({ loading: false, packages: [], note: "Staying in one place all nights is only possible when every night is in the same city." });
      return;
    }
    setSameStayState({ loading: true, packages: null, note: "" });
    try {
      const packages = (await customerHotelSameCityPackageRows(rows, nights, 8))
        .filter((item) => !item.priceError && item.perPersonPrice != null)
        .sort((left, right) => Number(left.perPersonPrice) - Number(right.perPersonPrice));
      setSameStayState({
        loading: false,
        packages,
        note: packages.length ? "" : "No stay is offered on every night of this trip.",
      });
    } catch (error) {
      console.error("Failed to price same-stay packages", error);
      setSameStayState({ loading: false, packages: [], note: "Could not work out these prices. Please try again." });
    }
  };

  // Room types a stay offers, for the comparison's picker - how many each sleeps and how many are
  // free on the date. Room type names only; nothing that identifies the hotel.
  const compareRoomTypes = (rateId) => {
    const rate = roomRateById.get(String(rateId));
    return categoryOptionsForRate(rate).map((category) => ({
      id: String(category.id),
      name: category.categoryName || "Room",
      sleeps: maxPeoplePerRoomForCategory(category),
      left: maxUnitsForCategory(category, rateId),
      soldOut: isCategorySoldOut(category, rateId),
    }));
  };

  /**
   * Prices one night's stay with a chosen room type, everything else as currently selected. The
   * group gets as many rooms of that type as it needs, capped at what is free on the date.
   */
  const priceCompareRoomType = async (night, rateId, categoryId) => {
    const rate = roomRateById.get(String(rateId));
    const category = categoryOptionsForRate(rate).find((item) => String(item.id) === String(categoryId));
    if (!category) throw new Error("Room type not found");
    const persons = Math.max(1, Number(quote?.roomCountPersons || selectedTravellerCount || 1));
    const units = Math.max(1, Math.min(
      maxUnitsForCategory(category, rateId),
      Math.ceil(persons / Math.max(1, maxPeoplePerRoomForCategory(category))),
    ));
    const nightRateIds = selectedRoomRateIdsForNight(night).map(String);
    const selections = [
      ...roomCategorySelections.filter((selection) => !nightRateIds.includes(String(selection.roomRateId))),
      { roomRateId: Number(rateId), roomCategoryId: Number(categoryId), units },
    ];
    const calculateQuote = adminCalculatorMode ? calculateAdminTripQuote : calculateTripQuote;
    const result = await calculateQuote(trip.id, quoteRequestPayload({
      selectedRoomRateIds: [
        ...selectedRoomRateIds.filter((id) => !nightRateIds.includes(String(id))).map(Number),
        Number(rateId),
      ],
      selectedRoomRateNightSelections: [
        ...selectedRoomRateNightSelections.filter((selection) => Number(selection.nightNumber) !== Number(night)),
        { roomRateId: Number(rateId), nightNumber: Number(night) },
      ],
      selectedRoomCategoryIds: selections.map((selection) => selection.roomCategoryId),
      selectedRoomUnits: selections.map((selection) => selection.units),
      selectedRoomCategorySelections: selections,
      // Paid extras are per hotel, so this night's do not carry over; other nights' stay as they are.
      selectedHotelFacilities: selectedHotelFacilities.filter(
        (facility) => facility.extensionStayKey || !nightRateIds.includes(String(facility.roomRateId)),
      ),
    }));
    const perPersonDifference = quotedPerPersonPrice(result) - quotedPerPersonPrice(quote);
    const currentRateId = nightRateIds[0];
    const currentSummary = currentRateId ? stayFacilitySummary(roomRateById.get(String(currentRateId))) : null;
    const sleeps = maxPeoplePerRoomForCategory(category);
    return {
      units,
      perPersonDifference,
      totalDifference: quotedTotalPrice(result) - quotedTotalPrice(quote),
      roomsText: `${units} x ${category.categoryName}${sleeps > 1 ? ` (sleeps ${sleeps}${units > 1 ? " each" : ""})` : ""}`,
      mealNote: mealComparisonNote(
        stayFacilitySummary(rate),
        { perPersonDifference, selected: false },
        currentSummary,
      ),
    };
  };

  // Applies a comparison choice: the stay, and - when a room type was picked there - exactly that
  // room type. Every room type of the stay is marked as set by hand, so the automatic room
  // suggestion does not add rooms of another type on top.
  const applyCompareChoice = (night, currentValue, nextValue, roomChoice) => {
    if (!currentValue) {
      setSelectedRoomRateIdsByNight((prev) => ({ ...prev, [String(night)]: [String(nextValue)] }));
    } else if (String(currentValue) !== String(nextValue)) {
      updateSelectedRoomRate(night, currentValue, nextValue);
    }
    if (!roomChoice) return;
    const categories = categoryOptionsForRate(roomRateById.get(String(nextValue)));
    setRoomsManuallyAdjusted(true);
    setManuallyAdjustedRoomUnitKeys((prev) => {
      const next = { ...prev };
      categories.forEach((category) => {
        next[categoryUnitKey(nextValue, category.id)] = true;
      });
      return next;
    });
    setSelectedRoomCategoryUnits((prev) => {
      const next = { ...prev };
      categories.forEach((category) => {
        next[categoryUnitKey(nextValue, category.id)] =
          String(category.id) === String(roomChoice.categoryId) ? roomChoice.units : 0;
      });
      return next;
    });
  };

  const isCurrentSameStay = (stays) =>
    stays.every((stay) => {
      const selected = selectedRoomRateIdsForNight(stay.night);
      return selected.length === 1 && String(selected[0]) === String(stay.roomRateId);
    });

  // Same as picking the stay in each night's list: swap the stay, clear its room counts, and let
  // the page suggest rooms for the new one.
  const chooseSameStayForAllNights = (stays) => {
    const previousIds = stays.flatMap((stay) => selectedRoomRateIdsForNight(stay.night));
    previousIds.forEach((rateId) => clearManualRoomUnitKeysForRate(rateId));
    setSelectedRoomRateIdsByNight((prev) => {
      const next = { ...prev };
      stays.forEach((stay) => {
        next[String(stay.night)] = [String(stay.roomRateId)];
      });
      return next;
    });
    setSelectedRoomCategoryUnits((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (previousIds.some((rateId) => key.startsWith(`${rateId}:`))) delete next[key];
      });
      return next;
    });
  };

  // Read facilities the same way the rest of the page does: the hotel's breakfast/dinner/campfire
  // booleans are legacy and set on almost no hotel; what is configured lives in the facility list.
  const stayFacilitySummary = (rate) => {
    const available = hotelFacilityItems(hotelForRate(rate), rate).filter((facility) => facility.available === true);
    const included = available.filter((facility) => facilityCostBehavior(facility) !== "OPTIONAL_PAID");
    const extras = available.filter((facility) => facilityCostBehavior(facility) === "OPTIONAL_PAID");
    return {
      mealPlan: mealPlanFromFacilities(
        included.map((facility) => facility.name),
        extras.map((facility) => facility.name),
      ),
      chips: included.map((facility) => facility.name).filter((name) => !isMealFacilityName(name)),
      extras: extras.map((facility) => `${facility.name} +Rs. ${formatAmount(facility.extraCost || 0)}`),
      paidMeals: extras.filter((facility) => isMealFacilityName(facility.name)),
      // Set in the hotel admin; describe the kind of place, never identify it.
      stayType: hotelForRate(rate)?.stayType || "",
      stayTypeDescription: hotelForRate(rate)?.stayTypeDescription || "",
      iotripsPick: hotelForRate(rate)?.iotripsPick === true,
    };
  };

  // What the paid meals add per person for one night. A per-person price is taken as is; a flat
  // per-night price is shared across the group, the way it would land on the bill.
  const paidMealsPerPerson = (paidMeals) => {
    const persons = Math.max(1, Number(quote?.chargeablePersons || selectedTravellerCount || 1));
    return paidMeals.reduce((total, facility) => {
      const cost = Number(facility.extraCost || 0);
      return total + (facility.pricingType === "PER_PERSON" ? cost : cost / persons);
    }, 0);
  };

  /**
   * A no-meals stay looks cheaper than one with meals until the meals are added back. When the
   * current choice includes meals, restate this option's difference with its paid meals added;
   * otherwise just say what the meals cost, so the comparison is never apples to oranges.
   */
  const mealComparisonNote = (summary, price, currentSummary) => {
    if (summary.mealPlan.tone !== "nomeals" || !summary.paidMeals.length) return "";
    const mealCost = paidMealsPerPerson(summary.paidMeals);
    if (mealCost <= 0) return "";
    const meals = MEALS.filter(([key]) => summary.paidMeals.some((facility) => String(facility.name).toLowerCase().includes(key)))
      .map(([, label]) => label.toLowerCase())
      .join(" + ");
    const difference = Number(price?.perPersonDifference);
    const currentHasMeals = currentSummary?.mealPlan?.tone === "meals";
    if (price && !price.selected && currentHasMeals && Number.isFinite(difference)) {
      const adjusted = Math.round(difference + mealCost);
      if (Math.abs(adjusted) < 50) return `With ${meals} added: about the same price`;
      return adjusted < 0
        ? `With ${meals} added: Rs. ${formatAmount(Math.abs(adjusted))} less pp`
        : `With ${meals} added: Rs. ${formatAmount(adjusted)} more pp`;
    }
    return `Adding ${meals}: about +Rs. ${formatAmount(Math.round(mealCost))} pp`;
  };

  // "1 x 2BHK (sleeps 4)" - the room count alone does not say whether the group fits comfortably.
  const optionRoomsText = (rate, price) => {
    const lines = price?.roomLines || [];
    if (!lines.length) return price?.roomSummary || "";
    const categories = categoryOptionsForRate(rate);
    return lines
      .map((line) => {
        const category = categories.find((item) => String(item.id) === String(line.roomCategoryId));
        const sleeps = category ? maxPeoplePerRoomForCategory(category) : 0;
        const rooms = Number(line.rooms || 0);
        const sleepsText = sleeps > 1 ? ` (sleeps ${sleeps}${rooms > 1 ? " each" : ""})` : "";
        return `${rooms} x ${line.categoryName}${sleepsText}`;
      })
      .join(" + ");
  };

  // Free rooms left at this stay on the chosen date, only once availability for it has loaded.
  const optionRoomsLeft = (rate) => {
    if (!rate?.id || !selectedTravelDateValue) return null;
    const categories = categoryOptionsForRate(rate);
    const known = categories.filter((category) => roomAvailabilityByKey[`${rate.id}:${category.id}`]);
    if (!known.length) return null;
    return known.reduce((total, category) => total + maxUnitsForCategory(category, rate.id), 0);
  };

  // Photos go out on request only - never on the page - so the hotel cannot be identified before
  // the advance. This hands the ask to the team with everything they need to answer it.
  const photoRequestLink = (night, rate) => {
    const lines = [
      `Hi Indian Odyssey, please share room photos for ${trip.title || "this trip"}.`,
      `Stay: ${roomRateHotelCodeLabel(rate, "Hotel option")} - Night ${night}`,
      selectedDate ? `Travel date: ${formatDisplayDate(selectedDate)}` : "",
      selectedTravellerCount ? `Travellers: ${selectedTravellerCount}` : "",
    ].filter(Boolean);
    return `https://wa.me/${RESERVATION_PHONE_DIGITS}?text=${encodeURIComponent(lines.join("\n"))}`;
  };
  // Employees reach this page from the lead portal or the admin calculator; they send photos from
  // the Hotel Images page themselves, so the customer-facing ask is not shown to them.
  const showPhotoRequest = !leadQuotePrefill && !adminCalculatorMode && !teamMediaAccess;

  // Every room type of the chosen stay with its photos - the ones the group is booked into first, so
  // a group split across room types (1 x 3-bed + 1 x 10-bed) can send photos of each. Matched by
  // display name and city, the way the Hotel Images page lists them.
  const teamMediaFor = (rate) => {
    if (!rate) return null;
    const code = roomRateHotelCodeLabel(rate, "");
    const city = String(roomRateLocationLabel(rate) || "").trim().toLowerCase();
    const hotelRow = teamHotelMedia.rows.find((row) =>
      String(row.customerCode || "") === String(code)
      && (!row.city || String(row.city).trim().toLowerCase() === city));
    const mediaById = new Map((hotelRow?.roomCategories || []).map((item) => [String(item.categoryId), item]));
    const categories = categoryOptionsForRate(rate).map((category) => ({
      categoryId: String(category.id),
      categoryName: category.categoryName || "Room",
      images: mediaById.get(String(category.id))?.images || [],
      inUse: Number(selectedRoomCategoryUnits[categoryUnitKey(rate.id, category.id)] || 0) > 0,
    }));
    if (!categories.length) return null;
    categories.sort((left, right) => Number(right.inUse) - Number(left.inUse));
    return {
      hotelRow: hotelRow || { customerCode: code, city: roomRateLocationLabel(rate), roomCategories: [] },
      categories,
    };
  };

  const defaultHotelOptionUnits = (category, rateId) => {
    const selectedUnits = Number(selectedRoomCategoryUnits[categoryUnitKey(rateId, category?.id)] || 0);
    if (selectedUnits > 0) return clampRoomUnits(selectedUnits, category, rateId);
    const people = Number(quote?.roomCountPersons || selectedTravellerCount || adults || 1);
    const capacity = Math.max(1, maxPeoplePerRoomForCategory(category));
    return clampRoomUnits(Math.ceil(people / capacity), category, rateId) || 1;
  };

  const customerHotelOptionRows = () => {
    const rows = [];
    const seenHotels = new Set();
    roomRateOptionsByNight.forEach((group) => {
      if (isNoStayNight(group.night)) return;
      const stayDate = formatDisplayDate(
        addLocalDays(
          selectedDate,
          Math.max(1, Number(group.night) || 1) - 1,
        ),
      );
      group.rates.forEach((rate) => {
        const hotelKey = `${group.night}:${rate.hotelId || rate.id}`;
        if (seenHotels.has(hotelKey)) return;
        const hotel = hotelForRate(rate);
        const quoteStay = [
          ...(quote?.roomStayCosts || []),
          ...(quote?.extensionStayCosts || []),
        ].find((stay) => String(stay.roomRateId) === String(rate.id));
        const facilities = hotelFacilityItems(hotel, rate, quoteStay);
        const includedFacilities = facilities
          .filter((facility) => facility?.name && facility.available === true && facilityCostBehavior(facility) !== "OPTIONAL_PAID")
          .map((facility) => hotelFacilityDisplayLabel(facility, rate.nightNumber || group.night));
        const optionalFacilities = facilities
          .filter((facility) => facility?.name && facility.available === true && facilityCostBehavior(facility) === "OPTIONAL_PAID")
          .map((facility) => hotelFacilityPriceLabel(facility));
        const availableCategories = categoryOptionsForRate(rate)
          .filter((category) => maxUnitsForCategory(category, rate.id) > 0)
          .sort((a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0));
        if (!availableCategories.length) return;
        const selectedCategory = availableCategories.find((category) =>
          Number(selectedRoomCategoryUnits[categoryUnitKey(rate.id, category.id)] || 0) > 0,
        );
        const category = selectedCategory || availableCategories[0];
        const units = defaultHotelOptionUnits(category, rate.id);
        seenHotels.add(hotelKey);
        rows.push({
          night: group.night,
          stayDate,
          destination: roomRateLocationLabel(rate),
          hotel: roomRateHotelCodeLabel(rate, "Hotel Option"),
          roomRateId: Number(rate.id),
          roomCategoryId: Number(category.id),
          room: category.categoryName || rate.roomCategoryName || "Room",
          capacity: maxPeoplePerRoomForCategory(category),
          units,
          facilities: includedFacilities,
          optionalFacilities,
        });
      });
    });
    return rows;
  };

  const pricedCustomerHotelOptionRows = async () => {
    const rows = customerHotelOptionRows();
    const nights = Array.from(new Set(rows.map((row) => Number(row.night)))).sort((a, b) => a - b);
    if (nights.length > 1) {
      const destinations = new Set(rows.map((row) => String(row.destination || "").trim().toLowerCase()).filter(Boolean));
      if (destinations.size <= 1) {
        const packageRows = await customerHotelSameCityPackageRows(rows, nights);
        if (packageRows.length) return packageRows;
      }
      return customerHotelCityWiseOptionRows(rows, nights);
    }
    const pricedRows = await Promise.all(
      rows.map(async (row) => {
        try {
          const otherSelections = roomCategorySelections.filter(
            (selection) => {
              const rate = (quoteOptions?.roomRates || []).find(
                (item) => String(item.id) === String(selection.roomRateId),
              );
              return Number(rate?.nightNumber || 1) !== Number(row.night);
            },
          );
          const selectedRoomCategorySelections = [
            ...otherSelections,
            {
              roomRateId: row.roomRateId,
              roomCategoryId: row.roomCategoryId,
              units: row.units,
            },
          ];
          const otherRateIds = selectedRoomRateIds
            .filter((rateId) => {
              const rate = (quoteOptions?.roomRates || []).find(
                (item) => String(item.id) === String(rateId),
              );
              return Number(rate?.nightNumber || 1) !== Number(row.night);
            })
            .map((rateId) => Number(rateId));
          const optionQuote = await calculateTripQuote(trip.id, quoteRequestPayload({
            selectedRoomRateIds: [...otherRateIds, row.roomRateId],
            selectedRoomRateNightSelections: [
              ...selectedRoomRateNightSelections.filter(
                (selection) => Number(selection.nightNumber) !== Number(row.night),
              ),
              { roomRateId: Number(row.roomRateId), nightNumber: Number(row.night) },
            ],
            selectedRoomCategoryIds: selectedRoomCategorySelections.map(
              (selection) => selection.roomCategoryId,
            ),
            selectedRoomUnits: selectedRoomCategorySelections.map(
              (selection) => selection.units,
            ),
            selectedRoomCategorySelections,
            selectedHotelFacilities: [],
          }));
          return {
            ...row,
            totalPrice: quotedTotalPrice(optionQuote),
            perPersonPrice: quotedPerPersonPrice(optionQuote),
            priceError: false,
          };
        } catch (error) {
          console.error("Failed to price hotel option", error);
          return { ...row, priceError: true };
        }
      }),
    );
    return pricedRows;
  };

  const customerHotelSameCityPackageRows = async (rows, nights, maxPackageOptions = 5) => {
    const rowsByHotel = rows.reduce((map, row) => {
      const key = String(row.hotel || "").trim().toLowerCase();
      if (!key) return map;
      const next = map.get(key) || [];
      next.push(row);
      map.set(key, next);
      return map;
    }, new Map());
    const selectedScore = (packageRows) => packageRows.reduce((score, row) => {
      const selectedIds = selectedRoomRateIdsForNight(row.night);
      return score + (selectedIds.includes(String(row.roomRateId)) ? 0 : 1);
    }, 0);
    const completePackages = Array.from(rowsByHotel.values())
      .map((hotelRows) => nights.map((night) => {
        const nightRows = hotelRows.filter((row) => Number(row.night) === Number(night));
        const selectedIds = selectedRoomRateIdsForNight(night);
        return nightRows.find((row) => selectedIds.includes(String(row.roomRateId))) || nightRows[0] || null;
      }))
      .filter((packageRows) => packageRows.every(Boolean))
      .sort((a, b) => selectedScore(a) - selectedScore(b))
      .slice(0, maxPackageOptions);

    return Promise.all(completePackages.map(async (packageRows) => {
      const selectedRoomCategorySelections = packageRows.map((row) => ({
        roomRateId: row.roomRateId,
        roomCategoryId: row.roomCategoryId,
        units: row.units,
      }));
      try {
        const optionQuote = await calculateTripQuote(trip.id, quoteRequestPayload({
          selectedRoomRateIds: packageRows.map((row) => row.roomRateId),
          selectedRoomRateNightSelections: packageRows.map((row) => ({
            roomRateId: Number(row.roomRateId),
            nightNumber: Number(row.night),
          })),
          selectedRoomCategoryIds: selectedRoomCategorySelections.map((selection) => selection.roomCategoryId),
          selectedRoomUnits: selectedRoomCategorySelections.map((selection) => selection.units),
          selectedRoomCategorySelections,
          selectedHotelFacilities: [],
        }));
        return {
          packageOption: true,
          stays: packageRows,
          totalPrice: quotedTotalPrice(optionQuote),
          perPersonPrice: quotedPerPersonPrice(optionQuote),
          priceError: false,
        };
      } catch (error) {
        console.error("Failed to price hotel package option", error);
        return {
          packageOption: true,
          stays: packageRows,
          priceError: true,
        };
      }
    }));
  };

  const customerHotelCityWiseOptionRows = (rows, nights) => {
    const maxOptionsPerNight = nights.length >= 3 ? 3 : 5;
    const groups = nights.map((night) => ({
      night,
      rows: rows
        .filter((row) => Number(row.night) === Number(night))
        .sort((a, b) => {
          const selectedIds = selectedRoomRateIdsForNight(night);
          const aSelected = selectedIds.includes(String(a.roomRateId)) ? 0 : 1;
          const bSelected = selectedIds.includes(String(b.roomRateId)) ? 0 : 1;
          return aSelected - bSelected;
        })
        .slice(0, maxOptionsPerNight),
    }));
    return groups
      .filter((group) => group.rows.length)
      .map((group) => ({
        cityWiseOption: true,
        night: group.night,
        stayDate: group.rows[0]?.stayDate || "",
        destination: group.rows[0]?.destination || "",
        options: group.rows,
        limitedOptions: maxOptionsPerNight < 5,
      }));
  };

  const drawWrappedCanvasText = (ctx, text, x, y, maxWidth, lineHeight) => {
    const words = String(text || "").split(/\s+/).filter(Boolean);
    const lines = [];
    let line = "";
    words.forEach((word) => {
      const nextLine = line ? `${line} ${word}` : word;
      if (ctx.measureText(nextLine).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = nextLine;
      }
    });
    if (line) lines.push(line);
    lines.forEach((item, index) => ctx.fillText(item, x, y + index * lineHeight));
    return Math.max(1, lines.length) * lineHeight;
  };

  const roundedCanvasRect = (ctx, x, y, width, height, radius) => {
    const r = Math.min(radius, width / 2, height / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + width, y, x + width, y + height, r);
    ctx.arcTo(x + width, y + height, x, y + height, r);
    ctx.arcTo(x, y + height, x, y, r);
    ctx.arcTo(x, y, x + width, y, r);
    ctx.closePath();
  };

  const loadCanvasImage = (src) =>
    new Promise((resolve) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => resolve(null);
      image.src = src;
    });

  const hotelOptionsImageBlob = async () => {
    const rows = await pricedCustomerHotelOptionRows();
    if (!rows.length) {
      throw new Error("No available hotel options found for the selected date.");
    }
    const width = 1080;
    const margin = 42;
    const cardGap = 18;
    const ctxMeasure = document.createElement("canvas").getContext("2d");
    ctxMeasure.font = "26px Arial";
    const measureWrappedHeight = (text, maxWidth, lineHeight, font) => {
      ctxMeasure.font = font;
      const words = String(text || "").split(/\s+/).filter(Boolean);
      let lines = 1;
      let line = "";
      words.forEach((word) => {
        const nextLine = line ? `${line} ${word}` : word;
        if (ctxMeasure.measureText(nextLine).width > maxWidth && line) {
          lines += 1;
          line = word;
        } else {
          line = nextLine;
        }
      });
      return lines * lineHeight;
    };
    const rowHeights = rows.map((row) => {
      if (row.cityWiseOption) {
        return 150 + row.options.reduce((total, option) => {
          const facilityText = option.facilities.length ? option.facilities.join("  |  ") : "Facilities not listed";
          const optionalText = option.optionalFacilities.length ? option.optionalFacilities.join(", ") : "None";
          return total
            + 154
            + measureWrappedHeight(`${option.hotel} - ${option.room}`, width - margin * 2 - 96, 32, "700 27px Arial")
            + measureWrappedHeight(facilityText, width - margin * 2 - 220, 30, "25px Arial")
            + measureWrappedHeight(optionalText, width - margin * 2 - 220, 30, "25px Arial");
        }, 0);
      }
      if (row.packageOption) {
        return 180 + row.stays.reduce((total, stay) => {
          const facilityText = stay.facilities.length ? stay.facilities.join("  |  ") : "Facilities not listed";
          const optionalText = stay.optionalFacilities.length ? stay.optionalFacilities.join(", ") : "None";
          return total
            + 142
            + measureWrappedHeight(`${stay.hotel} - ${stay.room}`, width - margin * 2 - 96, 28, "23px Arial")
            + measureWrappedHeight(facilityText, width - margin * 2 - 200, 26, "21px Arial")
            + measureWrappedHeight(optionalText, width - margin * 2 - 200, 26, "21px Arial");
        }, 0);
      }
      const facilityText = row.facilities.length ? row.facilities.join("  |  ") : "Facilities not listed";
      const optionalText = row.optionalFacilities.length ? row.optionalFacilities.join(", ") : "None";
      return 275
        + measureWrappedHeight(facilityText, width - margin * 2 - 200, 30, "24px Arial")
        + measureWrappedHeight(optionalText, width - margin * 2 - 200, 30, "24px Arial");
    });
    const cityWiseMode = rows.some((row) => row.cityWiseOption);
    const footerHeight = cityWiseMode ? 118 : 70;
    const height = 230 + rowHeights.reduce((total, item) => total + item + cardGap, 0) + footerHeight;
    const canvas = document.createElement("canvas");
    const scale = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    canvas.width = width * scale;
    canvas.height = height * scale;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(scale, scale);
    const brandIcon = await loadCanvasImage("/img/icon.png");
    ctx.fillStyle = "#f8fafc";
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = "#0f172a";
    ctx.fillRect(0, 0, width, 148);
    if (brandIcon) {
      ctx.drawImage(brandIcon, margin - 4, 24, 82, 82);
    } else {
      ctx.fillStyle = "#facc15";
      ctx.beginPath();
      ctx.arc(margin + 24, 56, 24, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#0f172a";
      ctx.font = "700 20px Arial";
      ctx.fillText("IO", margin + 13, 63);
    }
    ctx.fillStyle = "#ffffff";
    ctx.font = "700 38px Arial";
    ctx.fillText(cityWiseMode ? "IO Trips Stay Options" : rows.some((row) => row.packageOption) ? "IO Trips Stay Package Options" : "IO Trips Hotel Options", margin + 96, 58);
    ctx.font = "24px Arial";
    const subtitle = [
      selectedDate ? `Stay Date: ${formatDisplayDate(selectedDate)}` : "",
      trip?.title || "",
    ].filter(Boolean).join(" | ");
    ctx.fillStyle = "#cbd5e1";
    ctx.fillText(subtitle || "Available for selected date", margin + 96, 96);

    let y = 184;
    rows.forEach((row, index) => {
      const cardHeight = rowHeights[index];
      roundedCanvasRect(ctx, margin, y, width - margin * 2, cardHeight, 16);
      ctx.fillStyle = "#ffffff";
      ctx.fill();
      ctx.strokeStyle = "#dbe4f0";
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.fillStyle = "#2563eb";
      ctx.font = "700 22px Arial";
      ctx.fillText(row.cityWiseOption ? `Night ${row.night}` : `Option ${index + 1}`, margin + 28, y + 38);
      if (row.cityWiseOption) {
        ctx.fillStyle = "#0f172a";
        ctx.font = "700 30px Arial";
        ctx.fillText(row.destination || "Stay Location", margin + 28, y + 76);
        ctx.fillStyle = "#475569";
        ctx.font = "24px Arial";
        ctx.fillText(`Stay Date: ${row.stayDate}`, margin + 28, y + 112);
        let optionY = y + 158;
        row.options.forEach((option, optionIndex) => {
          ctx.fillStyle = "#eff6ff";
          roundedCanvasRect(ctx, margin + 24, optionY - 22, width - margin * 2 - 48, 38, 10);
          ctx.fill();
          ctx.fillStyle = "#1d4ed8";
          ctx.font = "700 25px Arial";
          ctx.fillText(`Stay Option ${optionIndex + 1}`, margin + 42, optionY + 2);
          optionY += 42;
          ctx.fillStyle = "#111827";
          ctx.font = "700 27px Arial";
          optionY += drawWrappedCanvasText(
            ctx,
            `${option.hotel} - ${option.room}`,
            margin + 42,
            optionY,
            width - margin * 2 - 84,
            32,
          );
          ctx.fillStyle = "#475569";
          ctx.font = "25px Arial";
          ctx.fillText(`Suitable for: Up to ${option.capacity} adults`, margin + 42, optionY + 24);
          optionY += 54;
          const optionLabelX = margin + 42;
          const optionValueX = margin + 170;
          const optionTextWidth = width - margin * 2 - 220;
          ctx.fillStyle = "#166534";
          ctx.font = "25px Arial";
          ctx.fillStyle = "#64748b";
          ctx.font = "700 25px Arial";
          ctx.fillText("Included facilities:", optionLabelX, optionY);
          ctx.fillStyle = "#166534";
          ctx.font = "25px Arial";
          optionY += drawWrappedCanvasText(ctx, option.facilities.length ? option.facilities.join("  |  ") : "None", optionValueX, optionY, optionTextWidth, 30);
          ctx.fillStyle = option.optionalFacilities.length ? "#9f1239" : "#64748b";
          ctx.font = "25px Arial";
          optionY += 10;
          ctx.fillStyle = "#64748b";
          ctx.font = "700 25px Arial";
          ctx.fillText("Optional:", optionLabelX, optionY);
          ctx.fillStyle = option.optionalFacilities.length ? "#9f1239" : "#64748b";
          ctx.font = "25px Arial";
          optionY += drawWrappedCanvasText(ctx, option.optionalFacilities.length ? option.optionalFacilities.join(", ") : "None", optionValueX, optionY, optionTextWidth, 30) + 18;
        });
        if (row.limitedOptions) {
          ctx.fillStyle = "#64748b";
          ctx.font = "23px Arial";
          ctx.fillText("More stay options are available on the trip page.", margin + 42, y + cardHeight - 28);
        }
        y += cardHeight + cardGap;
        return;
      }
      if (row.packageOption) {
        ctx.fillStyle = "#0f172a";
        ctx.font = "700 30px Arial";
        ctx.fillText("Stay Package", margin + 28, y + 76);
        ctx.fillStyle = "#475569";
        ctx.font = "24px Arial";
        ctx.fillText(`${row.stays.length} night stay combination`, margin + 28, y + 112);
        ctx.fillStyle = "#0f172a";
        ctx.font = "700 25px Arial";
        if (row.priceError) {
          ctx.fillText("Price: On request", margin + 28, y + 152);
        } else {
          ctx.fillText(`Total: Rs. ${formatAmount(row.totalPrice)}`, margin + 28, y + 152);
          ctx.fillText(`Per person: Rs. ${formatAmount(row.perPersonPrice)}`, margin + 370, y + 152);
        }
        let stayY = y + 198;
        row.stays.forEach((stay) => {
          ctx.fillStyle = "#eff6ff";
          roundedCanvasRect(ctx, margin + 24, stayY - 22, width - margin * 2 - 48, 38, 10);
          ctx.fill();
          ctx.fillStyle = "#1d4ed8";
          ctx.font = "700 21px Arial";
          ctx.fillText(`Night ${stay.night} - ${stay.stayDate} | ${stay.destination}`, margin + 42, stayY + 2);
          stayY += 38;
          ctx.fillStyle = "#111827";
          ctx.font = "700 23px Arial";
          stayY += drawWrappedCanvasText(
            ctx,
            `${stay.hotel} - ${stay.room}`,
            margin + 42,
            stayY,
            width - margin * 2 - 84,
            28,
          );
          ctx.fillStyle = "#475569";
          ctx.font = "21px Arial";
          ctx.fillText(`Suitable for: Up to ${stay.capacity} adults`, margin + 42, stayY + 24);
          stayY += 50;
          const stayLabelX = margin + 42;
          const stayValueX = margin + 150;
          const stayTextWidth = width - margin * 2 - 192;
          ctx.fillStyle = "#166534";
          ctx.font = "21px Arial";
          ctx.fillStyle = "#64748b";
          ctx.font = "700 21px Arial";
          ctx.fillText("Included facilities:", stayLabelX, stayY);
          ctx.fillStyle = "#166534";
          ctx.font = "21px Arial";
          stayY += drawWrappedCanvasText(ctx, stay.facilities.length ? stay.facilities.join("  |  ") : "None", stayValueX, stayY, stayTextWidth, 26);
          ctx.fillStyle = stay.optionalFacilities.length ? "#9f1239" : "#64748b";
          ctx.font = "21px Arial";
          stayY += 8;
          ctx.fillStyle = "#64748b";
          ctx.font = "700 21px Arial";
          ctx.fillText("Optional:", stayLabelX, stayY);
          ctx.fillStyle = stay.optionalFacilities.length ? "#9f1239" : "#64748b";
          ctx.font = "21px Arial";
          stayY += drawWrappedCanvasText(ctx, stay.optionalFacilities.length ? stay.optionalFacilities.join(", ") : "None", stayValueX, stayY, stayTextWidth, 26) + 16;
        });
        y += cardHeight + cardGap;
        return;
      }
      ctx.fillStyle = "#0f172a";
      ctx.font = "700 30px Arial";
      ctx.fillText(row.hotel, margin + 28, y + 76);
      ctx.fillStyle = "#475569";
      ctx.font = "24px Arial";
      ctx.fillText(`${row.destination} | ${row.stayDate}`, margin + 28, y + 112);
      ctx.fillStyle = "#111827";
      ctx.font = "700 24px Arial";
      const roomText = `Room: ${row.room}`;
      const roomMaxWidth = 360;
      drawWrappedCanvasText(ctx, roomText, margin + 28, y + 148, roomMaxWidth, 30);
      ctx.fillStyle = "#475569";
      ctx.font = "24px Arial";
      ctx.fillText(`Suitable for: Up to ${row.capacity} adults`, margin + 470, y + 148);

      ctx.fillStyle = "#0f172a";
      ctx.font = "700 25px Arial";
      if (row.priceError) {
        ctx.fillText("Price: On request", margin + 28, y + 190);
      } else {
        ctx.fillText(`Total: Rs. ${formatAmount(row.totalPrice)}`, margin + 28, y + 190);
        ctx.fillText(`Per person: Rs. ${formatAmount(row.perPersonPrice)}`, margin + 370, y + 190);
      }

      let lineY = y + 235;
      const labelX = margin + 28;
      const valueX = margin + 154;
      const valueWidth = width - margin * 2 - 182;
      ctx.fillStyle = "#64748b";
      ctx.font = "700 22px Arial";
      ctx.fillText("Included facilities:", labelX, lineY);
      ctx.fillStyle = "#166534";
      ctx.font = "24px Arial";
      const facilityTextHeight = drawWrappedCanvasText(
        ctx,
        row.facilities.length ? row.facilities.join("  |  ") : "None",
        valueX,
        lineY,
        valueWidth,
        30,
      );
      lineY += facilityTextHeight + 14;
      ctx.fillStyle = "#64748b";
      ctx.font = "700 22px Arial";
      ctx.fillText("Optional:", labelX, lineY);
      ctx.fillStyle = row.optionalFacilities.length ? "#9f1239" : "#64748b";
      ctx.font = "24px Arial";
      drawWrappedCanvasText(
        ctx,
        row.optionalFacilities.length ? row.optionalFacilities.join(", ") : "None",
        valueX,
        lineY,
        valueWidth,
        30,
      );
      y += cardHeight + cardGap;
    });

    ctx.fillStyle = "#475569";
    ctx.font = "22px Arial";
    if (cityWiseMode) {
      drawWrappedCanvasText(
        ctx,
        "Based on your hotel selection, our agent will share the package price. You can also explore and calculate pricing yourself on our trip page.",
        margin,
        height - 78,
        width - margin * 2,
        28,
      );
      ctx.fillText("Subject to availability at booking time.", margin, height - 24);
    } else {
      ctx.fillText("Subject to availability at booking time.", margin, height - 34);
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Could not create hotel options image."));
      }, "image/png");
    });
  };

  const downloadHotelOptionsImage = (blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `hotel-options-${selectedTravelDateValue || "trip"}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const copyHotelOptionsImage = async () => {
    try {
      const blob = await hotelOptionsImageBlob();
      if (navigator.clipboard?.write && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({ [blob.type]: blob }),
        ]);
        toast.success("Hotel options image copied. Paste it in WhatsApp.");
        return;
      }
      downloadHotelOptionsImage(blob);
      toast.info("Image copy is not supported in this browser. Image downloaded instead.");
    } catch (error) {
      if (error?.name === "NotAllowedError") {
        try {
          const blob = await hotelOptionsImageBlob();
          downloadHotelOptionsImage(blob);
          toast.info("Image copy was blocked by the browser. Image downloaded instead.");
        } catch {
          toast.error("Could not create hotel options image");
        }
        return;
      }
      toast.error(error?.message || "Could not copy hotel options image");
    }
  };

  const quotationPickupVideo = () => {
    const normalize = (value) => String(value || "").trim().toLowerCase();
    const quotePickup = quote?.selectedPickup;
    const matched =
      pickupOptions.find((option) =>
        [option.pickupLabel, option.pickupValue].some((optionValue) =>
          [quotePickup?.pickupName, quotePickup?.pickupValue].some(
            (sourceValue) =>
              sourceValue && normalize(optionValue) === normalize(sourceValue)
          )
        )
      ) || selectedPickup;
    return pickVideoForLanguage(matched?.videos);
  };

  const buildCalculatedPriceShareText = () => {
    if (!quote) return "";
    const tripUrl =
      trip.path && typeof window !== "undefined"
        ? `${window.location.origin}/trips/${trip.path}`
        : "";
    const tripDate = quotationDateText(quote);
    const travellerText = [
      `${quote.adultsCount ?? adults} adult${Number(quote.adultsCount ?? adults) === 1 ? "" : "s"}`,
      Number(quote.kidsCount ?? kids ?? 0) > 0
        ? `${quote.kidsCount ?? kids ?? 0} kid${Number(quote.kidsCount ?? kids ?? 0) === 1 ? "" : "s"}`
        : "",
    ]
      .filter(Boolean)
      .join(", ");
    const quotationVideo = quotationPickupVideo();
    const lines = [
      // Price first - it is what the customer looks for. These lines carry no
      // link, so the video below is still the first URL for WhatsApp's preview.
      `*Total Price* : *Rs. ${formatAmount(quotedTotalPrice(quote))}*`,
      `*Per Person* : *Rs. ${formatAmount(quotedPerPersonPrice(quote))}* (calculated for ${chargeablePersonText(quote)})`,
      `*Advance to Confirm* : ${quotationAdvanceText(quote)}`,
      "",
      // First URL in the message: WhatsApp builds its preview card from the
      // video rather than the trip page.
      ...(quotationVideo?.youtubeUrl
        ? [`*Video* : ${quotationVideo.youtubeUrl}`]
        : []),
      `*Quotation Details* : ${trip.title || "Trip"} - ${quotationDurationText(quote)}`,
      `*Date* : ${tripDate}`,
      `*Travellers* : ${travellerText}`,
      `*Pickup* : ${quote.selectedPickup?.pickupName || "-"}`,
      `*Vehicle* : ${quoteVehicleLabel || "-"}`,
      `*AC Type* : ${dynamicAcType === "AC" ? "AC" : "Non-AC"}`,
    ];
    const extensionText = customerTripExtensionText(quote);
    if (extensionText) {
      lines.push(`*Trip Extension* : ${extensionText}`);
    }
    const hotelRoomDetailLines = quoteHotelRoomQuotationLines(quote);
    if (hotelRoomDetailLines.length) {
      lines.push("*Hotel/Room Details* : ");
      hotelRoomDetailLines.forEach((line) => lines.push(`- ${line}`));
    }
    lines.push(
      `*Add-ons* : ${(quote.selectedAddons || []).map((addon) => addon.displayName || addon.name).join(", ") || "None"}`,
    );
    if (customizationRequired) {
      lines.push(
        `*Customization* : ${customizationDescription.trim() || "Required"}`,
      );
    }
    if (tripUrl) {
      lines.push("", "*Please visit for more details :*", tripUrl);
      lines.push(
        "*Highlights* : " + tripSectionUrl("highlights"),
        "*Overview* : " + tripSectionUrl("overview"),
        "*Itinerary* : " + tripSectionUrl("itinerary"),
      );
    }
    return lines.join("\n");
  };

  const copyCalculatedPrice = async () => {
    const text = buildCalculatedPriceShareText();
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      toast.success("Calculated price copied for WhatsApp");
    } catch (error) {
      toast.error("Could not copy calculated price");
    }
  };

  const buildQuotationPdfDocument = () => {
    if (!quote) return null;
    const tripDate = quotationDateText(quote);
    const document = {
      title: `${trip.title || "Trip"} Quotation`,
      subtitle: quotationDurationText(quote),
      brand: {
        name: "Indian Odyssey",
        website: "https://www.iotrips.in",
        instagram: "https://www.instagram.com/indianodyssey.in",
        googleReview: "https://share.google/SoSE0qDIehhvaL411",
        phone: "+91 91219 43442",
      },
      summary: [
        ["Trip", trip.title || "-"],
        ["Duration", quotationDurationText(quote)],
        ["Date", tripDate],
        ["Travellers", quoteTravellerSummary(quote) || "-"],
        ["Pickup", quote.selectedPickup?.pickupName || "-"],
        ...(quotationPickupVideo()?.youtubeUrl
          ? [["Video", quotationPickupVideo().youtubeUrl]]
          : []),
        ["Vehicle", quoteVehicleLabel || "-"],
        ["AC Type", dynamicAcType === "AC" ? "AC" : "Non-AC"],
        ...(customerTripExtensionText(quote)
          ? [["Trip Extension", customerTripExtensionText(quote)]]
          : []),
        [
          "Add-ons",
          (quote.selectedAddons || []).map((addon) => addon.displayName || addon.name).join(", ") ||
            "None",
        ],
        ...(customizationRequired
          ? [
              [
                "Customization",
                customizationDescription.trim() || "Required",
              ],
            ]
          : []),
        ["Total Price", `Rs. ${formatAmount(quotedTotalPrice(quote))}`],
        [
          "Per Person",
          `Rs. ${formatAmount(quotedPerPersonPrice(quote))} (${chargeablePersonText(quote)})`,
        ],
        ["Advance to Confirm", quotationAdvanceText(quote)],
      ].map(([label, value]) => ({ label, value })),
      sections: [],
    };
    const addSection = (title, blocks) => {
      const cleanBlocks = (blocks || []).filter((block) => {
        if (!block) return false;
        if (block.type === "bullets") return (block.items || []).length;
        if (block.type === "hotelRooms") return (block.items || []).length;
        return Boolean(block.text || block.title);
      });
      if (cleanBlocks.length) document.sections.push({ title, blocks: cleanBlocks });
    };
    const hotelRoomDetails = quoteHotelRoomPdfDetails(quote);
    addSection("Hotel/Room Details", [
      { type: "hotelRooms", items: hotelRoomDetails },
    ]);
    addSection("Highlights", highlights.length
      ? [
          {
            type: "bullets",
            items: highlights.map((item) =>
              [item.title, item.description].filter(Boolean).join(" - "),
            ),
          },
        ]
      : [{ type: "paragraph", text: "Not available" }]);
    addSection("Overview", [
      { type: "paragraph", text: displayTrip.overviewText || "Not available" },
      {
        type: "detail",
        title: "Departure & Return Location",
        text: [displayTrip.departureLocation, displayTrip.returnLocation]
          .filter(Boolean)
          .join("\n") || "Not available",
      },
      {
        type: "detail",
        title: "Departure Time",
        text: displayTrip.departureTime || "Not available",
      },
      {
        type: "bullets",
        title: "Price Includes",
        items: displayTrip.priceIncludes || [],
      },
      {
        type: "bullets",
        title: "Price Excludes",
        items: displayTrip.priceExcludes || [],
      },
    ]);
    addSection("Itinerary", (displayTrip.itinerary || []).length
      ? (displayTrip.itinerary || []).map((dayPlan) => ({
          type: "day",
          title: [dayPlan.day, dayPlan.title].filter(Boolean).join(" - "),
          text: dayPlan.description || "",
          items: [
            ...(dayPlan.points || []),
            ...(dayPlan.stayInfo ? [`Stay Type: ${dayPlan.stayInfo}`] : []),
          ],
        }))
      : [{ type: "paragraph", text: "Not available" }]);
    addSection("Terms & Conditions", termsConditions.length
      ? termsConditions.map((section, index) => ({
          type: "detail",
          title: `${index + 1}. ${section.title || "Terms"}`,
          items: section.points || [],
        }))
      : [{ type: "paragraph", text: "Not available" }]);
    addSection("FAQs", (trip.faqs || []).length
      ? (trip.faqs || []).map((faq, index) => ({
          type: "detail",
          title: `${index + 1}. ${faq.question || "Question"}`,
          text: faq.answer || "-",
        }))
      : [{ type: "paragraph", text: "Not available" }]);
    return document;
  };

  const quotationPdfFileName = () =>
    `${slugifyFileName(trip.title || "trip")}-quotation.pdf`;

  const saveQuotationPdfBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = quotationPdfFileName();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadQuotationPdf = async () => {
    const document = buildQuotationPdfDocument();
    if (!document) return;
    try {
      saveQuotationPdfBlob(await createQuotationPdfBlob(document));
      toast.success("Quotation PDF downloaded");
    } catch (error) {
      toast.error("Could not create quotation PDF");
    }
  };

  const currentQuoteTotal = () =>
    quoteOptions && quote
      ? Number(quote.roundedTotalPrice || quote.finalTripPrice || 0)
      : trip.price * numTravellers;

  const chooseAddonOption = (addonId, change) => {
    setAddonChoices((prev) => ({
      ...prev,
      [String(addonId)]: { ...(prev[String(addonId)] || {}), ...change },
    }));
  };

  const toggleAddon = (addonId, checked) => {
    setManuallyToggledAddonIds((prev) => ({ ...prev, [String(addonId)]: true }));
    setSelectedAddonIds((prev) =>
      checked
        ? Array.from(new Set([...prev, addonId]))
        : prev.filter((id) => id !== addonId),
    );
  };

  const handleWishlistClick = async () => {
    if (!user) {
      toast.warning("Please login to use wishlist");
      return;
    }
    setLoading(true);
    try {
      const result = await toggleWishlist(user.id, trip.path);
      if (result.includes("Added")) {
        setWishlist((prev) => [...prev, trip]);
        setIsWishlisted(true);
      } else {
        setWishlist((prev) => prev.filter((t) => t.path !== trip.path));
        setIsWishlisted(false);
      }
      toast.success(result);
    } catch (err) {
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestCoupon = async () => {
    try {
      const res = await suggestCoupon(currentQuoteTotal());
      if (res && res.length > 0) {
        setSuggestedCoupons(res);
        toast.info("Coupons loaded. Select one to apply.");
      } else {
        toast.warning("No applicable coupons found.");
      }
    } catch (err) {
      toast.error("Error fetching coupons.");
      console.error(err);
    }
  };

  const handleApplyCoupon = async () => {
    try {
      const res = await applyCoupon(couponCode, currentQuoteTotal());
      if (res && res.discountAmount) {
        setDiscountAmount(res.discountAmount);
        setFinalPrice(res.finalPrice);
        toast.success(`Coupon Applied! ₹${res.discountAmount} off`);
      }
    } catch (err) {
      toast.error("Invalid or inapplicable coupon.");
    }
  };

  // Group price options by duration (e.g., "2N 3D")
  const groupedPrices = React.useMemo(() => {
    const groups = {};
    (priceOptions || []).forEach((p) => {
      if (p.isActive === false) return;
      const key = `${p.durationNights}N ${p.durationDays}D`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });
    // sort vehicles within groups by people count then name for stable order
    Object.values(groups).forEach((arr) =>
      arr.sort((a, b) => {
        const countA =
          getPeopleCount(a.vehicleType) ?? Number.POSITIVE_INFINITY;
        const countB =
          getPeopleCount(b.vehicleType) ?? Number.POSITIVE_INFINITY;
        if (countA !== countB) return countA - countB;
        return (a.vehicleType || "").localeCompare(b.vehicleType || "");
      }),
    );
    return groups;
  }, [priceOptions]);

  return (
    <div className={adminCalculatorMode ? "trip-admin-calculator-preview" : ""}>
      {!adminCalculatorMode && (
        <>
          <NavBar categories={categories} />
          <CoverPage
            imageSrc={trip.coverUrl}
            altText={trip.title}
            showContent={false}
            title={trip.title}
            duration={trip.duration}
          />
          <FloatingContact />
        </>
      )}
      <style>
        {`
          .trip-admin-calculator-preview {
            background: #fff;
          }

          .trip-admin-calculator-preview .trip-detail-page {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
          }

          .trip-admin-calculator-preview .trip-tabs-section,
          .trip-admin-calculator-preview .trip-pickup-strip,
          .trip-admin-calculator-preview .trip-book-floating {
            display: none !important;
          }

          .trip-admin-calculator-preview #book-now {
            width: 100%;
            max-width: 560px;
            flex: 0 0 560px;
            margin: 0;
          }

          .trip-admin-calculator-preview .trip-booking-card {
            margin-bottom: 0 !important;
          }

          /* Breakup colours are themed: the light palette is the default, and the dark palette is
             applied both for an explicit dark choice and for the "auto" setting on a dark system. */
          .trip-admin-calculator-preview .trip-admin-breakup {
            --breakup-text: #0f172a;
            --breakup-muted: #475569;
            --breakup-border: rgba(15, 23, 42, 0.18);
            --breakup-total-border: rgba(15, 23, 42, 0.55);
            --breakup-subrow-bg: rgba(15, 23, 42, 0.05);
            color: var(--breakup-text);
          }

          @media (prefers-color-scheme: dark) {
            :root:not([data-theme="light"]) .trip-admin-calculator-preview .trip-admin-breakup {
              --breakup-text: #f8fafc;
              --breakup-muted: #cbd5e1;
              --breakup-border: rgba(255, 255, 255, 0.32);
              --breakup-total-border: rgba(255, 255, 255, 0.7);
              --breakup-subrow-bg: rgba(255, 255, 255, 0.08);
            }
          }

          :root[data-theme="dark"] .trip-admin-calculator-preview .trip-admin-breakup {
            --breakup-text: #f8fafc;
            --breakup-muted: #cbd5e1;
            --breakup-border: rgba(255, 255, 255, 0.32);
            --breakup-total-border: rgba(255, 255, 255, 0.7);
            --breakup-subrow-bg: rgba(255, 255, 255, 0.08);
          }

          .trip-admin-calculator-preview .trip-admin-breakup-row {
            border-color: var(--breakup-border) !important;
          }

          .trip-admin-calculator-preview .trip-admin-breakup-subrow {
            background: var(--breakup-subrow-bg) !important;
            color: var(--breakup-text);
          }

          .trip-admin-calculator-preview .trip-admin-breakup .text-muted,
          .trip-admin-calculator-preview .trip-admin-breakup-note {
            color: var(--breakup-muted) !important;
          }

          .trip-admin-calculator-preview .trip-admin-breakup-total {
            border-color: var(--breakup-total-border) !important;
          }

          .trip-book-floating {
            position: fixed;
            left: 20px;
            bottom: 20px;
            z-index: 1001;
            border-radius: 999px;
            padding: 12px 22px;
          }

          @media (max-width: 768px) {
            .trip-book-floating {
              left: 15px;
              bottom: 15px;
              padding: 10px 18px;
            }
          }
        `}
      </style>
      {!adminCalculatorMode && (
        <Button
          as="a"
          href="#book-now"
          variant="warning"
          className="trip-book-floating fw-bold shadow"
        >
          Get Trip Price
        </Button>
      )}
      <Container className="mt-4 trip-pickup-strip">
        <Row className="g-3 align-items-end">
          {pickupOptions.length > 0 && (
            <Col md={5} lg={4}>
              <Form.Group controlId="pickupLocationTop">
                <Form.Label className="fw-semibold">Pickup Location</Form.Label>
                <Form.Select
                  value={selectedPickupOptionId}
                  onChange={(e) => setSelectedPickupOptionId(e.target.value)}
                  required
                >
                  {pickupOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.pickupLabel}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          )}
          <Col className="d-flex justify-content-end">
            <Button
              variant={isWishlisted ? "danger" : "outline-danger"}
              className="fw-semibold"
              onClick={handleWishlistClick}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />
                  Please wait...
                </>
              ) : isWishlisted ? (
                "❤ In Wishlist"
              ) : (
                "♡ Add to Wishlist"
              )}
            </Button>
          </Col>
        </Row>
      </Container>
      <Container className="my-5 trip-detail-page">
        <Row>
          <Col
            id="book-now"
            lg={6}
            className="order-2 order-lg-2"
            style={{ scrollMarginTop: "90px" }}
          >
            <Card className="mb-4 shadow-sm trip-booking-card">
              <Card.Body>
                <p className="text-muted mb-3">{trip.availability}</p>
                {quoteOptions && (
                  <div className="mb-3">
                    <Form.Label className="fw-semibold">
                      Calculate Package Price
                    </Form.Label>
                    <Row className="g-2">
                      <Col xs={6}>
                        <Form.Label className="small text-muted">
                          Adults <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          placeholder="Select"
                          value={adults}
                          onChange={(e) =>
                            handleAdultKidChange(e.target.value, kids)
                          }
                        />
                      </Col>
                      <Col xs={6}>
                        <Form.Label className="small text-muted">
                          Kids
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          placeholder="Select"
                          value={kids}
                          onChange={(e) =>
                            handleAdultKidChange(adults, e.target.value)
                          }
                        />
                      </Col>
                      <Col xs={12} className="trip-travel-date-field">
                        <Form.Label className="small text-muted d-block mb-1">
                          Select Trip Start Date{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <DatePicker
                          selected={selectedDate}
                          onChange={(date) => {
                            setSelectedDate(date);
                            resetRoomManualAdjustments();
                          }}
                          minDate={new Date()}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="DD/MM/YYYY"
                          className="form-control"
                          disabled={selectedTravellerCount < 1}
                          required
                        />
                        <Form.Text className="text-muted">
                          Hotel availability is checked using each night's
                          displayed stay date.
                        </Form.Text>
                      </Col>
                      {Number(kids) > 0 && (
                        <Col xs={12}>
                          <Form.Label className="small text-muted">
                            Kid ages
                          </Form.Label>
                          <Row className="g-2">
                            {Array.from(
                              { length: Number(kids) || 0 },
                              (_, index) => (
                                <Col xs={12} key={index}>
                                  <InputGroup size="sm">
                                    <InputGroup.Text>
                                      Kid {index + 1}
                                    </InputGroup.Text>
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      step="1"
                                      value={kidAges[index] ?? ""}
                                      onChange={(e) =>
                                        updateKidAge(index, e.target.value)
                                      }
                                      isInvalid={kidAges[index] === ""}
                                      required
                                    />
                                    <InputGroup.Text>yrs</InputGroup.Text>
                                  </InputGroup>
                                  <Form.Check
                                    className="mt-1"
                                    type="switch"
                                    id={`kid-seat-needed-${index}`}
                                    label="Vehicle seat needed?"
                                    checked={
                                      effectiveKidVehicleSeatNeeded[index] !==
                                      false
                                    }
                                    onChange={(event) =>
                                      updateKidVehicleSeatNeeded(
                                        index,
                                        event.target.checked,
                                      )
                                    }
                                  />
                                </Col>
                              ),
                            )}
                          </Row>
                          {selectedQuoteVehicle && (
                            <Form.Text className="text-muted">
                              {selectedQuoteVehicle.vehicleName}: children aged{" "}
                              {selectedQuoteVehicle.childConcessionAgeLimit ??
                                0}{" "}
                              years or below default to no separate seat. Up to{" "}
                              {selectedQuoteVehicle.maxKidsWithoutSeat ?? 0}{" "}
                              child
                              {Number(
                                selectedQuoteVehicle.maxKidsWithoutSeat ?? 0,
                              ) === 1
                                ? ""
                                : "ren"}{" "}
                              can travel without a seat.
                            </Form.Text>
                          )}
                          {!kidAgesComplete && (
                            <Form.Text className="text-muted">
                              Enter each kid's age to calculate room pricing.
                            </Form.Text>
                          )}
                        </Col>
                      )}
                      {roomRateOptionsByNight.length > 0 &&
                        selectedTravelDateValue && (
                          <Col xs={12}>
                            <div className="trip-form-section">Where you'll stay</div>
                            <Form.Label className="small text-muted">
                              Hotel / Room Category
                            </Form.Label>
                            {showPhotoRequest && (
                              <div className="small text-muted mb-2">
                                <i className="bi bi-shield-lock me-1" aria-hidden="true" />
                                The hotel's name, photos and exact location are shared once you pay the advance.
                                Want to see the rooms first? Tap "Request room photos" on any stay.
                              </div>
                            )}
                            <Form.Check
                              type="switch"
                              id="trip-meals-included-only"
                              className="small mb-2"
                              label="Show only stays with meals included"
                              checked={mealsIncludedOnly}
                              onChange={(event) => setMealsIncludedOnly(event.target.checked)}
                            />
                            {stayNightGroups.length >= 2 && (
                              <div className="border rounded p-2 mb-2 bg-white">
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                  <div>
                                    <div className="small fw-semibold">Stay in one place all nights</div>
                                    <div className="small text-muted">
                                      No packing up between nights - see what keeping one stay for the whole trip costs.
                                    </div>
                                  </div>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={loadSameStayPackages}
                                    disabled={sameStayState.loading || !quote}
                                  >
                                    {sameStayState.loading ? (
                                      <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                                    ) : sameStayState.packages ? "Refresh prices" : "Show options"}
                                  </Button>
                                </div>
                                {sameStayState.note && <div className="small text-muted mt-2">{sameStayState.note}</div>}
                                {(sameStayState.packages || []).length > 0 && (
                                  <div className="d-flex flex-column gap-2 mt-2">
                                    {sameStayState.packages.map((stayPackage) => {
                                      const firstStay = stayPackage.stays[0];
                                      const summary = stayFacilitySummary(roomRateById.get(String(firstStay.roomRateId)));
                                      const current = isCurrentSameStay(stayPackage.stays);
                                      const difference = Math.round(Number(stayPackage.perPersonPrice) - quotedPerPersonPrice(quote));
                                      const differenceText = current
                                        ? "Your current choice"
                                        : Math.abs(difference) < 1
                                          ? "Same price"
                                          : difference < 0
                                            ? `Rs. ${formatAmount(Math.abs(difference))} less pp`
                                            : `Rs. ${formatAmount(difference)} more pp`;
                                      return (
                                        <div
                                          key={stayPackage.stays.map((stay) => stay.roomRateId).join("-")}
                                          className={`d-flex flex-wrap justify-content-between align-items-center gap-2 border rounded px-2 py-2${current ? " bg-light" : ""}`}
                                        >
                                          <div>
                                            <div className="small fw-semibold">
                                              {firstStay.hotel} - all {stayPackage.stays.length} nights
                                            </div>
                                            <div className="d-flex flex-wrap gap-1 mt-1">
                                              {summary.iotripsPick && <span className="hotel-select__chip is-pick">★ Iotrips pick</span>}
                                              <span className={`hotel-select__chip is-${summary.mealPlan.tone}`}>{summary.mealPlan.label}</span>
                                              {summary.stayType && <span className="hotel-select__chip is-type">{summary.stayType}</span>}
                                            </div>
                                          </div>
                                          <div className="text-end">
                                            <div className={`small fw-semibold ${!current && difference < 0 ? "text-success" : !current && difference > 0 ? "text-warning-emphasis" : "text-muted"}`}>
                                              {differenceText}
                                            </div>
                                            {!current && (
                                              <Button
                                                type="button"
                                                size="sm"
                                                variant="primary"
                                                className="mt-1"
                                                onClick={() => chooseSameStayForAllNights(stayPackage.stays)}
                                              >
                                                Choose for all nights
                                              </Button>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                    <div className="small text-muted">
                                      Prices are for the whole trip with the suggested rooms; the final price updates once you choose.
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            <Row className="g-2">
                              <CompareStaysModal
                                show={Boolean(compareNight)}
                                night={compareNight?.night}
                                options={compareNight?.options || []}
                                value={compareNight?.value || ""}
                                formatAmount={formatAmount}
                                roomTypesFor={compareRoomTypes}
                                priceRoomType={(rateId, categoryId) =>
                                  priceCompareRoomType(compareNight.night, rateId, categoryId)
                                }
                                onHide={() => setCompareNight(null)}
                                onChoose={(nextValue, roomChoice) => {
                                  applyCompareChoice(compareNight.night, compareNight.value, nextValue, roomChoice);
                                  setCompareNight(null);
                                }}
                              />
                              {roomRateOptionsByNight.map((group) => {
                                const selectedRateIds =
                                  selectedRoomRateIdsForNight(group.night);
                                const nightPrices =
                                  hotelOptionPrices[String(group.night)] || {};
                                const groupOptions = roomRateOptionsForGroup(group);
                                // One city for the whole night: it is already in the night header,
                                // so repeating it on every option is noise.
                                const singleCityNight = new Set(
                                  groupOptions.map((option) =>
                                    roomRateLocationLabel(roomRateById.get(String(option.value))),
                                  ),
                                ).size <= 1;
                                const currentPrice = Object.values(nightPrices).find((item) => item?.selected);
                                const currentSummary = currentPrice
                                  ? stayFacilitySummary(roomRateById.get(String(currentPrice.roomRateId)))
                                  : null;
                                const hotelOptions = groupOptions
                                  .map((option) => {
                                    const rate = roomRateById.get(String(option.value));
                                    const price = nightPrices[String(option.value)] || null;
                                    const summary = stayFacilitySummary(rate);
                                    return {
                                      ...option,
                                      label: singleCityNight
                                        ? roomRateHotelCodeLabel(rate, option.label)
                                        : option.label,
                                      roomsText: optionRoomsText(rate, price),
                                      roomsLeft: optionRoomsLeft(rate),
                                      mealNote: mealComparisonNote(summary, price, currentSummary),
                                    };
                                  })
                                  .map((option) => ({
                                    ...option,
                                    soldOut: isRoomRateSoldOut(
                                      roomRateById.get(String(option.value)),
                                    ),
                                    price: nightPrices[String(option.value)] || null,
                                    // Per person alone hides the group's real saving or cost.
                                    totalDifference: (() => {
                                      const price = nightPrices[String(option.value)];
                                      const current = Object.values(nightPrices).find((item) => item?.selected);
                                      if (!price || !current || price.totalPrice == null || current.totalPrice == null) return null;
                                      return Number(price.totalPrice) - Number(current.totalPrice);
                                    })(),
                                    // Meal plan, what the price includes, and paid extras kept apart.
                                    // Nothing here identifies the hotel.
                                    ...stayFacilitySummary(roomRateById.get(String(option.value))),
                                  }))
                                  // The meal line below already says meals cost extra - the tag need not repeat it.
                                  .map((option) =>
                                    option.mealNote && option.mealPlan?.tone === "nomeals"
                                      ? { ...option, mealPlan: { ...option.mealPlan, label: "No meals included" } }
                                      : option,
                                  )
                                  // Cheapest first. Options we could not price keep their
                                  // configured order at the bottom rather than reading as free.
                                  .sort((a, b) => {
                                    const left = a.price?.perPersonPrice;
                                    const right = b.price?.perPersonPrice;
                                    if (left == null && right == null) return 0;
                                    if (left == null) return 1;
                                    if (right == null) return -1;
                                    return Number(left) - Number(right);
                                  });
                                const pricedAmounts = hotelOptions
                                  .map((option) => Number(option.price?.perPersonPrice))
                                  .filter((amount) => Number.isFinite(amount));
                                // Rounded outward so the handles can always reach the cheapest and
                                // dearest hotel rather than stopping just short of them.
                                const priceBounds = pricedAmounts.length > 1
                                  ? {
                                      min: Math.floor(Math.min(...pricedAmounts) / 100) * 100,
                                      max: Math.ceil(Math.max(...pricedAmounts) / 100) * 100,
                                    }
                                  : null;
                                const priceRange =
                                  priceBounds &&
                                  (hotelPriceRanges[String(group.night)] || [
                                    priceBounds.min,
                                    priceBounds.max,
                                  ]);
                                const visibleHotelOptions = hotelOptions.filter((option) => {
                                  // Whatever is already selected always stays on the list.
                                  if (selectedRateIds.includes(String(option.value))) return true;
                                  if (mealsIncludedOnly && option.mealPlan?.tone !== "meals") return false;
                                  if (!priceRange) return true;
                                  const amount = Number(option.price?.perPersonPrice);
                                  // Options we could not price stay rather than vanish.
                                  if (!Number.isFinite(amount)) return true;
                                  return amount >= priceRange[0] && amount <= priceRange[1];
                                });
                                const selectedRates = selectedRateIds
                                  .map((rateId) =>
                                    group.rates.find(
                                      (rate) =>
                                        String(rate.id) === String(rateId),
                                    ),
                                  )
                                  .filter(Boolean);
                                const stayDateLabel = formatDisplayDate(
                                  addLocalDays(
                                    selectedDate,
                                    Math.max(1, Number(group.night) || 1) - 1,
                                  ),
                                );
                                const nightCityLabel = Array.from(
                                  new Set(
                                    (selectedRates.length
                                      ? selectedRates
                                      : group.rates
                                    ).map((rate) => roomRateLocationLabel(rate)),
                                  ),
                                ).join(" / ");
                                const nightNoStay = isNoStayNight(group.night);
                                // At least one night keeps a stay, so the last one can't be switched off.
                                const stayNightsLeft = roomRateOptionsByNight.filter(
                                  (item) => !isNoStayNight(item.night),
                                ).length;
                                const canSkipStay = nightNoStay || stayNightsLeft > 1;
                                return (
                                  <Col xs={12} key={group.night}>
                                    <div className="border rounded p-2 bg-light trip-room-selector-card">
                                      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                                        <div>
                                          <div className="small fw-semibold">
                                            Night {group.night}
                                            {nightCityLabel ? ` - ${nightCityLabel}` : ""} - Stay
                                            Date: {stayDateLabel}
                                          </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                                          {roomRateOptionsByNight.length > 1 && (
                                            <Form.Select
                                              size="sm"
                                              value={nightNoStay ? "NO_STAY" : "STAY"}
                                              onChange={(event) => {
                                                const skip = event.target.value === "NO_STAY";
                                                const night = Number(group.night);
                                                setNoStayNights((prev) =>
                                                  skip
                                                    ? Array.from(new Set([...prev, night])).sort((a, b) => a - b)
                                                    : prev.filter((item) => item !== night),
                                                );
                                              }}
                                              aria-label={`Stay needed for night ${group.night}`}
                                              style={{ width: 150 }}
                                            >
                                              <option value="STAY">Stay needed</option>
                                              <option value="NO_STAY" disabled={!canSkipStay}>
                                                No stay
                                              </option>
                                            </Form.Select>
                                          )}
                                          {!nightNoStay && roomCityOptions.length > 1 && (
                                            <Form.Select
                                              size="sm"
                                              value={group.selectedCity || ""}
                                              onChange={(event) =>
                                                updateRoomCityForNight(group.night, event.target.value)
                                              }
                                              aria-label={`Select city for night ${group.night}`}
                                              style={{ width: 150 }}
                                            >
                                              {roomCityOptions.map((city) => (
                                                <option key={city} value={city}>{city}</option>
                                              ))}
                                            </Form.Select>
                                          )}
                                          {!nightNoStay && hotelOptions.length > 1 && (
                                            <Button
                                              type="button"
                                              size="sm"
                                              variant="outline-secondary"
                                              onClick={() =>
                                                setCompareNight({
                                                  night: group.night,
                                                  value: selectedRateIds[0] || "",
                                                  options: hotelOptions.map((option) => ({
                                                    ...option,
                                                    disabled: option.soldOut || Boolean(option.price?.unavailableReason),
                                                  })),
                                                })
                                              }
                                            >
                                              Compare
                                            </Button>
                                          )}
                                          {!nightNoStay && (
                                          <Button
                                            type="button"
                                            size="sm"
                                            variant="outline-primary"
                                            onClick={() =>
                                              addSelectedRoomRate(group.night)
                                            }
                                            disabled={
                                              selectedRateIds.length >=
                                              hotelOptions.length
                                            }
                                          >
                                            Add Hotel
                                          </Button>
                                          )}
                                        </div>
                                      </div>
                                      {nightNoStay && (
                                        <div className="small text-muted">
                                          No stay for this night - you'll arrange your own accommodation.
                                          The vehicle and the rest of the trip are still included.
                                        </div>
                                      )}
                                      {!nightNoStay && priceBounds && (
                                        <HotelPriceRangeFilter
                                          bounds={priceBounds}
                                          value={priceRange}
                                          onChange={(next) =>
                                            setHotelPriceRanges((prev) => ({
                                              ...prev,
                                              [String(group.night)]: next,
                                            }))
                                          }
                                          shownCount={visibleHotelOptions.length}
                                          totalCount={hotelOptions.length}
                                          formatAmount={formatAmount}
                                        />
                                      )}
                                      {!nightNoStay && !selectedRates.length && (
                                        <HotelOptionSelect
                                          options={visibleHotelOptions.map((option) => ({
                                            ...option,
                                            // The hotel's minimum guests rules it out for this group.
                                            disabled: option.soldOut || Boolean(option.price?.unavailableReason),
                                            disabledReason: option.soldOut ? "" : option.price?.unavailableReason,
                                          }))}
                                          value=""
                                          loading={hotelPricesLoading}
                                          formatAmount={formatAmount}
                                          groupByStayType
                                          ariaLabel={`Select hotel for night ${group.night}`}
                                          onChange={(nextValue) =>
                                            setSelectedRoomRateIdsByNight((prev) => ({
                                              ...prev,
                                              [String(group.night)]: nextValue ? [nextValue] : [],
                                            }))
                                          }
                                        />
                                      )}
                                      <div className="d-flex flex-column gap-2">
                                        {!nightNoStay && selectedRates.map((selectedRate, hotelIndex) => {
                                          const selectedRateId = selectedRate.id;
                                          const rateSoldOut =
                                            isRoomRateSoldOut(selectedRate);
                                          const categories =
                                            categoryOptionsForRate(selectedRate);
                                          const selectedHotel = hotelForRate(selectedRate);
                                          const quoteStay = [
                                            ...(quote?.roomStayCosts || []),
                                            ...(quote?.extensionStayCosts || []),
                                          ].find(
                                            (stay) => String(stay.roomRateId) === String(selectedRateId),
                                          );
                                          const facilityItems = hotelFacilityItems(selectedHotel, selectedRate, quoteStay);
                                          const includedFacilityItems = facilityItems.filter(
                                            (facility) => facility.available === true && facilityCostBehavior(facility) !== "OPTIONAL_PAID",
                                          );
                                          const optionalFacilityItems = facilityItems.filter(
                                            (facility) => facility.id != null && facility.available && facilityCostBehavior(facility) === "OPTIONAL_PAID",
                                          );
                                          return (
                                            <div className="border rounded p-2 bg-white" key={selectedRate.id}>
                                              <div className="d-flex align-items-center gap-2 mb-2">
                                                <HotelOptionSelect
                                                  options={visibleHotelOptions.map((option) => ({
                                                    ...option,
                                                    // Already taken by another row on this night,
                                                    // or unavailable - either way not choosable here.
                                                    disabled:
                                                      String(option.value) !== String(selectedRateId) &&
                                                      (selectedRateIds.includes(String(option.value)) ||
                                                        option.soldOut ||
                                                        Boolean(option.price?.unavailableReason)),
                                                    disabledReason:
                                                      option.soldOut || selectedRateIds.includes(String(option.value))
                                                        ? ""
                                                        : option.price?.unavailableReason,
                                                  }))}
                                                  value={selectedRateId}
                                                  disabled={hotelOptions.length <= 1}
                                                  loading={hotelPricesLoading}
                                                  formatAmount={formatAmount}
                                                  groupByStayType
                                                  ariaLabel={`Select hotel ${hotelIndex + 1} for night ${group.night}`}
                                                  onChange={(nextValue) =>
                                                    updateSelectedRoomRate(
                                                      group.night,
                                                      selectedRateId,
                                                      nextValue,
                                                    )
                                                  }
                                                />
                                                {rateSoldOut && (
                                                  <span className="badge bg-danger-subtle text-danger border border-danger-subtle flex-shrink-0">
                                                    Sold out
                                                  </span>
                                                )}
                                                {selectedRates.length > 1 && (
                                                  <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline-danger"
                                                    onClick={() =>
                                                      removeSelectedRoomRate(
                                                        group.night,
                                                        selectedRateId,
                                                      )
                                                    }
                                                  >
                                                    Remove
                                                  </Button>
                                                )}
                                              </div>
                                              {(() => {
                                                const mealPlan = stayFacilitySummary(selectedRate).mealPlan;
                                                return (
                                                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                                                    <span className={`trip-meal-plan is-${mealPlan.tone}`}>{mealPlan.label}</span>
                                                    {showPhotoRequest && (
                                                      <a
                                                        className="small"
                                                        href={photoRequestLink(group.night, selectedRate)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                      >
                                                        <i className="bi bi-whatsapp me-1" aria-hidden="true" />
                                                        Request room photos
                                                      </a>
                                                    )}
                                                  </div>
                                                );
                                              })()}
                                              {teamMediaAccess && (
                                                teamHotelMedia.expired ? (
                                                  <div className="small text-danger mb-2">
                                                    Your team login has expired - log in to the Call / Chat portal again to copy room photos.
                                                  </div>
                                                ) : (() => {
                                                  // Still working out which saved login is valid.
                                                  if (!teamHotelMedia.access) return null;
                                                  const media = teamMediaFor(selectedRate);
                                                  return media ? (
                                                    <div className="mb-2">
                                                      <TeamStayMedia
                                                        team={teamHotelMedia.access.team}
                                                        token={teamHotelMedia.access.token}
                                                        hotelRow={media.hotelRow}
                                                        categories={media.categories}
                                                        onExpired={() => setTeamHotelMedia({ rows: [], expired: true, access: null })}
                                                      />
                                                    </div>
                                                  ) : null;
                                                })()
                                              )}
                                              <div className="trip-hotel-facilities small mb-2">
                                                <span className="text-muted">Included facilities:</span>{" "}
                                                {includedFacilityItems.length ? (
                                                  includedFacilityItems.map((facility) => (
                                                    <span
                                                      className="trip-facility-pill"
                                                      key={`${selectedRateId}-${facility.name}`}
                                                    >
                                                      {hotelFacilityDisplayLabel(facility, selectedRate?.nightNumber || group.night)}
                                                    </span>
                                                  ))
                                                ) : (
                                                  <span className="text-muted">None</span>
                                                )}
                                              </div>
                                              {optionalFacilityItems.length > 0 && (
                                                <div className="border-top pt-2 mb-2">
                                                  <div className="small text-muted mb-1">Optional facilities</div>
                                                  <div className="d-flex flex-column gap-1">
                                                    {optionalFacilityItems.map((facility) => {
                                                      const facilityKey = `${selectedRateId}:${facility.id}`;
                                                      return (
                                                        <Form.Check
                                                          key={facilityKey}
                                                          type="switch"
                                                          className="small"
                                                          id={`hotel-paid-facility-${facilityKey}`}
                                                          label={`${facility.name} (+Rs. ${formatAmount(facility.extraCost || 0)}${facility.pricingType === "PER_PERSON" ? " / person / night" : " / night"})`}
                                                          checked={selectedHotelFacilityKeys[facilityKey] === true}
                                                          onChange={(event) =>
                                                            setSelectedHotelFacilityKeys((prev) => ({
                                                              ...prev,
                                                              [facilityKey]: event.target.checked,
                                                            }))
                                                          }
                                                        />
                                                      );
                                                    })}
                                                  </div>
                                                </div>
                                              )}
                                              <div className="d-flex flex-column gap-1">
                                                {categories.map((category) => {
                                          const key = categoryUnitKey(
                                            selectedRateId,
                                            category.id,
                                          );
                                          const units = clampRoomUnits(
                                            selectedRoomCategoryUnits[key] || 0,
                                            category,
                                            selectedRateId,
                                          );
                                          const maxUnits = maxUnitsForCategory(
                                            category,
                                            selectedRateId,
                                          );
                                          const maxPeople =
                                            maxPeoplePerRoomForCategory(
                                              category,
                                            );
                                          const soldOut = isCategorySoldOut(
                                            category,
                                            selectedRateId,
                                          );
                                          const canIncrease =
                                            !soldOut && units < maxUnits;
                                          return (
                                            <div
                                              className={`d-flex justify-content-between align-items-center gap-2${soldOut ? " opacity-75" : ""}`}
                                              key={category.id}
                                            >
                                              <span className="small d-flex flex-column gap-1">
                                                <span className="fw-semibold">
                                                  {category.categoryName}
                                                </span>
                                                <span className="d-flex align-items-center gap-1 text-muted flex-wrap">
                                                  <span>
                                                    Max adults {maxPeople}
                                                    {Number(category.minGuests || 0) > 0 && ` · Min ${category.minGuests} per room`}
                                                  </span>
                                                  <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                      <Tooltip
                                                        id={`room-capacity-${group.night}-${category.id}`}
                                                        className="trip-info-tooltip"
                                                      >
                                                        {categoryCapacityTooltip(
                                                          category,
                                                        )}
                                                      </Tooltip>
                                                    }
                                                  >
                                                    <button
                                                      type="button"
                                                      className="trip-info-button"
                                                      aria-label={`Room capacity details for ${category.categoryName}`}
                                                    >
                                                      <i
                                                        className="bi bi-info-lg"
                                                        aria-hidden="true"
                                                      />
                                                    </button>
                                                  </OverlayTrigger>
                                                  {soldOut ? (
                                                    <span className="badge bg-danger-subtle text-danger border border-danger-subtle">
                                                      Sold out
                                                    </span>
                                                  ) : (
                                                    <span>- Available units {maxUnits}</span>
                                                  )}
                                                </span>
                                              </span>
                                              <InputGroup
                                                size="sm"
                                                style={{
                                                  width: canIncrease ? 118 : 78,
                                                }}
                                              >
                                                <Button
                                                  variant="outline-secondary"
                                                  onClick={() =>
                                                    updateSelectedRoomCategoryUnits(
                                                      selectedRateId,
                                                      category,
                                                      -1,
                                                    )
                                                  }
                                                  disabled={units <= 0}
                                                  aria-label={`Reduce ${category.categoryName} units for night ${group.night}`}
                                                >
                                                  -
                                                </Button>
                                                <InputGroup.Text>
                                                  {units}
                                                </InputGroup.Text>
                                                {canIncrease && (
                                                  <Button
                                                    variant="outline-secondary"
                                                    onClick={() =>
                                                      updateSelectedRoomCategoryUnits(
                                                        selectedRateId,
                                                        category,
                                                        1,
                                                      )
                                                    }
                                                    aria-label={`Increase ${category.categoryName} units for night ${group.night}`}
                                                  >
                                                    +
                                                  </Button>
                                                )}
                                              </InputGroup>
                                            </div>
                                          );
                                        })}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </Col>
                                );
                              })}
                            </Row>
                          </Col>
                        )}
                      {quoteError && (
                        <Col xs={12}>
                          <Alert variant="warning" className="py-2 small mb-0">
                            {quoteError}
                          </Alert>
                        </Col>
                      )}
                      <Col xs={12}>
                        <div className="trip-form-section">Getting around</div>
                      </Col>
                      <Col xs={12}>
                        <Form.Label className="small text-muted">
                          Pickup Type
                        </Form.Label>
                        <Form.Select
                          value={dynamicPickupId}
                          onChange={(e) => setDynamicPickupId(e.target.value)}
                        >
                          {(quoteOptions.pickups || []).map((pickup) => (
                            <option key={pickup.id} value={pickup.id}>
                              {pickup.pickupName}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col xs={12}>
                        <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
                          <Form.Label className="small text-muted mb-0">
                            Vehicle
                          </Form.Label>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline-primary"
                            onClick={() =>
                              setAdditionalVehicleIds((prev) => [
                                ...prev,
                                String(quote?.selectedVehicle?.id || pickupQuoteVehicles[0]?.id || ""),
                              ])
                            }
                            disabled={!pickupQuoteVehicles.length}
                          >
                            Add Vehicle
                          </Button>
                        </div>
                        <Form.Select
                          value={dynamicVehicleId}
                          onChange={(e) => setDynamicVehicleId(e.target.value)}
                          aria-label="Vehicle 1"
                        >
                          <option value="">
                            {autoSuggestedVehicleLabel
                              ? `Auto suggest - ${autoSuggestedVehicleLabel}`
                              : "Auto suggest"}
                          </option>
                          {firstVehicleOptions.map((vehicle) => (
                            <option key={vehicle.id} value={vehicle.id}>
                              {vehicle.vehicleName} (max {vehicle.capacityMax})
                            </option>
                          ))}
                        </Form.Select>
                        {additionalVehicleIds.map((vehicleId, index) => (
                          <div key={index} className="d-flex align-items-center gap-2 mt-2">
                            <Form.Select
                              value={vehicleId}
                              onChange={(e) =>
                                setAdditionalVehicleIds((prev) =>
                                  prev.map((value, i) => (i === index ? e.target.value : value)),
                                )
                              }
                              aria-label={`Vehicle ${index + 2}`}
                            >
                              {pickupQuoteVehicles.map((vehicle) => (
                                <option key={vehicle.id} value={vehicle.id}>
                                  {vehicle.vehicleName} (max {vehicle.capacityMax})
                                </option>
                              ))}
                            </Form.Select>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline-danger"
                              onClick={() =>
                                setAdditionalVehicleIds((prev) => prev.filter((_, i) => i !== index))
                              }
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                      </Col>
                      <Col xs={6}>
                        <Form.Label className="small text-muted">
                          Vehicle AC
                        </Form.Label>
                        <Form.Select
                          value={dynamicAcType}
                          onChange={(e) => setDynamicAcType(e.target.value)}
                        >
                          <option value="NON_AC">Non-AC</option>
                          <option value="AC">AC</option>
                        </Form.Select>
                      </Col>
                      <Col xs={12}>
                        <div className="trip-form-section">Extras</div>
                      </Col>
                      <Col xs={12}>
                        <div className="border rounded p-2 bg-light">
                          <div className="small fw-semibold mb-2">
                            Trip Extension
                          </div>
                          <Row className="g-2">
                            <Col xs={12}>
                              <Form.Label className="small text-muted">
                                Trip Extension Required
                              </Form.Label>
                              <Form.Select
                                value={tripExtensionRequired ? "YES" : "NO"}
                                onChange={(e) => {
                                  const required = e.target.value === "YES";
                                  setTripExtensionRequired(required);
                                  if (required && !extraPreNightCount && !extraPostNightCount) {
                                    setExtraPreNights("1");
                                  }
                                }}
                              >
                                <option value="NO">No</option>
                                <option value="YES">Yes</option>
                              </Form.Select>
                              {extensionStayOnlyForPickup && (
                                <Form.Text className="text-muted">
                                  Only extra hotel nights can be added online for this pickup location.
                                  For extra vehicle days, please contact our team and we will arrange it for you.
                                </Form.Text>
                              )}
                            </Col>
                            {tripExtensionRequired && (
                              <>
                            <Col xs={6}>
                              <Form.Label className="small text-muted">
                                Extra pre-nights
                              </Form.Label>
                              <Form.Control
                                type="number"
                                min="0"
                                step="1"
                                value={extraPreNights}
                                onChange={(e) =>
                                  setExtraPreNights(e.target.value)
                                }
                                placeholder="0"
                              />
                            </Col>
                            <Col xs={6}>
                              <Form.Label className="small text-muted">
                                Extra post-nights
                              </Form.Label>
                              <Form.Control
                                type="number"
                                min="0"
                                step="1"
                                value={extraPostNights}
                                onChange={(e) =>
                                  setExtraPostNights(e.target.value)
                                }
                                placeholder="0"
                              />
                            </Col>
                            {extensionStays.map((stay, index) => {
                              const hotels = extensionHotelsForCity(stay.city);
                              const categories = extensionCategoriesForHotel(stay.hotelId);
                              const extensionSelections = stay.roomCategorySelections || [];
                              const updateStay = (changes) =>
                                setExtensionStays((previous) =>
                                  previous.map((item, itemIndex) =>
                                    itemIndex === index ? { ...item, ...changes } : item,
                                  ),
                                );
                              const categoryUnits = (categoryId) =>
                                Number(extensionSelections.find(
                                  (selection) => String(selection.roomCategoryId) === String(categoryId),
                                )?.units || 0);
                              const updateCategoryUnits = (category, units) => {
                                const nextUnits = Math.max(
                                  0,
                                  Math.min(extensionMaxUnits(category, stay), Number(units || 0)),
                                );
                                const nextSelections = extensionSelections
                                  .filter((selection) => String(selection.roomCategoryId) !== String(category.id));
                                if (nextUnits > 0) {
                                  nextSelections.push({ roomCategoryId: category.id, units: nextUnits });
                                }
                                const primary = nextSelections.find((selection) => Number(selection.units || 0) > 0) || {};
                                updateStay({
                                  roomCategorySelections: nextSelections,
                                  roomCategoryId: primary.roomCategoryId || "",
                                  units: primary.units || 0,
                                });
                              };
                              const sameNightCount = extensionStays.filter(
                                (item) =>
                                  String(item.position || "PRE") === String(stay.position || "PRE") &&
                                  Number(item.nightIndex || 1) === Number(stay.nightIndex || 1),
                              ).length;
                              const extensionStayDate = selectedDate
                                ? addLocalDays(
                                    selectedDate,
                                    stay.position === "POST"
                                      ? Number(quoteOptions?.durationNights || 0) + Number(stay.nightIndex || 1) - 1
                                      : -Number(stay.nightIndex || 1),
                                  )
                                : null;
                              const addHotelForNight = () =>
                                setExtensionStays((previous) => {
                                  const lastIndex = previous.reduce(
                                    (found, item, itemIndex) =>
                                      String(item.position || "PRE") === String(stay.position || "PRE") &&
                                      Number(item.nightIndex || 1) === Number(stay.nightIndex || 1)
                                        ? itemIndex
                                        : found,
                                    index,
                                  );
                                  const next = [...previous];
                                  next.splice(
                                    lastIndex + 1,
                                    0,
                                    defaultExtensionStay(stay.position || "PRE", {}, Number(stay.nightIndex || 1)),
                                  );
                                  return next;
                                });
                              const removeHotelForNight = () =>
                                sameNightCount > 1 &&
                                setExtensionStays((previous) =>
                                  previous.filter((_, itemIndex) => itemIndex !== index),
                                );
                              const stayIncludesStay = (stay.scope || "BOTH") !== "VEHICLE";
                              const selectedExtensionHotel = extensionHotelOptions.find(
                                (item) => String(item.id) === String(stay.hotelId),
                              );
                              const facilityItems = stayIncludesStay && selectedExtensionHotel
                                ? hotelFacilityItems(selectedExtensionHotel)
                                : [];
                              const includedFacilityItems = facilityItems.filter(
                                (facility) => facility.available === true && facilityCostBehavior(facility) !== "OPTIONAL_PAID",
                              );
                              const optionalFacilityItems = facilityItems.filter(
                                (facility) => facility.id != null && facility.available && facilityCostBehavior(facility) === "OPTIONAL_PAID",
                              );
                              return (
                                <Col xs={12} key={stay.stayKey || `${stay.position}-${index}`}>
                                  <div className="border rounded p-2 bg-white">
                                    <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                                      <div className="small fw-semibold">
                                        {stay.position === "POST" ? "Extra post-night" : "Extra pre-night"} {Number(stay.nightIndex || 1)}
                                        {extensionStayDate ? ` - Stay Date: ${formatDisplayDate(extensionStayDate)}` : ""}
                                      </div>
                                      <div className="d-flex gap-2">
                                        {sameNightCount > 1 && (
                                          <Button type="button" variant="outline-danger" size="sm" onClick={removeHotelForNight}>
                                            Remove
                                          </Button>
                                        )}
                                        {stayIncludesStay && (
                                          <Button type="button" variant="outline-primary" size="sm" onClick={addHotelForNight}>
                                            Add Hotel
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                    <Row className="g-2">
                                      <Col xs={12}>
                                        <Form.Label className="small text-muted">This night needs</Form.Label>
                                        <Form.Select
                                          size="sm"
                                          value={extensionStayOnlyForPickup ? "STAY" : stay.scope || "BOTH"}
                                          disabled={extensionStayOnlyForPickup}
                                          onChange={(event) => updateStay({ scope: event.target.value })}
                                        >
                                          {!extensionStayOnlyForPickup && (
                                            <option value="BOTH">Stay and Vehicle</option>
                                          )}
                                          <option value="STAY">Stay only</option>
                                          {!extensionStayOnlyForPickup && (
                                            <option value="VEHICLE">Vehicle only</option>
                                          )}
                                        </Form.Select>
                                      </Col>
                                      {stayIncludesStay && (
                                        <>
                                      <Col xs={6}>
                                        <Form.Label className="small text-muted">City</Form.Label>
                                        <Form.Select
                                          value={stay.city || ""}
                                          onChange={(event) => {
                                            const city = event.target.value;
                                            const hotel = extensionHotelsForCity(city)[0] || null;
                                            const category = firstFillableCategory(extensionCategoriesForHotel(hotel?.id));
                                            updateStay({
                                              city,
                                              hotelId: hotel?.id || "",
                                              roomCategoryId: category?.id || "",
                                              units: suggestedExtensionUnits(category),
                                              roomCategorySelections: category
                                                ? [{ roomCategoryId: category.id, units: suggestedExtensionUnits(category) }]
                                                : [],
                                              selectedFacilityKeys: {},
                                            });
                                          }}
                                        >
                                          <option value="">Select city</option>
                                          {extensionCityOptions.map((city) => (
                                            <option key={city} value={city}>{city}</option>
                                          ))}
                                        </Form.Select>
                                      </Col>
                                      <Col xs={6}>
                                        <Form.Label className="small text-muted">Hotel</Form.Label>
                                        <HotelOptionSelect
                                          options={(() => {
                                            const byHotel = extensionOptionPrices[String(stay.stayKey)] || {};
                                            return hotels
                                              .map((hotel) => ({
                                                value: String(hotel.id),
                                                label: hotel.hotelCode || hotel.hotelName || `Hotel ${hotel.id}`,
                                                price: byHotel[String(hotel.id)] || null,
                                                // The hotel's minimum guests rules it out for this group.
                                                disabled: Boolean(byHotel[String(hotel.id)]?.unavailableReason),
                                                disabledReason: byHotel[String(hotel.id)]?.unavailableReason || "",
                                                stayType: hotel.stayType || "",
                                                stayTypeDescription: hotel.stayTypeDescription || "",
                                                chips: hotelFacilityItems(hotel)
                                                  .filter(
                                                    (facility) =>
                                                      facility.available === true &&
                                                      facility.costBehavior !== "OPTIONAL_PAID",
                                                  )
                                                  .map((facility) => facility.name),
                                              }))
                                              .sort((a, b) => {
                                                const left = a.price?.perPersonPrice;
                                                const right = b.price?.perPersonPrice;
                                                if (left == null && right == null) return 0;
                                                if (left == null) return 1;
                                                if (right == null) return -1;
                                                return Number(left) - Number(right);
                                              });
                                          })()}
                                          value={stay.hotelId || ""}
                                          formatAmount={formatAmount}
                                          groupByStayType
                                          ariaLabel={`Select hotel for extra night ${stay.nightIndex || 1}`}
                                          onChange={(hotelId) => {
                                            const category = firstFillableCategory(extensionCategoriesForHotel(hotelId));
                                            updateStay({
                                              hotelId,
                                              roomCategoryId: category?.id || "",
                                              units: suggestedExtensionUnits(category),
                                              roomCategorySelections: category
                                                ? [{ roomCategoryId: category.id, units: suggestedExtensionUnits(category) }]
                                                : [],
                                              selectedFacilityKeys: {},
                                            });
                                          }}
                                        />
                                      </Col>
                                      <Col xs={12}>
                                        <Form.Label className="small text-muted">Room categories</Form.Label>
                                        <div className="d-flex flex-column gap-1">
                                          {categories.map((category) => {
                                            const units = categoryUnits(category.id);
                                            const maxUnits = extensionMaxUnits(category, stay);
                                            const maxPeople = maxPeoplePerRoomForCategory(category);
                                            const availabilityKnown = Boolean(
                                              extensionAvailabilityByKey[
                                                `${category.id}:${extensionStayDateValue(stay)}`
                                              ],
                                            );
                                            const soldOut = availabilityKnown && maxUnits === 0;
                                            return (
                                              <div key={category.id} className="d-flex justify-content-between align-items-center gap-2">
                                                <span className={soldOut ? "small text-muted" : "small"}>
                                                  {category.categoryName}{" "}
                                                  <span className="text-muted">
                                                    Max adults {maxPeople}
                                                    {Number(category.minGuests || 0) > 0 && ` · Min ${category.minGuests} per room`}
                                                  </span>
                                                  {availabilityKnown && (
                                                    <span className="text-muted">
                                                      {soldOut ? " · Sold out for this night" : ` · ${maxUnits} left`}
                                                    </span>
                                                  )}
                                                </span>
                                                <InputGroup size="sm" style={{ width: 118 }}>
                                                  <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    disabled={units <= 0}
                                                    onClick={() => updateCategoryUnits(category, units - 1)}
                                                  >
                                                    -
                                                  </Button>
                                                  <InputGroup.Text>{units}</InputGroup.Text>
                                                  <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    disabled={units >= maxUnits}
                                                    onClick={() => updateCategoryUnits(category, units + 1)}
                                                  >
                                                    +
                                                  </Button>
                                                </InputGroup>
                                              </div>
                                            );
                                          })}
                                          {!categories.length && <div className="small text-danger">No room categories available for this hotel.</div>}
                                        </div>
                                      </Col>
                                      <Col xs={12}>
                                        <div className="trip-hotel-facilities small mb-2">
                                          <span className="text-muted">Included facilities:</span>{" "}
                                          {includedFacilityItems.length ? (
                                            includedFacilityItems.map((facility) => (
                                              <span
                                                className="trip-facility-pill"
                                                key={`${stay.stayKey}-${facility.name}`}
                                              >
                                                {hotelFacilityDisplayLabel(facility, stay.nightIndex)}
                                              </span>
                                            ))
                                          ) : (
                                            <span className="text-muted">None</span>
                                          )}
                                        </div>
                                        {optionalFacilityItems.length > 0 && (
                                          <div className="border-top pt-2">
                                            <div className="small text-muted mb-1">Optional facilities</div>
                                            <div className="d-flex flex-column gap-1">
                                              {optionalFacilityItems.map((facility) => (
                                                <Form.Check
                                                  key={`${stay.stayKey}:${facility.id}`}
                                                  type="switch"
                                                  className="small"
                                                  id={`extension-facility-${stay.stayKey}-${facility.id}`}
                                                  label={`${facility.name} (+Rs. ${formatAmount(facility.extraCost || 0)}${facility.pricingType === "PER_PERSON" ? " / person / night" : " / night"})`}
                                                  checked={stay.selectedFacilityKeys?.[facility.id] === true}
                                                  onChange={(event) =>
                                                    updateStay({
                                                      selectedFacilityKeys: {
                                                        ...(stay.selectedFacilityKeys || {}),
                                                        [facility.id]: event.target.checked,
                                                      },
                                                    })
                                                  }
                                                />
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </Col>
                                        </>
                                      )}
                                    </Row>
                                  </div>
                                </Col>
                              );
                            })}
                            <Col xs={12}>
                              <Form.Label className="small text-muted">
                                Extension note
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={tripExtensionNote}
                                onChange={(e) =>
                                  setTripExtensionNote(e.target.value)
                                }
                                placeholder="Example: 2 PM Bangalore pickup, direct transfer to Coorg, no sightseeing"
                              />
                            </Col>
                              </>
                            )}
                          </Row>
                        </div>
                      </Col>
                      <Col xs={12}>
                        <Form.Label className="small text-muted">
                          Customization Required
                        </Form.Label>
                        <Form.Select
                          value={customizationRequired ? "YES" : "NO"}
                          onChange={(e) => {
                            const required = e.target.value === "YES";
                            setCustomizationRequired(required);
                            if (!required) {
                              setCustomizationDescription("");
                              setCustomizationPrice("");
                              setCustomizationPriceAdjustment("ADD");
                            }
                          }}
                        >
                          <option value="NO">No</option>
                          <option value="YES">Yes</option>
                        </Form.Select>
                      </Col>
                      {customizationRequired && (
                        <>
                          <Col xs={12}>
                            <Form.Label className="small text-muted">
                              Price Adjustment
                            </Form.Label>
                            <Form.Select
                              value={customizationPriceAdjustment}
                              onChange={(e) =>
                                setCustomizationPriceAdjustment(e.target.value)
                              }
                            >
                              <option value="ADD">Add to final amount</option>
                              <option value="REDUCE">
                                Reduce from final amount
                              </option>
                            </Form.Select>
                          </Col>
                          <Col xs={12}>
                            <Form.Label className="small text-muted">
                              Customization Details
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={customizationDescription}
                              onChange={(e) =>
                                setCustomizationDescription(e.target.value)
                              }
                              placeholder="Enter customization details"
                            />
                          </Col>
                          <Col xs={12}>
                            <Form.Label className="small text-muted">
                              Customization Price
                            </Form.Label>
                            <InputGroup>
                              <InputGroup.Text>Rs.</InputGroup.Text>
                              <Form.Control
                                type="number"
                                min="0"
                                step="1"
                                value={customizationPrice}
                                onChange={(e) =>
                                  setCustomizationPrice(e.target.value)
                                }
                                placeholder="Enter price"
                              />
                            </InputGroup>
                          </Col>
                        </>
                      )}
                      {(quoteOptions.addons || []).length > 0 && (
                        <Col xs={12}>
                          <Form.Label className="small text-muted">
                            Add-ons
                          </Form.Label>
                          <div className="d-flex flex-column gap-2">
                            {(quoteOptions.addons || []).map((addon) => {
                              const selected = selectedAddonIds.includes(addon.id);
                              const options = (addon.options || []).filter((item) => item.active !== false);
                              const choice = addonChoices[String(addon.id)] || {};
                              const option =
                                options.find((item) => String(item.id) === String(choice.optionId)) ||
                                options[0] ||
                                null;
                              const pricing = option || addon;
                              const countable = ["PER_PERSON", "PER_VEHICLE"].includes(pricing.pricingType);
                              return (
                                <div key={addon.id}>
                                  <Form.Check
                                    type="checkbox"
                                    id={`quote-addon-${addon.id}`}
                                    checked={selected}
                                    onChange={(e) =>
                                      toggleAddon(addon.id, e.target.checked)
                                    }
                                    label={<span>{addon.name}</span>}
                                  />
                                  {selected && options.length > 0 && (
                                    <div className="ms-4 mt-1 d-flex flex-column gap-1">
                                      {options.map((item) => (
                                        <Form.Check
                                          key={item.id}
                                          type="radio"
                                          name={`quote-addon-${addon.id}-option`}
                                          id={`quote-addon-${addon.id}-option-${item.id}`}
                                          checked={String(option?.id) === String(item.id)}
                                          onChange={() => chooseAddonOption(addon.id, { optionId: item.id })}
                                          label={
                                            <span>
                                              {item.name}
                                              {item.description && (
                                                <span className="text-muted small"> - {item.description}</span>
                                              )}
                                            </span>
                                          }
                                        />
                                      ))}
                                    </div>
                                  )}
                                  {selected && countable && (
                                    <div className="ms-4 mt-1 d-flex flex-wrap align-items-center gap-2 small">
                                      <span className="text-muted">How many take it</span>
                                      <Form.Control
                                        type="number"
                                        size="sm"
                                        min="1"
                                        max={selectedTravellerCount || undefined}
                                        style={{ width: 80 }}
                                        value={choice.people ?? ""}
                                        placeholder={String(selectedTravellerCount || "")}
                                        aria-label={`How many take ${addon.name}`}
                                        onChange={(e) => {
                                          const typed = e.target.value === "" ? null : Math.max(1, Number(e.target.value));
                                          // The whole group is the default - stored as nothing, not as a number.
                                          const people = typed && selectedTravellerCount && typed >= selectedTravellerCount ? null : typed;
                                          chooseAddonOption(addon.id, { people });
                                        }}
                                      />
                                      <span className="text-muted">
                                        {choice.people ? `of ${selectedTravellerCount}` : "everyone"}
                                        {pricing.pricingType === "PER_PERSON" && pricing.childrenFreeUpToAge != null
                                          ? ` · children aged ${pricing.childrenFreeUpToAge} and under go free`
                                          : ""}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </Col>
                      )}
                    </Row>

                    {quote && (
                      <Card className="mt-3 border-0 trip-price-card" ref={priceCardRef}>
                        <Card.Body className="py-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <div className="fw-semibold">
                                Calculated Price
                              </div>
                              <div className="text-muted small">
                                {quote.totalTravellers} travellers,{" "}
                                {quoteVehicleLabel}
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={copyCalculatedPrice}
                                disabled={quoteLoading}
                              >
                                Copy Quotation
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={copyHotelOptionsImage}
                                disabled={quoteLoading || !selectedTravelDateValue || !roomRateOptionsByNight.length}
                              >
                                Copy Hotel Options Image
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={downloadQuotationPdf}
                                disabled={quoteLoading}
                              >
                                Download Complete Details
                              </Button>
                              {quoteLoading && (
                                <span className="spinner-border spinner-border-sm" />
                              )}
                            </div>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span>Total</span>
                            <strong>
                              Rs. {formatAmount(quotedTotalPrice(quote))}
                            </strong>
                          </div>
                          {Number(quote.tripExtensionCost || 0) > 0 && (
                            <div className="d-flex justify-content-between text-muted small">
                              <span>Trip extension</span>
                              <span>
                                + Rs. {formatAmount(quote.tripExtensionCost)}
                              </span>
                            </div>
                          )}
                          {customizationRequired && (
                            <div className="d-flex justify-content-between text-muted small">
                              <span>
                                Customization
                                {customizationDescription.trim()
                                  ? `: ${customizationDescription.trim()}`
                                  : ""}
                              </span>
                              <span>
                                {customizationPriceAdjustment === "REDUCE"
                                  ? "-"
                                  : "+"}{" "}
                                Rs. {formatAmount(customizationPriceAmount)}
                              </span>
                            </div>
                          )}
                          <div className="d-flex justify-content-between">
                            <span>
                              Per person ({chargeablePersonText(quote)})
                            </span>
                            <strong>
                              Rs. {formatAmount(quotedPerPersonPrice(quote))}
                            </strong>
                          </div>
                          <div className="mt-3 d-flex flex-wrap gap-2">
                            {TRIP_SECTION_LINKS.map((link) => (
                              <Button
                                key={link.key}
                                as="a"
                                href={`#${link.key}`}
                                size="sm"
                                variant="outline-primary"
                                onClick={(event) =>
                                  handleTripSectionLink(event, link.key)
                                }
                              >
                                {link.label}
                              </Button>
                            ))}
                          </div>
                          <div className="mt-3 pt-3 border-top">
                            <div className="fw-semibold mb-2">Trip Summary</div>
                            {(quote.validationMessages || []).length > 0 && (
                              <Alert variant="warning" className="py-2 small">
                                {(quote.validationMessages || []).map(
                                  (message, index) => (
                                    <div key={index}>{message}</div>
                                  ),
                                )}
                              </Alert>
                            )}
                            <div className="d-flex flex-column gap-2 small">
                              <div>
                                <div className="text-muted">Travellers</div>
                                <div>{quoteTravellerSummary(quote)}</div>
                              </div>
                              {quoteHotelRoomDetailLines(quote).length > 0 && (
                                <div>
                                  <div className="text-muted">
                                    Hotel/Room Details
                                  </div>
                                  <div className="d-flex flex-column gap-1">
                                    {quoteHotelRoomDetailLines(quote).map(
                                      (line, index) => {
                                        const [roomAndFacilities, optionalFacilitiesBlock = ""] = String(line).split("\nOptional Facilities:\n");
                                        const [roomLine, facilitiesBlock = ""] = roomAndFacilities.split("\nFacilities:\n");
                                        const facilities = facilitiesBlock
                                          .split("\n")
                                          .map((item) => item.trim())
                                          .filter(Boolean)
                                          .map((item) => {
                                            const [name, value = ""] = item.split(":");
                                            return { name: name?.trim(), available: value.trim().toLowerCase() !== "no" };
                                          })
                                          .filter((item) => item.name);
                                        const optionalFacilities = optionalFacilitiesBlock
                                          .split("\n")
                                          .map((item) => item.trim())
                                          .filter(Boolean)
                                          .map((item) => {
                                            const separator = item.lastIndexOf(":");
                                            const name = separator >= 0 ? item.slice(0, separator).trim() : item;
                                            const value = separator >= 0 ? item.slice(separator + 1).trim() : "";
                                            return { name, available: value.toLowerCase() === "yes" };
                                          })
                                          .filter((item) => item.name);
                                        return (
                                          <div
                                            key={index}
                                            className="border rounded px-2 py-1 bg-light trip-summary-pill"
                                          >
                                            <div>{roomLine}</div>
                                            {facilities.length > 0 && (
                                              <div className="trip-hotel-facilities mt-1">
                                                <span className="text-muted">Facilities:</span>{" "}
                                                {facilities.map((facility) => (
                                                  <span
                                                    className="trip-facility-pill"
                                                    key={`${index}-${facility.name}`}
                                                  >
                                                    {facility.name}
                                                  </span>
                                                ))}
                                              </div>
                                            )}
                                            {optionalFacilities.length > 0 && (
                                              <div className="trip-hotel-facilities mt-1">
                                                <span className="text-muted">Optional Facilities:</span>{" "}
                                                {optionalFacilities.map((facility) => (
                                                  <span
                                                    className="trip-facility-pill"
                                                    key={`${index}-optional-${facility.name}`}
                                                  >
                                                    {facility.name}: {facility.available ? "Yes" : "No"}
                                                  </span>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      },
                                    )}
                                  </div>
                                </div>
                              )}
                              <div>
                                <div className="text-muted">Pickup</div>
                                <div>
                                  {quote.selectedPickup?.pickupName || "-"}
                                </div>
                              </div>
                              <div>
                                <div className="text-muted">Vehicle</div>
                                <div>
                                  {quoteVehicleLabel || "-"}
                                </div>
                              </div>
                              {tripExtensionSummary(quote) && (
                                <div>
                                  <div className="text-muted">
                                    Trip Extension
                                  </div>
                                  <div>{tripExtensionSummary(quote)}</div>
                                </div>
                              )}
                              {(quote.selectedAddons || []).length > 0 && (
                                <div>
                                  <div className="text-muted">Add-ons</div>
                                  <div>
                                    {quote.selectedAddons
                                      .map((addon) => addon.displayName || addon.name)
                                      .join(", ")}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          {adminCalculatorMode && renderAdminQuoteBreakup(quote)}
                        </Card.Body>
                      </Card>
                    )}
                  </div>
                )}
                {/* Price selection */}
                {!quoteOptions && (
                  <div className="mb-3">
                    <Form.Label className="fw-semibold">
                      Select Package
                    </Form.Label>
                    {!priceOptions.length ? (
                      <div className="text-muted small">
                        No prices available for this trip.
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {Object.entries(groupedPrices).map(
                          ([duration, items]) => (
                            <Card
                              key={duration}
                              className="border-0 trip-price-card"
                            >
                              <Card.Body className="py-2">
                                <div className="fw-semibold mb-2">
                                  {duration}
                                </div>
                                {items.map((opt) => (
                                  <div
                                    key={opt.id}
                                    className={`d-flex justify-content-between align-items-center py-1 px-2 rounded ${
                                      selectedPriceListId === opt.id
                                        ? "trip-price-option-selected border"
                                        : ""
                                    }`}
                                  >
                                    <div className="d-flex align-items-center gap-2">
                                      <Form.Check
                                        type="radio"
                                        id={`price-${opt.id}`}
                                        name="priceList"
                                        checked={selectedPriceListId === opt.id}
                                        onChange={() => {
                                          setSelectedPriceListId(opt.id);
                                          const people = getPeopleCount(
                                            opt.vehicleType,
                                          );
                                          if (people) {
                                            handleTravellerCountChange(people);
                                          }
                                        }}
                                      />
                                      <label
                                        htmlFor={`price-${opt.id}`}
                                        className="mb-0"
                                      >
                                        {opt.vehicleType}
                                      </label>
                                    </div>
                                    <div className="text-end">
                                      {opt.originalPrice ? (
                                        <>
                                          <div className="text-muted text-decoration-line-through small">
                                            ₹
                                            {Number(
                                              opt.originalPrice,
                                            ).toLocaleString()}
                                          </div>
                                          <div className="fw-semibold">
                                            ₹
                                            {Number(opt.price).toLocaleString()}{" "}
                                            {opt.currencyCode || ""}
                                          </div>
                                        </>
                                      ) : (
                                        <div className="fw-semibold">
                                          ₹{Number(opt.price).toLocaleString()}{" "}
                                          {opt.currencyCode || ""}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </Card.Body>
                            </Card>
                          ),
                        )}
                      </div>
                    )}
                  </div>
                )}
                {!quoteOptions && (
                  <Form.Group className="mb-3" controlId="numTravellers">
                    <Form.Label className="fw-semibold">
                      Number of Travellers
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={numTravellers}
                      onChange={(e) =>
                        handleTravellerCountChange(e.target.value)
                      }
                      placeholder="Enter number of travellers"
                      required
                    />
                  </Form.Group>
                )}
                {!quoteOptions && (
                  <Form.Group className="mb-3" controlId="selectDate">
                    <Form.Label className="fw-semibold">
                      Select Trip Start Date
                    </Form.Label>
                    <DatePicker
                      selected={selectedDate}
                      onChange={(date) => {
                        setSelectedDate(date);
                        resetRoomManualAdjustments();
                      }}
                      minDate={new Date()}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="DD/MM/YYYY"
                      className="form-control"
                      disabled={
                        !numTravellers ||
                        isNaN(numTravellers) ||
                        numTravellers < 1
                      }
                    />
                  </Form.Group>
                )}

                {!adminCalculatorMode && (
                <Card className="mb-4 p-3 trip-coupon-card" border="info">
                  <Form.Group className="mb-2">
                    <Form.Label className="fw-semibold">
                      Select a Coupon
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        list="couponOptions"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        placeholder="Enter or select coupon"
                      />
                      <datalist id="couponOptions">
                        {suggestedCoupons.map((coupon) => (
                          <option key={coupon.id} value={coupon.code}>
                            {coupon.code} —{" "}
                            {coupon.discountAmount != null
                              ? `₹${coupon.discountAmount} off`
                              : coupon.discountPercentage != null
                                ? `${coupon.discountPercentage}% off`
                                : "No discount"}
                          </option>
                        ))}
                      </datalist>

                      <Button
                        variant="success"
                        className="fw-bold"
                        onClick={handleApplyCoupon}
                        disabled={!couponCode}
                      >
                        Apply
                      </Button>

                      <Button
                        variant="outline-info"
                        onClick={handleSuggestCoupon}
                      >
                        Suggest
                      </Button>
                    </InputGroup>

                    {discountAmount > 0 && (
                      <div className="mt-2">
                        <span className="text-success">
                          ₹{discountAmount} off applied!
                        </span>
                        <br />
                        <strong className="trip-emphasis">
                          Final Price: ₹{finalPrice}
                        </strong>
                      </div>
                    )}
                  </Form.Group>
                </Card>
                )}

                {numTravellers > 0 && (
                  <div className="mt-2">
                    <strong>
                      Total Price (Before Discount): ₹
                      {quoteOptions && quote
                        ? `Rs. ${formatAmount(quote.roundedTotalPrice)}`
                        : (priceOptions.find(
                            (p) => p.id === selectedPriceListId,
                          )?.price ?? trip.price) * numTravellers}
                    </strong>
                    <br />
                    {discountAmount > 0 && (
                      <>
                        <span className="text-success">
                          Coupon Applied: ₹{discountAmount} off
                        </span>
                        <br />
                        <strong className="trip-emphasis">
                          Final Price: ₹{finalPrice}
                        </strong>
                      </>
                    )}
                  </div>
                )}

                {!adminCalculatorMode && (
                  <>
                    <Button
                      variant="warning"
                      className="w-100 fw-bold"
                      onClick={() => {
                        setShowReservationContact(true);
                        toast.info(
                          `Please contact Indian Odyssey on WhatsApp or phone at ${RESERVATION_PHONE_DISPLAY}.`,
                        );
                      }}
                    >
                      Contact to Reserve
                    </Button>
                    {showReservationContact && (
                      <Alert variant="info" className="mt-3 mb-0">
                        <div className="fw-semibold mb-1">
                          Please contact Indian Odyssey to reserve this trip.
                        </div>
                        <div className="small mb-3">
                          Online reservation is currently handled by our team. Please contact us on WhatsApp or phone at {RESERVATION_PHONE_DISPLAY}.
                        </div>
                        <div className="d-flex flex-column flex-sm-row gap-2">
                          <Button
                            as="a"
                            href={`https://wa.me/${RESERVATION_PHONE_DIGITS}?text=${encodeURIComponent(
                              `Hi Indian Odyssey, I want to reserve ${trip.title || "this trip"}${selectedDate ? ` for ${formatDisplayDate(selectedDate)}` : ""}. ${typeof window !== "undefined" ? window.location.href : ""}`,
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="success"
                            size="sm"
                          >
                            <i className="bi bi-whatsapp me-1" aria-hidden="true" />
                            WhatsApp
                          </Button>
                          <Button
                            as="a"
                            href={`tel:+${RESERVATION_PHONE_DIGITS}`}
                            variant="outline-primary"
                            size="sm"
                          >
                            <i className="bi bi-telephone me-1" aria-hidden="true" />
                            Call {RESERVATION_PHONE_DISPLAY}
                          </Button>
                        </div>
                      </Alert>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
          {/* Tabs Section */}
          <Col lg={6} ref={tripTabsRef} className="order-1 order-lg-1 mt-0 mt-lg-0 trip-tabs-section">
            <Tab.Container
              activeKey={activeTripTab}
              onSelect={(key) => key && setActiveTripTab(key)}
            >
              <Row className="mb-4">
                <Nav variant="tabs" className="justify-content-center">
                  <Nav.Item>
                    <Nav.Link eventKey="highlights">Highlights</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="overview">Overview</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="itinerary">Itinerary</Nav.Link>
                  </Nav.Item>
                  {pickupVideo?.youtubeUrl && (
                    <Nav.Item>
                      <Nav.Link eventKey="video">Video</Nav.Link>
                    </Nav.Item>
                  )}
                  <Nav.Item>
                    <Nav.Link eventKey="gallery">Gallery</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="terms">Terms & Conditions</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="faqs">FAQs</Nav.Link>
                  </Nav.Item>
                </Nav>
              </Row>

              <Tab.Content>
                {/* Highlights Tab */}
                <Tab.Pane eventKey="highlights" id="trip-section-highlights">
                  <h2 className="mb-4">
                    <i className="bi bi-stars text-warning me-2"></i>
                    Highlights
                  </h2>
                  {highlights.length ? (
                    <ListGroup variant="flush" className="trip-highlights-list">
                      {highlights.map((item, index) => (
                        <ListGroup.Item
                          key={index}
                          className="trip-highlight-item"
                        >
                          <span className="trip-highlight-icon">
                            {item.icon}
                          </span>
                          <span className="trip-highlight-copy">
                            <strong>{item.title}</strong>
                            {item.description ? ` - ${item.description}` : ""}
                          </span>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <p className="text-muted">
                      Highlights will be updated soon.
                    </p>
                  )}
                </Tab.Pane>

                {/* Overview Tab */}
                <Tab.Pane eventKey="overview" id="trip-section-overview">
                  <h2 className="mb-4">Overview</h2>
                  <p>{displayTrip.overviewText}</p>

                  <h5 className="mt-4">Departure & Return Location</h5>
                  <p>{displayTrip.departureLocation}</p>
                  {displayTrip.returnLocation && (
                    <p className="text-muted">{displayTrip.returnLocation}</p>
                  )}

                  <h5>Departure Time</h5>
                  <p>{displayTrip.departureTime}</p>

                  <h5>Price Includes</h5>
                  {(displayTrip.priceIncludes || []).map((item, index) => (
                    <p key={index}>
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      {item}
                    </p>
                  ))}

                  <h5>Price Excludes</h5>
                  {(displayTrip.priceExcludes || []).map((item, index) => (
                    <p key={index}>
                      <i className="bi bi-x-circle-fill text-danger me-2"></i>
                      {item}
                    </p>
                  ))}
                </Tab.Pane>

                {/* Itinerary Tab */}
                <Tab.Pane eventKey="itinerary" id="trip-section-itinerary">
                  {(displayTrip.itinerary || []).map((dayPlan, index) => (
                    <div key={index} className="mb-4 trip-itinerary-block">
                      <h5 className="trip-itinerary-day-title p-3 rounded fw-bold">
                        {dayPlan.day}{" "}
                        <span className="text-primary">{dayPlan.title}</span>
                      </h5>
                      {dayPlan.description && <p>{dayPlan.description}</p>}
                      {dayPlan.points && (
                        <ListGroup variant="flush" className="trip-themed-list">
                          {dayPlan.points.map((point, idx) => (
                            <ListGroup.Item key={idx}>{point}</ListGroup.Item>
                          ))}
                        </ListGroup>
                      )}
                      {dayPlan.stayInfo && (
                        <div className="trip-stay-info mt-4 p-3 rounded">
                          <strong>STAY TYPE –</strong> {dayPlan.stayInfo}
                        </div>
                      )}
                    </div>
                  ))}
                </Tab.Pane>

                {/* Gallery Tab */}
                {pickupVideo?.youtubeUrl && (
                  <Tab.Pane eventKey="video" id="trip-section-video">
                    <h3 className="mb-4">
                      <i className="bi bi-play-btn-fill text-danger me-2"></i>
                      {pickupVideo.title || "Trip Video"}
                    </h3>
                    <div className="ratio ratio-16x9 rounded overflow-hidden">
                      <iframe
                        src={`https://www.youtube-nocookie.com/embed/${youtubeVideoId(
                          pickupVideo.youtubeUrl
                        )}`}
                        title={pickupVideo.title || "Trip video"}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                      />
                    </div>
                    {selectedPickup?.pickupLabel && (
                      <div className="text-muted small mt-2">
                        Video for pickup from {selectedPickup.pickupLabel}
                      </div>
                    )}
                    {pickupVideo.downloadUrl && (
                      <div className="mt-3">
                        <Button
                          as="a"
                          href={pickupVideo.downloadUrl}
                          download
                          variant="outline-primary"
                          size="sm"
                        >
                          <i className="bi bi-download me-2"></i>
                          Download video
                        </Button>
                      </div>
                    )}
                  </Tab.Pane>
                )}

                <Tab.Pane eventKey="gallery" id="trip-section-gallery">
                  <h3 className="mb-4">
                    <i className="bi bi-image-fill me-2"></i> Gallery
                  </h3>
                  <Carousel>
                    {trip.galleryImages.map((imgSrc, idx) => (
                      <Carousel.Item key={idx}>
                        <img
                          className="d-block w-100 rounded"
                          src={imgSrc}
                          alt={`Slide ${idx + 1}`}
                          style={{ maxHeight: "500px", objectFit: "cover" }}
                        />
                      </Carousel.Item>
                    ))}
                  </Carousel>
                </Tab.Pane>

                {/* Terms Tab */}
                <Tab.Pane eventKey="terms" id="trip-section-terms">
                  <h2 className="mb-4">
                    <i className="bi bi-card-checklist text-primary me-2"></i>
                    Terms & Conditions
                  </h2>
                  {termsConditions.length ? (
                    termsConditions.map((section, index) => (
                      <div key={index} className="mb-4 trip-terms-block">
                        <h5>
                          {index + 1}. {section.title}
                        </h5>
                        {section.points?.length ? (
                          <ul className="mt-3">
                            {section.points.map((point, pointIdx) => (
                              <li key={pointIdx} className="mb-2">
                                {point}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <p className="text-muted">
                      Terms and conditions will be updated soon.
                    </p>
                  )}
                </Tab.Pane>

                {/* FAQs Tab */}
                <Tab.Pane eventKey="faqs" id="trip-section-faqs">
                  <h3 className="mb-4">
                    <i className="bi bi-question-circle-fill me-2"></i> FAQs
                  </h3>
                  <Accordion defaultActiveKey="0">
                    {(trip.faqs || []).map((faq, idx) => (
                      <Accordion.Item eventKey={idx.toString()} key={idx}>
                        <Accordion.Header>{faq.question}</Accordion.Header>
                        <Accordion.Body>{faq.answer}</Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Col>

          {/* Why Book With Us */}
          <Col xs={12} className="order-3 mt-4">
            <Card className="trip-why-card trip-why-card-inline">
              <Card.Header>
                <strong>Why Book With Us?</strong>
              </Card.Header>
              <ListGroup variant="flush" className="trip-themed-list">
                <ListGroup.Item>Safety First</ListGroup.Item>
                <ListGroup.Item>Experienced Team</ListGroup.Item>
                <ListGroup.Item>
                  Sustainability and Responsible Travel
                </ListGroup.Item>
                <ListGroup.Item>Creating Unforgettable Memories</ListGroup.Item>
              </ListGroup>
            </Card>
          </Col>
        </Row>
      </Container>
      <ContactPage />
      {!adminCalculatorMode && <FooterPage />}
      {/* Keeps the figure everyone is actually watching on screen while options are changed.
          Only appears once the real price card has scrolled away. */}
      {!adminCalculatorMode && quote && !priceCardVisible && (
        <div className="trip-sticky-price" role="status" aria-live="polite">
          <div className="trip-sticky-price__inner">
            <div className="trip-sticky-price__figures">
              <div className="trip-sticky-price__total">
                Rs. {formatAmount(quotedTotalPrice(quote))}
              </div>
              <div className="trip-sticky-price__per">
                Rs. {formatAmount(quotedPerPersonPrice(quote))} per person
                {quoteLoading && (
                  <span className="spinner-border spinner-border-sm ms-2" aria-hidden="true" />
                )}
              </div>
            </div>
            <Button
              size="sm"
              variant="primary"
              onClick={() =>
                priceCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
              }
            >
              View details
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const slugifyFileName = (value) =>
  String(value || "quotation")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "quotation";

const sanitizePdfText = (value) =>
  String(value ?? "")
    .replace(/₹/g, "Rs.")
    .normalize("NFKD")
    .split("")
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code === 9 || code === 10 || code === 13 || (code >= 32 && code <= 126);
    })
    .join("");

const escapePdfText = (value) =>
  sanitizePdfText(value).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");

const wrapPdfLine = (line, maxChars = 92) => {
  const text = sanitizePdfText(line).trimEnd();
  if (!text) return [""];
  const words = text.split(/\s+/);
  const wrapped = [];
  let current = "";
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxChars && current) {
      wrapped.push(current);
      current = word;
    } else {
      current = next;
    }
  });
  if (current) wrapped.push(current);
  return wrapped;
};

const pdfColor = ([r, g, b]) => `${r / 255} ${g / 255} ${b / 255}`;
const pdfText = (text, x, y, size = 10, bold = false, color = [17, 24, 39]) =>
  `BT ${pdfColor(color)} rg /${bold ? "F2" : "F1"} ${size} Tf ${x} ${y} Td (${escapePdfText(text)}) Tj ET`;
const pdfRect = (x, y, width, height, color) =>
  `${pdfColor(color)} rg ${x} ${y} ${width} ${height} re f`;
const pdfLine = (x1, y1, x2, y2, color = [226, 232, 240], width = 1) =>
  `${pdfColor(color)} RG ${width} w ${x1} ${y1} m ${x2} ${y2} l S`;
const pdfImage = (name, x, y, width, height) =>
  `q ${width} 0 0 ${height} ${x} ${y} cm /${name} Do Q`;

const loadPdfLogoImage = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined") {
      resolve(null);
      return;
    }
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = window.document.createElement("canvas");
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        const scale = Math.min(canvas.width / image.width, canvas.height / image.height);
        const width = image.width * scale;
        const height = image.height * scale;
        ctx.drawImage(image, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
        const binary = window.atob(dataUrl.split(",")[1] || "");
        let hex = "";
        for (let index = 0; index < binary.length; index += 1) {
          hex += binary.charCodeAt(index).toString(16).padStart(2, "0");
        }
        resolve({ width: canvas.width, height: canvas.height, hex });
      } catch (error) {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = "/img/icon.png";
  });

const createQuotationPdfBlob = async (document) => {
  const logoImage = await loadPdfLogoImage();
  const pages = [];
  let commands = [];
  let y = 742;
  const margin = 46;
  const contentWidth = 520;
  const brand = document.brand || {};

  const addFooter = () => {
    commands.push(pdfLine(margin, 38, margin + contentWidth, 38, [203, 213, 225], 0.8));
    commands.push(pdfText(brand.website || "https://www.iotrips.in", margin, 24, 8, false, [37, 99, 235]));
    commands.push(pdfText(brand.instagram || "https://www.instagram.com/indianodyssey.in", 204, 24, 8, false, [190, 24, 93]));
    commands.push(pdfText(brand.googleReview || "https://share.google/SoSE0qDIehhvaL411", 402, 24, 8, false, [22, 101, 52]));
  };

  const addPage = () => {
    if (commands.length) {
      addFooter();
      pages.push(commands);
    }
    commands = [
      pdfRect(0, 756, 612, 36, [15, 23, 42]),
      logoImage
        ? pdfImage("Im1", margin, 762, 24, 24)
        : pdfRect(margin, 764, 23, 18, [250, 204, 21]),
      logoImage ? "" : pdfText("IO", margin + 5, 769, 10, true, [15, 23, 42]),
      pdfText(brand.name || "Indian Odyssey", margin + 32, 772, 11, true, [255, 255, 255]),
      pdfText(brand.website || "https://www.iotrips.in", 372, 772, 8, false, [191, 219, 254]),
    ].filter(Boolean);
    y = 726;
  };

  const ensureSpace = (height) => {
    if (y - height < 54) addPage();
  };

  const addWrappedText = (
    text,
    x = margin,
    width = contentWidth,
    size = 10,
    bold = false,
    color = [31, 41, 55],
    lineGap = 4,
  ) => {
    const maxChars = Math.max(28, Math.floor(width / (size * 0.52)));
    const lines = String(text || "")
      .split(/\r?\n/)
      .flatMap((line) => wrapPdfLine(line, maxChars));
    lines.forEach((line) => {
      ensureSpace(size + lineGap + 2);
      commands.push(pdfText(line, x, y, size, bold, color));
      y -= size + lineGap;
    });
  };

  const addSectionTitle = (title) => {
    ensureSpace(36);
    y -= 8;
    commands.push(pdfRect(margin, y - 8, contentWidth, 24, [239, 246, 255]));
    commands.push(pdfRect(margin, y - 8, 4, 24, [37, 99, 235]));
    commands.push(pdfText(title, margin + 12, y, 13, true, [30, 64, 175]));
    y -= 30;
  };

  const addFacilityBadge = (facility, x, currentY) => {
    const available = facility.available === true;
    const text = sanitizePdfText(facility.label || `${facility.name}: ${available ? "Yes" : "No"}`);
    const width = Math.max(48, text.length * 4.7 + 14);
    // Neutral: an extra the customer can add, neither included nor missing.
    const bg = facility.neutral ? [241, 245, 249] : available ? [236, 253, 245] : [255, 241, 242];
    const border = facility.neutral ? [203, 213, 225] : available ? [187, 247, 208] : [254, 205, 211];
    const fg = facility.neutral ? [51, 65, 85] : available ? [22, 101, 52] : [190, 18, 60];
    commands.push(pdfRect(x, currentY - 8, width, 14, bg));
    commands.push(pdfLine(x, currentY - 8, x + width, currentY - 8, border, 0.6));
    commands.push(pdfLine(x, currentY + 6, x + width, currentY + 6, border, 0.6));
    commands.push(pdfLine(x, currentY - 8, x, currentY + 6, border, 0.6));
    commands.push(pdfLine(x + width, currentY - 8, x + width, currentY + 6, border, 0.6));
    commands.push(pdfText(text, x + 7, currentY - 3, 8, true, fg));
    return width;
  };

  const addHotelRoomDetails = (items) => {
    (items || []).forEach((item) => {
      ensureSpace(44);
      commands.push(pdfRect(margin, y - 21, contentWidth, 30, [248, 250, 252]));
      commands.push(pdfText(item.text || "-", margin + 10, y - 4, 10, true, [17, 24, 39]));
      y -= 26;
      if (item.meals) {
        commands.push(pdfText(item.meals, margin + 18, y, 9, false, [51, 65, 85]));
        y -= 15;
      }
      const facilities = item.facilities || [];
      const optionalFacilities = item.optionalFacilities || [];
      const addBadges = (label, badges) => {
        if (!badges.length) return;
        commands.push(pdfText(label, margin + 18, y, 9, true, [71, 85, 105]));
        y -= 15;
        let x = margin + 18;
        badges.forEach((facility) => {
          const text = facility.label || `${facility.name}: ${facility.available === true ? "Yes" : "No"}`;
          const width = Math.max(48, sanitizePdfText(text).length * 4.7 + 14);
          if (x + width > margin + contentWidth - 10) {
            y -= 18;
            x = margin + 18;
            ensureSpace(20);
          }
          x += addFacilityBadge(facility, x, y) + 8;
        });
        y -= 16;
      };
      addBadges("Included:", facilities);
      addBadges("Can add:", optionalFacilities);
      y -= 5;
    });
  };

  const addBulletItems = (items, x = margin + 12, width = contentWidth - 12) => {
    (items || []).forEach((item) => {
      addWrappedText(`- ${item}`, x, width, 10);
    });
  };

  addPage();
  commands.push(pdfRect(0, 632, 612, 124, [15, 23, 42]));
  if (logoImage) {
    commands.push(pdfRect(margin, 682, 56, 56, [255, 255, 255]));
    commands.push(pdfImage("Im1", margin + 4, 686, 48, 48));
  } else {
    commands.push(pdfRect(margin, 690, 46, 46, [250, 204, 21]));
    commands.push(pdfText("IO", margin + 12, 706, 18, true, [15, 23, 42]));
  }
  commands.push(pdfText(brand.name || "Indian Odyssey", margin + 62, 724, 13, true, [255, 255, 255]));
  commands.push(pdfText(document.title || "Quotation", margin + 62, 696, 24, true, [255, 255, 255]));
  if (document.subtitle) {
    commands.push(pdfText(document.subtitle, margin + 62, 672, 13, false, [219, 234, 254]));
  }
  commands.push(pdfText("Quotation prepared from selected trip page details", margin + 62, 652, 10, false, [203, 213, 225]));
  commands.push(pdfText(brand.phone || "", 390, 722, 9, false, [219, 234, 254]));
  commands.push(pdfText(brand.website || "", 390, 706, 8, false, [191, 219, 254]));
  commands.push(pdfText(brand.instagram || "", 390, 690, 8, false, [244, 114, 182]));
  commands.push(pdfText(brand.googleReview || "", 390, 676, 8, false, [134, 239, 172]));
  y = 604;

  ensureSpace(170);
  commands.push(pdfRect(margin, y - 146, contentWidth, 156, [248, 250, 252]));
  commands.push(pdfRect(margin, y - 146, contentWidth, 18, [226, 232, 240]));
  commands.push(pdfText("Quotation Details", margin + 14, y - 14, 14, true, [17, 24, 39]));
  let rowY = y - 38;
  (document.summary || []).forEach((item, index) => {
    const col = index % 2;
    const row = Math.floor(index / 2);
    const x = margin + 14 + col * 252;
    const currentY = rowY - row * 23;
    commands.push(pdfText(`${item.label}:`, x, currentY, 9, true, [75, 85, 99]));
    const value = sanitizePdfText(item.value || "-");
    const displayValue = value.length > 34 ? `${value.slice(0, 31)}...` : value;
    commands.push(pdfText(displayValue, x + 82, currentY, 9, false, [17, 24, 39]));
  });
  y -= 176;

  (document.sections || []).forEach((section) => {
    addSectionTitle(section.title);
    (section.blocks || []).forEach((block) => {
      if (block.title && block.type !== "day") {
        addWrappedText(block.title, margin, contentWidth, 11, true, [55, 65, 81], 4);
      }
      if (block.type === "hotelRooms") {
        addHotelRoomDetails(block.items);
      } else if (block.type === "day") {
        addWrappedText(block.title || "Day", margin, contentWidth, 11, true, [17, 24, 39], 4);
        if (block.text) addWrappedText(block.text, margin + 12, contentWidth - 12, 10);
      } else if (block.text) {
        addWrappedText(block.text, margin, contentWidth, 10);
      }
      if (block.type !== "hotelRooms") {
        addBulletItems(block.items);
      }
      y -= 5;
    });
  });

  if (commands.length) {
    addFooter();
    pages.push(commands);
  }
  const pageCount = pages.length;
  const fontObjectId = 3 + pageCount * 2;
  const boldFontObjectId = fontObjectId + 1;
  const imageObjectId = logoImage ? boldFontObjectId + 1 : null;
  const objects = [];
  objects[1] = "<< /Type /Catalog /Pages 2 0 R >>";
  objects[2] = `<< /Type /Pages /Kids [${pages
    .map((_, index) => `${3 + index} 0 R`)
    .join(" ")}] /Count ${pageCount} >>`;

  pages.forEach((pageCommands, index) => {
    const pageObjectId = 3 + index;
    const contentObjectId = 3 + pageCount + index;
    const content = pageCommands.join("\n");
    const xObjects = logoImage ? ` /XObject << /Im1 ${imageObjectId} 0 R >>` : "";
    objects[pageObjectId] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 ${fontObjectId} 0 R /F2 ${boldFontObjectId} 0 R >>${xObjects} >> /Contents ${contentObjectId} 0 R >>`;
    objects[contentObjectId] = `<< /Length ${content.length} >>\nstream\n${content}\nendstream`;
  });
  objects[fontObjectId] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>";
  objects[boldFontObjectId] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>";
  if (logoImage) {
    objects[imageObjectId] = `<< /Type /XObject /Subtype /Image /Width ${logoImage.width} /Height ${logoImage.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter [/ASCIIHexDecode /DCTDecode] /Length ${logoImage.hex.length + 1} >>\nstream\n${logoImage.hex}>\nendstream`;
  }

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  for (let id = 1; id < objects.length; id += 1) {
    if (!objects[id]) continue;
    offsets[id] = pdf.length;
    pdf += `${id} 0 obj\n${objects[id]}\nendobj\n`;
  }
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length}\n0000000000 65535 f \n`;
  for (let id = 1; id < objects.length; id += 1) {
    pdf += `${String(offsets[id] || 0).padStart(10, "0")} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return new Blob([pdf], { type: "application/pdf" });
};

function facilityCostBehavior(facility) {
  if (facility?.costBehavior === "INCLUDED_EXTERNAL") return "INCLUDED_EXTERNAL";
  if (facility?.costBehavior === "OPTIONAL_PAID" || facility?.optionalWithCost === true) return "OPTIONAL_PAID";
  if (Number(facility?.extraCost || 0) > 0) return "INCLUDED_EXTERNAL";
  return "INCLUDED_BY_HOTEL";
}

const MEALS = [["breakfast", "Breakfast"], ["lunch", "Lunch"], ["dinner", "Dinner"]];

function isMealFacilityName(name) {
  return /breakfast|lunch|dinner/i.test(String(name || ""));
}

// "Breakfast + Dinner included" / "No meals included" - shown first on every stay option, because
// a cheaper stay that drops the meals is the difference people most often miss.
function mealPlanFromFacilities(includedNames, extraNames) {
  const has = (names, key) => (names || []).some((name) => String(name || "").toLowerCase().includes(key));
  const included = MEALS.filter(([key]) => has(includedNames, key)).map(([, label]) => label);
  if (included.length) return { label: `${included.join(" + ")} included`, tone: "meals" };
  const mealsAtExtraCost = MEALS.some(([key]) => has(extraNames, key));
  return {
    label: mealsAtExtraCost ? "No meals included (available at extra cost)" : "No meals included",
    tone: "nomeals",
  };
}

function mergeFacilityCostBehavior(existing, next) {
  const existingBehavior = facilityCostBehavior(existing);
  const nextBehavior = facilityCostBehavior(next);
  if (existingBehavior === "OPTIONAL_PAID" || nextBehavior === "OPTIONAL_PAID") return "OPTIONAL_PAID";
  if (existingBehavior === "INCLUDED_EXTERNAL" || nextBehavior === "INCLUDED_EXTERNAL") return "INCLUDED_EXTERNAL";
  return "INCLUDED_BY_HOTEL";
}

function hotelFacilityDisplayLabel(facility, nightNumber) {
  const name = String(facility?.name || "").trim();
  if (name.toLowerCase() !== "breakfast") return name;
  const checkoutDay = Number(nightNumber || 0) + 1;
  return checkoutDay > 1
    ? `Breakfast included on checkout morning (Day ${checkoutDay})`
    : "Breakfast included on checkout morning";
}

export default DynamicPage;
